import { Feather } from "@expo/vector-icons";
import Hls from "hls.js";
import {
  createElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from "react-native";
import { siteLocale } from "../../i18n/strings";
import type { ColumnVideo } from "../columns";
import {
  createPaidAccountSession,
  createAdminVideo,
  clearPaidAccountSession,
  DEFAULT_SMART_PLAYBACK_POLICY,
  fetchPaidAccountSession,
  fetchAdminVideos,
  fetchSmartPlaybackPolicy,
  fetchAllColumnVideos,
  fetchColumnVideoPage,
  fetchColumnVideos,
  fetchPaidVideoEntitlement,
  fetchP2pStatus,
  organizeAdminVideo,
  PaidVideoApiError,
  purchasePaidVideo,
  deleteAdminVideo,
  registerPaidAccount,
  resetSmartPlaybackPolicy,
  saveSmartPlaybackPolicy,
  updateAdminVideo,
  type AdminMediaVariant,
  type AdminVideo,
  type PaidAccount,
  type PaidEntitlement,
  type SmartPlaybackPolicy,
  type DesktopVideoSort,
} from "../../services/videoServer";
import {
  mediaVariantLabel,
  mergeLandscapeVariantIntoDesktop,
  preferredPlaybackVariant,
} from "../../utils/mediaVariants";
import styles from "./styles";
import HlsCacheSettings from "../../components/settings/hlsCacheSettings";
import {
  adaptivePrefetchCount,
  cacheCompletedHlsVideo,
  cacheHlsPlaybackCheckpoint,
  getHlsCacheSettings,
  getDesktopCachePolicy,
  getResumePosition,
  prefetchMediaUrls,
  saveDesktopPrefetchTracks,
  saveResumePosition,
} from "../../services/mobileHlsCache";
import {
  mediaEdgeById,
  mediaEdgeLabelForUrl,
  mediaEdgeOptions,
  nextMediaEdge,
  reportMediaEdgeFailure,
  selectMediaEdge,
} from "../../services/mediaEdgeSelector";
import type { RootState } from "../../redux/store";
import type { AppDispatch } from "../../redux/store";
import {
  login,
  persistLocalAuthenticatedUser,
  setUserState,
  userAuthStateListener,
} from "../../redux/slices/authSlice";
import {
  cycleLoopMode,
  LOOP_MODE_STORAGE_KEY,
  type LoopMode,
} from "../../redux/slices/playbackSlice";
import { DEMO_EMAIL, DEMO_PASSWORD } from "../../demo/localDemo";
import { USE_LOCAL_DEMO_AUTH } from "../../../firebaseConfig";
import { VIDEO_SERVER_BASE_URL } from "../../config/videoServer";
import { createHlsPlayer } from "../../services/p2pHls";
import { attachVideoSource } from "../../services/videoSource";
import {
  adaptiveVariantBitrate,
  adaptiveVariantHeight,
  decideAdaptiveVariant,
  estimateSmartBufferWaitSeconds,
  initialAdaptiveVariant,
  smartBufferTargetSeconds,
} from "../../services/adaptivePlayback";
import {
  getAccessVisitorId,
  recordAccessEvent,
  type AccessIdentity,
} from "../../services/accessTracking";
import {
  addVideoComment,
  deleteVideoComment,
  fetchVideoActionState,
  setVideoFavorite,
  updateVideoComment,
  type VideoComment,
} from "../../services/videoInteractions";
import {
  fetchSearchKeywordHistory,
  type SearchKeywordHistoryItem,
} from "../../services/searchKeywordHistory";
import {
  readDesktopSoundPreference,
  writeDesktopSoundPreference,
} from "../../services/desktopSoundPreference";
import {
  isWindowsNativePlaybackActive,
  isWindowsCompatibleMediaVariant,
  notifyWindowsPlayerCommand,
  notifyWindowsPlayerSlot,
  notifyWindowsPlayerSourceSelection,
  notifyWindowsPlayerVideoClose,
  notifyWindowsPlayerVideoOpen,
  subscribeWindowsPlayerProgress,
} from "../../services/windowsDesktopBridge";
import {
  matchPlaybackVariantPreference,
  playbackPreferenceOwner,
  readPlaybackVariantPreference,
  rememberAudioVariantPreference,
  rememberVideoVariantPreference,
} from "../../services/playbackVariantPreference";
import {
  fetchChannelSubscriptions,
  fetchViewerWatchHistory,
  setChannelSubscription,
  type ChannelSubscription,
  type ViewerWatchHistoryItem,
} from "../../services/viewerLibrary";
import {
  DOWNGRADE_LEVEL_STORAGE_KEY,
  evaluateDowngradePressure,
  playbackDowngradePolicy,
  readDowngradeLevel,
  type DowngradePressure,
} from "../../services/playbackDowngradePolicy";
import ProductSpotlight from "../../commerce/ProductSpotlight";
import ProductPlacementEditor from "../../commerce/ProductPlacementEditor";
import VideoShareButton from "../../components/general/videoShareSheet";
import {
  buildIntelligentCachePlan,
  diagnosePlayback,
  rankPlaybackCandidates,
  type IntelligentCacheTarget,
  type PlaybackHealthSample,
} from "../../services/playbackIntelligence";

type SmartKeywordGroup = {
  label: string;
  terms: string[];
};

type SmartSearchTag = {
  key: string;
  label: string;
  count: number;
  score: number;
};

type SearchSuggestion = {
  key: string;
  label: string;
  source: "用户历史" | "公共热词" | "智能关键词" | "视频关键词";
  score: number;
};

type DesktopProfileTab = "history" | "favorites" | "comments";

type PlaybackCacheDiagnosticRow = {
  url: string;
  name: string;
  resource: "playlist" | "segment" | "video";
  requests: number;
  hits: number;
  cached: boolean;
  storing: boolean;
  bytes: number;
  source: "cache" | "network";
  at: number;
};
type SmartPlaybackFieldKey = keyof SmartPlaybackPolicy;

type SmartPlaybackPreset = {
  key: string;
  label: string;
  description: string;
  policy: SmartPlaybackPolicy;
};

type FloatingPlayerLayout = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type FloatingPlayerPointerStartEvent = {
  clientX: number;
  clientY: number;
  preventDefault: () => void;
};

const FLOATING_PLAYER_MARGIN = 18;
const FLOATING_PLAYER_MIN_WIDTH = 560;
const FLOATING_PLAYER_MIN_HEIGHT = 430;
const FLOATING_PLAYER_CHROME_HEIGHT = 190;

const desktopLoopModeLabels: Record<LoopMode, string> = {
  single: "单曲循环",
  sequence: "顺序播放",
  shuffle: "随机播放",
  smart: "智能播放",
};

const advertisementDisplayModeLabels = {
  smart: "智能推荐",
  keyword: "关键词定向",
  broad: "全面展示",
} as const;

function desktopLoopModeIcon(mode: LoopMode) {
  if (mode === "single") return "repeat" as const;
  if (mode === "sequence") return "list" as const;
  if (mode === "shuffle") return "shuffle" as const;
  return "zap" as const;
}

const AUTO_VIDEO_VARIANT_ID = "__automatic_quality__";

function browserPlaybackNetwork() {
  const connection =
    typeof navigator === "undefined"
      ? undefined
      : (
          navigator as Navigator & {
            connection?: {
              downlink?: number;
              effectiveType?: string;
              saveData?: boolean;
            };
          }
        ).connection;
  const effectiveFallback: Record<string, number> = {
    "slow-2g": 250_000,
    "2g": 500_000,
    "3g": 1_500_000,
    "4g": 6_000_000,
  };
  return {
    bandwidthBps:
      connection?.downlink && connection.downlink > 0
        ? connection.downlink * 1_000_000
        : effectiveFallback[connection?.effectiveType || ""] || 3_000_000,
    saveData: connection?.saveData === true,
  };
}

function bufferedSecondsAhead(video: HTMLVideoElement) {
  const position = video.currentTime || 0;
  for (let index = 0; index < video.buffered.length; index += 1) {
    if (
      video.buffered.start(index) <= position + 0.1 &&
      video.buffered.end(index) >= position
    ) {
      let bufferedEnd = video.buffered.end(index);
      // CMAF/HLS timestamps can leave tiny gaps between otherwise continuous
      // segments. Treat them as one playable reserve so the safety gate does
      // not remain stuck at the end of the first segment.
      for (let next = index + 1; next < video.buffered.length; next += 1) {
        if (video.buffered.start(next) > bufferedEnd + 0.75) break;
        bufferedEnd = Math.max(bufferedEnd, video.buffered.end(next));
      }
      return Math.max(0, bufferedEnd - position);
    }
  }
  return 0;
}

function getNextDesktopVideo(
  items: ColumnVideo[],
  currentId: string,
  mode: LoopMode,
  policy: SmartPlaybackPolicy,
  recentVideoIds: string[] = [],
) {
  if (items.length < 2 || mode === "single") {
    return items.find((item) => item.id === currentId) || items[0] || null;
  }
  const currentIndex = items.findIndex((item) => item.id === currentId);
  if (currentIndex < 0) return items[0];

  if (mode === "shuffle") {
    let nextIndex = currentIndex;
    while (nextIndex === currentIndex) {
      nextIndex = Math.floor(Math.random() * items.length);
    }
    return items[nextIndex];
  }

  const sequenceVideo = items[(currentIndex + 1) % items.length];
  if (mode === "sequence") return sequenceVideo;

  const current = items[currentIndex];
  const ranked = rankPlaybackCandidates(items, current, policy, recentVideoIds);
  const candidates = ranked.map((candidate) => candidate.video);
  if (
    candidates.length > 1 &&
    Math.random() * 100 < policy.explorationPercent
  ) {
    return candidates[Math.floor(Math.random() * candidates.length)];
  }
  return ranked[0]?.video || sequenceVideo;
}

function getPreviousDesktopVideo(
  items: ColumnVideo[],
  currentId: string,
  mode: LoopMode,
  policy: SmartPlaybackPolicy = DEFAULT_SMART_PLAYBACK_POLICY,
) {
  return getNextDesktopVideo([...items].reverse(), currentId, mode, policy);
}

function floatingPlayerViewport() {
  return {
    width: typeof window === "undefined" ? 1440 : window.innerWidth,
    height: typeof window === "undefined" ? 900 : window.innerHeight,
  };
}

function clampFloatingPlayerLayout(
  layout: FloatingPlayerLayout,
): FloatingPlayerLayout {
  const viewport = floatingPlayerViewport();
  const maxWidth = Math.max(320, viewport.width);
  const maxHeight = Math.max(320, viewport.height - FLOATING_PLAYER_MARGIN * 2);
  const minWidth = Math.min(FLOATING_PLAYER_MIN_WIDTH, maxWidth);
  const minHeight = Math.min(FLOATING_PLAYER_MIN_HEIGHT, maxHeight);
  const width = Math.max(
    minWidth,
    Math.min(layout.width, maxWidth),
  );
  const fullyVisibleHeight = Math.min(
    maxHeight,
    (width * 9) / 16 + FLOATING_PLAYER_CHROME_HEIGHT,
  );
  const height = Math.max(
    minHeight,
    fullyVisibleHeight,
    Math.min(layout.height, maxHeight),
  );
  return {
    x: Math.max(
      0,
      Math.min(layout.x, viewport.width - width),
    ),
    y: Math.max(
      FLOATING_PLAYER_MARGIN,
      Math.min(layout.y, viewport.height - 80),
    ),
    width,
    height,
  };
}

function initialFloatingPlayerLayout(): FloatingPlayerLayout {
  const viewport = floatingPlayerViewport();
  const width = Math.min(
    1120,
    Math.max(FLOATING_PLAYER_MIN_WIDTH, viewport.width * 0.66),
  );
  const height = Math.min(
    860,
    Math.max(FLOATING_PLAYER_MIN_HEIGHT, viewport.height * 0.78),
  );
  return clampFloatingPlayerLayout({
    x: viewport.width - width - 34,
    y: 24,
    width,
    height,
  });
}

function compactPlaybackEdgeLabel(label: string): string {
  const compact = String(label || "播放节点")
    .replace(/自动中继/g, "")
    .replace(/自建节点/g, "")
    .replace(/\s+/g, " ")
    .trim();
  const characters = Array.from(compact || "播放节点");
  return characters.length > 10
    ? `${characters.slice(0, 10).join("")}…`
    : characters.join("");
}

function playbackEdgeHost(url: string): string {
  try {
    return new URL(url).host || "当前站点";
  } catch {
    return "当前站点";
  }
}

type PlaybackEdgeMode = "auto" | "manual";
type PlaybackEdgePreference = {
  mode: PlaybackEdgeMode;
  priorityIds: string[];
  manualEdgeId: string;
};
const PLAYBACK_EDGE_PREFERENCE_KEY = "kirkwat-playback-edge-preference-v1";

function readPlaybackEdgePreference(): PlaybackEdgePreference {
  const fallback: PlaybackEdgePreference = {
    mode: "auto",
    priorityIds: [],
    manualEdgeId: "",
  };
  try {
    const raw = window.localStorage.getItem(PLAYBACK_EDGE_PREFERENCE_KEY);
    const value = raw ? JSON.parse(raw) : null;
    return {
      mode: value?.mode === "manual" ? "manual" : "auto",
      priorityIds: Array.isArray(value?.priorityIds)
        ? value.priorityIds.filter((id: unknown) => typeof id === "string")
        : [],
      manualEdgeId:
        typeof value?.manualEdgeId === "string" ? value.manualEdgeId : "",
    };
  } catch {
    return fallback;
  }
}

function writePlaybackEdgePreference(value: PlaybackEdgePreference) {
  try {
    window.localStorage.setItem(
      PLAYBACK_EDGE_PREFERENCE_KEY,
      JSON.stringify(value),
    );
  } catch {}
  return value;
}

const desktopProfileTabs: {
  key: DesktopProfileTab;
  label: string;
  icon: "clock" | "bookmark" | "message-circle";
}[] = [
  { key: "history", label: "观看记录", icon: "clock" },
  { key: "favorites", label: "我的收藏", icon: "bookmark" },
  { key: "comments", label: "评论互动", icon: "message-circle" },
];

const smartPlaybackFields: {
  key: SmartPlaybackFieldKey;
  label: string;
  description: string;
  maximum: number;
  step: number;
}[] = [
  {
    key: "relatedTermsWeight",
    label: "关键词相关",
    description: "标题、标签匹配",
    maximum: 100,
    step: 5,
  },
  {
    key: "sameChannelWeight",
    label: "同作者",
    description: "继续观看同频道",
    maximum: 100,
    step: 5,
  },
  {
    key: "sameCategoryWeight",
    label: "同分类",
    description: "保持内容类型",
    maximum: 100,
    step: 5,
  },
  {
    key: "personalizedWeight",
    label: "个人兴趣",
    description: "观看互动偏好",
    maximum: 100,
    step: 5,
  },
  {
    key: "publicWeight",
    label: "公共热度",
    description: "全站热门内容",
    maximum: 100,
    step: 5,
  },
  {
    key: "advertisementRelevanceWeight",
    label: "广告相关",
    description: "相关广告匹配",
    maximum: 100,
    step: 5,
  },
  {
    key: "explorationPercent",
    label: "随机探索",
    description: "发现不同内容",
    maximum: 100,
    step: 5,
  },
  {
    key: "recentHistorySize",
    label: "播放去重",
    description: "近期不重复数量",
    maximum: 50,
    step: 1,
  },
];

const smartPlaybackPresets: SmartPlaybackPreset[] = [
  {
    key: "balanced",
    label: "均衡推荐",
    description: "各种信号均衡",
    policy: {
      relatedTermsWeight: 30,
      sameChannelWeight: 60,
      sameCategoryWeight: 20,
      personalizedWeight: 35,
      publicWeight: 10,
      advertisementRelevanceWeight: 20,
      explorationPercent: 8,
      recentHistorySize: 12,
    },
  },
  {
    key: "channel",
    label: "同作者连看",
    description: "优先同一频道",
    policy: {
      relatedTermsWeight: 25,
      sameChannelWeight: 100,
      sameCategoryWeight: 30,
      personalizedWeight: 30,
      publicWeight: 5,
      advertisementRelevanceWeight: 10,
      explorationPercent: 2,
      recentHistorySize: 16,
    },
  },
  {
    key: "category",
    label: "同分类沉浸",
    description: "持续同类内容",
    policy: {
      relatedTermsWeight: 25,
      sameChannelWeight: 25,
      sameCategoryWeight: 100,
      personalizedWeight: 35,
      publicWeight: 10,
      advertisementRelevanceWeight: 10,
      explorationPercent: 5,
      recentHistorySize: 15,
    },
  },
  {
    key: "personal",
    label: "兴趣优先",
    description: "跟随个人偏好",
    policy: {
      relatedTermsWeight: 35,
      sameChannelWeight: 35,
      sameCategoryWeight: 35,
      personalizedWeight: 100,
      publicWeight: 5,
      advertisementRelevanceWeight: 15,
      explorationPercent: 3,
      recentHistorySize: 15,
    },
  },
  {
    key: "popular",
    label: "热门优先",
    description: "优先全站热度",
    policy: {
      relatedTermsWeight: 10,
      sameChannelWeight: 15,
      sameCategoryWeight: 20,
      personalizedWeight: 20,
      publicWeight: 100,
      advertisementRelevanceWeight: 10,
      explorationPercent: 12,
      recentHistorySize: 8,
    },
  },
  {
    key: "keywords",
    label: "关键词深挖",
    description: "紧贴标题标签",
    policy: {
      relatedTermsWeight: 100,
      sameChannelWeight: 20,
      sameCategoryWeight: 20,
      personalizedWeight: 45,
      publicWeight: 10,
      advertisementRelevanceWeight: 20,
      explorationPercent: 5,
      recentHistorySize: 12,
    },
  },
  {
    key: "discover",
    label: "新鲜探索",
    description: "更多意外发现",
    policy: {
      relatedTermsWeight: 20,
      sameChannelWeight: 15,
      sameCategoryWeight: 20,
      personalizedWeight: 20,
      publicWeight: 20,
      advertisementRelevanceWeight: 10,
      explorationPercent: 65,
      recentHistorySize: 6,
    },
  },
  {
    key: "stable",
    label: "稳定少跳",
    description: "结果更可预测",
    policy: {
      relatedTermsWeight: 45,
      sameChannelWeight: 70,
      sameCategoryWeight: 45,
      personalizedWeight: 40,
      publicWeight: 10,
      advertisementRelevanceWeight: 10,
      explorationPercent: 0,
      recentHistorySize: 5,
    },
  },
  {
    key: "mixed",
    label: "广泛混合",
    description: "多维度交叉推荐",
    policy: {
      relatedTermsWeight: 45,
      sameChannelWeight: 45,
      sameCategoryWeight: 45,
      personalizedWeight: 45,
      publicWeight: 45,
      advertisementRelevanceWeight: 25,
      explorationPercent: 25,
      recentHistorySize: 20,
    },
  },
  {
    key: "no-repeat",
    label: "强力防重复",
    description: "最大近期去重",
    policy: {
      relatedTermsWeight: 30,
      sameChannelWeight: 40,
      sameCategoryWeight: 30,
      personalizedWeight: 35,
      publicWeight: 15,
      advertisementRelevanceWeight: 10,
      explorationPercent: 15,
      recentHistorySize: 50,
    },
  },
];

function sameSmartPlaybackPolicy(
  left: SmartPlaybackPolicy,
  right: SmartPlaybackPolicy,
) {
  return smartPlaybackFields.every(
    (field) => left[field.key] === right[field.key],
  );
}

function formatProfileWatchTime(seconds: number) {
  const safeSeconds = Math.max(0, Math.round(seconds));
  if (safeSeconds < 60) return `${safeSeconds} 秒`;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  return hours ? `${hours} 小时 ${minutes} 分钟` : `${minutes} 分钟`;
}

function formatPlaybackPosition(seconds: number) {
  const safeSeconds = Math.max(0, Math.round(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;
  return hours
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(
        remainingSeconds,
      ).padStart(2, "0")}`
    : `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function formatHistoryTimestamp(value: string) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "时间未知";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

const categoryKeywordLabels: Partial<Record<ColumnVideo["category"], string>> =
  {
    music: "音乐",
    freshVideos: "最新上传",
    live: "直播",
    games: "游戏",
    shorts: "短视频",
  };

const smartKeywordGroups: SmartKeywordGroup[] = [
  { label: "音乐", terms: ["音乐", "音樂", "music", "歌曲", "song", "nhạc"] },
  {
    label: "DJ Remix",
    terms: ["dj", "remix", "nonstop", "mix", "串烧", "串燒"],
  },
  {
    label: "热门舞曲",
    terms: [
      "热门",
      "熱門",
      "hot",
      "trend",
      "舞曲",
      "上头",
      "上頭",
      "dance",
      "club",
    ],
  },
  { label: "抖音热歌", terms: ["douyin", "tiktok", "tik tok", "抖音"] },
  { label: "Funky House", terms: ["funky house", "funkyhouse", "house"] },
  { label: "R&B", terms: ["r&b", "rnb", "groove", "힙합"] },
  { label: "歌单", terms: ["playlist", "歌单", "歌單", "플리"] },
  { label: "英文", terms: ["英文", "english"] },
  {
    label: "助眠",
    terms: [
      "助眠",
      "睡眠",
      "sleep",
      "insomnia",
      "睡前",
      "安眠",
      "半夜",
      "夜间",
      "夜間",
    ],
  },
  {
    label: "放松",
    terms: [
      "放松",
      "放鬆",
      "relax",
      "relaxing",
      "relaxation",
      "chill",
      "平静",
      "平靜",
    ],
  },
  {
    label: "冥想",
    terms: ["冥想", "meditation", "禅", "禪", "静心", "靜心", "打坐"],
  },
  {
    label: "疗愈",
    terms: ["疗愈", "療癒", "治愈", "治癒", "healing", "relief"],
  },
  {
    label: "减压",
    terms: [
      "减压",
      "減壓",
      "压力",
      "壓力",
      "stress",
      "焦虑",
      "焦慮",
      "烦躁",
      "煩躁",
    ],
  },
  { label: "安神", terms: ["安神", "养神", "養神", "安稳", "安穩"] },
  {
    label: "养生",
    terms: ["养生", "養生", "五音", "入肝", "入肺", "入肾", "入腎"],
  },
  { label: "古风", terms: ["古风", "古風", "zen", "chinese", "禅音", "禪音"] },
  { label: "古筝", terms: ["古筝", "古箏", "guzheng"] },
  { label: "直播", terms: ["直播", "live"] },
  { label: "游戏", terms: ["游戏", "遊戲", "game", "gaming"] },
  { label: "短视频", terms: ["短视频", "短視頻", "shorts", "short video"] },
];

const searchKeywordStopWords = new Set([
  "and",
  "the",
  "for",
  "with",
  "from",
  "this",
  "that",
  "full",
  "track",
  "video",
  "official",
  "deep",
  "一个",
  "一种",
  "可以",
  "适合",
  "適合",
  "正在",
  "通过",
]);

const normalizeSearchText = (value: string) =>
  value
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[‐‑–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

const normalizedGroupTerms = (group: SmartKeywordGroup) =>
  [group.label, ...group.terms].map(normalizeSearchText).filter(Boolean);

function groupMatchesText(group: SmartKeywordGroup, normalizedText: string) {
  return normalizedGroupTerms(group).some((term) =>
    normalizedText.includes(term),
  );
}

function buildSearchMatchers(query: string) {
  const normalizedQuery = normalizeSearchText(query);
  let residual = normalizedQuery;
  const groups: SmartKeywordGroup[] = [];
  for (const group of smartKeywordGroups) {
    const matches = normalizedGroupTerms(group)
      .filter((term) => residual.includes(term))
      .sort((left, right) => right.length - left.length);
    if (!matches.length) continue;
    groups.push(group);
    for (const term of matches) residual = residual.split(term).join(" ");
  }
  const terms = (residual.match(/[\p{L}\p{N}&.+-]+/gu) || [])
    .map((term) => term.trim())
    .filter((term) => term && !searchKeywordStopWords.has(term));
  return { normalizedQuery, groups, terms };
}

function videoSearchText(item: ColumnVideo) {
  return normalizeSearchText(
    [
      item.title,
      item.channel,
      item.meta,
      item.category,
      categoryKeywordLabels[item.category] || "",
      ...(item.tags || []),
      ...(item.adKeywords || []),
    ].join(" "),
  );
}

function videoMatchesSearch(item: ColumnVideo, query: string) {
  const matchers = buildSearchMatchers(query);
  if (!matchers.normalizedQuery) return true;
  const searchable = videoSearchText(item);
  return (
    matchers.groups.every((group) => groupMatchesText(group, searchable)) &&
    matchers.terms.every((term) => searchable.includes(term))
  );
}

function advertisementKeywordMatches(item: ColumnVideo, query: string) {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return false;
  return (item.adKeywords || []).some((rawKeyword) => {
    const keyword = normalizeSearchText(rawKeyword);
    return (
      Boolean(keyword) &&
      (normalizedQuery.includes(keyword) || keyword.includes(normalizedQuery))
    );
  });
}

function advertisementEligibleForDesktop(
  item: ColumnVideo,
  sortMode: DesktopVideoSort,
  committedQuery: string,
  activeSearchTag: string,
) {
  if (!item.isAdvertisement) return true;
  const mode = item.adDisplayMode || "smart";
  if (mode === "broad") return true;
  if (mode === "smart") return sortMode === "personalized";
  return (
    advertisementKeywordMatches(item, committedQuery) ||
    advertisementKeywordMatches(item, activeSearchTag)
  );
}

function keywordGroupForLabel(label: string) {
  const normalized = normalizeSearchText(label);
  return smartKeywordGroups.find((group) =>
    normalizedGroupTerms(group).some((term) => term === normalized),
  );
}

function usefulTitleKeyword(label: string) {
  const normalized = normalizeSearchText(label);
  if (
    !normalized ||
    normalized.length > 24 ||
    searchKeywordStopWords.has(normalized)
  )
    return false;
  if (/^\d+$/.test(normalized) || /^(?:19|20)\d{2}$/.test(normalized))
    return false;
  if (/^[a-f0-9-]{12,}$/.test(normalized) || /^\d{3,4}p$/.test(normalized))
    return false;
  const hasWideCharacters = /[\u3400-\u9fff\uac00-\ud7af]/.test(normalized);
  return hasWideCharacters
    ? normalized.length >= 2
    : normalized.length >= 3 || ["dj", "r&b"].includes(normalized);
}

function titleKeywords(title: string) {
  const Segmenter = (Intl as any).Segmenter;
  const values: string[] = [];
  if (Segmenter) {
    const segmenter = new Segmenter(undefined, { granularity: "word" });
    for (const part of segmenter.segment(title) as any) {
      if (part.isWordLike && usefulTitleKeyword(part.segment))
        values.push(part.segment.trim());
    }
  } else {
    values.push(
      ...(title.match(/[\p{L}\p{N}&.+-]+/gu) || []).filter(usefulTitleKeyword),
    );
  }
  return [...new Set(values)];
}

function videoKeywordCandidates(item: ColumnVideo) {
  const candidates = new Map<
    string,
    { key: string; label: string; boost: number }
  >();
  const add = (rawLabel: string, boost: number) => {
    const trimmed = rawLabel.trim();
    if (!trimmed) return;
    const group = keywordGroupForLabel(trimmed);
    const label = group?.label || trimmed;
    const key = normalizeSearchText(label);
    const current = candidates.get(key);
    if (!current || boost > current.boost)
      candidates.set(key, { key, label, boost });
  };
  for (const tag of item.tags || []) add(tag, 3);
  for (const keyword of item.adKeywords || []) add(keyword, 4);
  add(categoryKeywordLabels[item.category] || "", 2);
  const searchable = videoSearchText(item);
  for (const group of smartKeywordGroups)
    if (groupMatchesText(group, searchable)) add(group.label, 1);
  for (const keyword of titleKeywords(item.title)) add(keyword, 0);
  return [...candidates.values()];
}

type SearchTagStrategy = "user" | "public";

function searchTagStrategyWeight(
  video: ColumnVideo,
  strategy: SearchTagStrategy,
) {
  if (strategy === "public") {
    const metrics = video.ranking?.metrics;
    const clicks = metrics?.clicks || 0;
    const watchSeconds = metrics?.watchSeconds || 0;
    const comments = metrics?.comments || 0;
    if (clicks + watchSeconds + comments <= 0) return 0;
    return (
      Math.log1p(clicks) * 1.2 +
      Math.log1p(watchSeconds / 30) * 1.5 +
      Math.log1p(comments) * 2 +
      (video.ranking?.publicScore || 0) / 100 +
      (video.rankingScore || 0) / 200
    );
  }
  const signals = video.ranking?.userSignals;
  if (!signals) return 0;
  const directEvidence =
    Math.log1p(signals.clicks) * 1.4 +
    Math.log1p(signals.watchSeconds / 30) * 1.8 +
    (signals.favorited ? 4 : 0) +
    (signals.commented ? 3 : 0);
  return directEvidence > 0 ? directEvidence + signals.tagAffinity * 2 : 0;
}

function buildRelatedSearchTags(
  videos: ColumnVideo[],
  query: string,
  strategy: SearchTagStrategy,
  limit = 6,
): SmartSearchTag[] {
  const matchers = buildSearchMatchers(query);
  const matchedVideos = matchers.normalizedQuery
    ? videos.filter((video) => videoMatchesSearch(video, query))
    : videos;
  if (!matchedVideos.length) return [];
  if (matchers.normalizedQuery) {
    const compactQuery = matchers.normalizedQuery.replace(/\s/g, "");
    if (
      /^[a-z]$/i.test(compactQuery) ||
      (/^[\u3400-\u9fff]$/.test(compactQuery) && matchedVideos.length < 2)
    )
      return [];
  }

  const matchedIds = new Set(matchedVideos.map((video) => video.id));
  const selectedGroups = new Set(
    matchers.groups.map((group) => normalizeSearchText(group.label)),
  );
  const global = new Map<
    string,
    { key: string; label: string; documents: number; boost: number }
  >();
  const cooccurrence = new Map<string, { count: number; weight: number }>();

  for (const video of videos) {
    const strategyWeight = searchTagStrategyWeight(video, strategy);
    for (const candidate of videoKeywordCandidates(video)) {
      const current = global.get(candidate.key);
      if (current) {
        current.documents += 1;
        current.boost = Math.max(current.boost, candidate.boost);
      } else {
        global.set(candidate.key, { ...candidate, documents: 1 });
      }
      if (matchedIds.has(video.id) && strategyWeight > 0) {
        const currentMatch = cooccurrence.get(candidate.key) || {
          count: 0,
          weight: 0,
        };
        currentMatch.count += 1;
        currentMatch.weight += strategyWeight;
        cooccurrence.set(candidate.key, currentMatch);
      }
    }
  }

  return [...global.values()]
    .flatMap((candidate) => {
      const relevance = cooccurrence.get(candidate.key);
      const count = relevance?.count || 0;
      if (
        !count ||
        selectedGroups.has(candidate.key) ||
        matchers.normalizedQuery.includes(candidate.key)
      )
        return [];
      if (candidate.boost === 0 && candidate.documents < 2) return [];
      const inverseFrequency = Math.log(
        (videos.length + 1) / (candidate.documents + 1),
      );
      const score =
        (strategy === "user" ? relevance!.weight : count) * inverseFrequency +
        candidate.boost * 0.75 +
        count * 0.15 +
        relevance!.weight * (strategy === "public" ? 0.25 : 0.1);
      return [{ key: candidate.key, label: candidate.label, count, score }];
    })
    .sort(
      (left, right) =>
        right.score - left.score ||
        right.count - left.count ||
        left.label.localeCompare(right.label, "zh-CN"),
    )
    .slice(0, limit);
}

function searchTagDedupKey(label: string) {
  return normalizeSearchText(keywordGroupForLabel(label)?.label || label);
}

function historyKeywordMatchesQuery(keyword: string, query: string) {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;
  const normalizedKeyword = normalizeSearchText(keyword);
  if (
    normalizedKeyword.includes(normalizedQuery) ||
    normalizedQuery.includes(normalizedKeyword) ||
    searchTagDedupKey(keyword) === searchTagDedupKey(query)
  )
    return true;
  const queryGroups = new Set(
    buildSearchMatchers(query).groups.map((group) =>
      normalizeSearchText(group.label),
    ),
  );
  return buildSearchMatchers(keyword).groups.some((group) =>
    queryGroups.has(normalizeSearchText(group.label)),
  );
}

function mergeSearchHistoryTags(
  derivedTags: SmartSearchTag[],
  history: SearchKeywordHistoryItem[],
  query: string,
  limit: number,
) {
  const merged = new Map<string, SmartSearchTag>();
  const normalizedQuery = normalizeSearchText(query);
  for (const item of history) {
    if (!historyKeywordMatchesQuery(item.keyword, query)) continue;
    if (
      normalizedQuery &&
      normalizeSearchText(item.keyword) === normalizedQuery
    )
      continue;
    const key = searchTagDedupKey(item.keyword);
    const recency = Number.isFinite(Date.parse(item.lastSearchedAt))
      ? Date.parse(item.lastSearchedAt) / 1e13
      : 0;
    merged.set(key, {
      key,
      label: item.keyword,
      count: item.count,
      score: 1000 + Math.log1p(item.count) * 10 + recency,
    });
  }
  for (const tag of derivedTags) {
    const key = searchTagDedupKey(tag.label);
    const current = merged.get(key);
    if (current) {
      current.count = Math.max(current.count, tag.count);
      continue;
    }
    merged.set(key, { ...tag, key });
  }
  return [...merged.values()]
    .sort(
      (left, right) =>
        right.score - left.score ||
        left.label.localeCompare(right.label, "zh-CN"),
    )
    .slice(0, limit);
}

function searchSuggestionMatchScore(normalizedInput: string, values: string[]) {
  let score = 0;
  const normalizedLabel = normalizeSearchText(values[0] || "");
  const singleLatinCharacter = /^[a-z0-9]$/i.test(normalizedInput);
  for (const rawValue of values) {
    const value = normalizeSearchText(rawValue);
    if (!value) continue;
    if (value === normalizedInput) score = Math.max(score, 120);
    else if (value.startsWith(normalizedInput)) score = Math.max(score, 100);
    else if (
      value
        .split(/[\s|/·,_+\-]+/)
        .some((part) => part.startsWith(normalizedInput))
    )
      score = Math.max(score, 90);
    else if (
      value.includes(normalizedInput) &&
      (!singleLatinCharacter || normalizedLabel.includes(normalizedInput))
    )
      score = Math.max(score, 70);
  }
  return score;
}

function buildSearchSuggestions(
  videos: ColumnVideo[],
  history: {
    user: SearchKeywordHistoryItem[];
    public: SearchKeywordHistoryItem[];
  },
  input: string,
  limit = 8,
): SearchSuggestion[] {
  const normalizedInput = normalizeSearchText(input);
  if (!normalizedInput) return [];

  const suggestions = new Map<string, SearchSuggestion>();
  const add = (
    rawLabel: string,
    aliases: string[],
    source: SearchSuggestion["source"],
    baseScore: number,
  ) => {
    const label = keywordGroupForLabel(rawLabel)?.label || rawLabel.trim();
    if (!label) return;
    const matchScore = searchSuggestionMatchScore(normalizedInput, [
      label,
      rawLabel,
      ...aliases,
    ]);
    if (!matchScore) return;
    const key = searchTagDedupKey(label);
    const next = { key, label, source, score: baseScore + matchScore };
    const current = suggestions.get(key);
    if (!current || next.score > current.score) suggestions.set(key, next);
  };

  for (const item of history.user) {
    add(item.keyword, [], "用户历史", 400 + Math.log1p(item.count) * 5);
  }
  for (const item of history.public) {
    add(item.keyword, [], "公共热词", 300 + Math.log1p(item.count) * 5);
  }
  for (const group of smartKeywordGroups) {
    add(group.label, group.terms, "智能关键词", 200);
  }

  const videoCandidates = new Map<
    string,
    { label: string; documents: number; boost: number }
  >();
  for (const video of videos) {
    for (const candidate of videoKeywordCandidates(video)) {
      const current = videoCandidates.get(candidate.key);
      if (current) {
        current.documents += 1;
        current.boost = Math.max(current.boost, candidate.boost);
      } else {
        videoCandidates.set(candidate.key, {
          label: candidate.label,
          documents: 1,
          boost: candidate.boost,
        });
      }
    }
  }
  for (const candidate of videoCandidates.values()) {
    add(
      candidate.label,
      [],
      "视频关键词",
      100 + candidate.boost * 5 + Math.log1p(candidate.documents) * 4,
    );
  }

  return [...suggestions.values()]
    .sort(
      (left, right) =>
        right.score - left.score ||
        left.label.localeCompare(right.label, "zh-CN"),
    )
    .slice(0, limit);
}

const sortModes: {
  key: DesktopVideoSort;
  label: string;
  group: "用户策略" | "公共策略";
}[] = [
  { key: "personalized", label: "智能推荐", group: "用户策略" },
  { key: "public", label: "综合热度", group: "公共策略" },
  { key: "manualScore", label: "策略分最高", group: "公共策略" },
  { key: "clicks", label: "点击最多", group: "公共策略" },
  { key: "watchTime", label: "播放最长", group: "公共策略" },
  { key: "comments", label: "评论最多", group: "公共策略" },
  { key: "latest", label: "最新上传", group: "公共策略" },
];

const fallbackVideos: ColumnVideo[] = [
  {
    id: "desktop-music-1",
    title: "城市夜景音乐精选｜放松工作与学习节奏",
    channel: "DaVinci Music",
    meta: "12万次观看 · 2小时前",
    thumbnail:
      "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?auto=format&fit=crop&w=1200&q=82",
    videoUrl:
      "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    duration: "32:18",
    category: "music",
  },
  {
    id: "desktop-music-2",
    title: "霓虹城市 DJ Remix｜2026 热门舞曲串烧",
    channel: "Night Drive",
    meta: "8.6万次观看 · 5小时前",
    thumbnail:
      "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=82",
    videoUrl:
      "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    duration: "58:42",
    category: "music",
  },
  {
    id: "desktop-fresh-1",
    title: "创作者工作室：一支音乐视频是怎样完成的",
    channel: "Behind The Lens",
    meta: "3.4万次观看 · 1天前",
    thumbnail:
      "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&w=1200&q=82",
    videoUrl:
      "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    duration: "12:09",
    category: "freshVideos",
  },
  {
    id: "desktop-live-1",
    title: "LIVE｜电子音乐现场与灯光秀",
    channel: "DaVinci Live",
    meta: "1.2万人正在观看",
    thumbnail:
      "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?auto=format&fit=crop&w=1200&q=82",
    videoUrl:
      "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    duration: "直播",
    category: "live",
  },
  {
    id: "desktop-games-1",
    title: "本周值得玩的独立游戏推荐",
    channel: "Pixel Station",
    meta: "21万次观看 · 3天前",
    thumbnail:
      "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1200&q=82",
    videoUrl:
      "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    duration: "16:30",
    category: "games",
  },
  {
    id: "desktop-shorts-1",
    title: "60 秒看完一场城市日落",
    channel: "Frame by Frame",
    meta: "45万次观看 · 4天前",
    thumbnail:
      "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=82",
    videoUrl:
      "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
    duration: "0:59",
    category: "shorts",
  },
];

const sidebar = [
  ["home", "首页"],
  ["zap", "短视频"],
  ["youtube", "订阅内容"],
  ["bookmark", "收藏夹"],
  ["user", "个人中心"],
  ["clock", "观看记录"],
] as const;

const desktopPlayerLoadingLogo = require("../../../assets/haihuang-logo-gold.png");

function DesktopPlayerLoadingPlaceholder({
  title,
  thumbnail,
  source,
  status,
}: {
  title: string;
  thumbnail?: string;
  source: "cache" | "network" | "";
  status?: string;
}) {
  const countdownMatch = (status || "").match(/预计\s*(\d+)\s*秒/);
  const countdownSeconds = countdownMatch ? Number(countdownMatch[1]) : 0;
  const dramaticCountdown =
    countdownSeconds >= 1 && countdownSeconds <= 4
      ? countdownSeconds
      : null;
  const sourceCopy =
    source === "cache"
      ? "本机缓存 · 正在恢复画面"
      : source === "network"
        ? "网络节点 · 正在缓冲"
        : "正在准备播放";
  const visibleStatus = dramaticCountdown
    ? (status || sourceCopy).replace(/\s*·\s*预计\s*\d+\s*秒/, "")
    : status || sourceCopy;
  return createElement("div", { className: "neptune-player-loading", role: "status", "aria-live": "polite", "aria-label": "正在加载视频" }, [
    createElement("style", { key: "neptune-player-loading-style" }, `
      @keyframes neptunePlayerAurora { 0%,100% { transform:translate3d(-5%,-4%,0) rotate(0deg) scale(1); opacity:.58; } 50% { transform:translate3d(5%,4%,0) rotate(18deg) scale(1.13); opacity:.96; } }
      @keyframes neptunePlayerOrbit { to { transform:rotate(360deg); } }
      @keyframes neptunePlayerCounterOrbit { to { transform:rotate(-360deg); } }
      @keyframes neptunePlayerLogo { 0%,100% { transform:scale(.96); filter:drop-shadow(0 0 10px rgba(241,200,104,.35)); } 50% { transform:scale(1.06); filter:drop-shadow(0 0 30px rgba(255,215,125,.95)); } }
      @keyframes neptunePlayerCountdown { 0% { opacity:0; transform:scale(1.72) rotate(-5deg); filter:blur(7px); } 22% { opacity:1; transform:scale(.88) rotate(1deg); filter:blur(0); } 48% { transform:scale(1.08); } 72%,100% { opacity:1; transform:scale(1); filter:blur(0); } }
      @keyframes neptunePlayerCountdownGlow { 0%,100% { text-shadow:0 0 12px rgba(241,200,104,.72),0 0 42px rgba(40,201,154,.28); } 50% { text-shadow:0 0 24px rgba(255,226,143,1),0 0 72px rgba(40,201,154,.72); } }
      @keyframes neptunePlayerThumbnail { from { opacity:0; transform:translateY(10px) scale(.97); } to { opacity:1; transform:translateY(0) scale(1); } }
      @keyframes neptunePlayerSignal { 0% { transform:translateX(-120%); opacity:0; } 20%,80% { opacity:.8; } 100% { transform:translateX(120%); opacity:0; } }
      .neptune-player-loading { position:absolute; inset:0; overflow:hidden; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:13px; background:radial-gradient(circle at 50% 45%,#183229 0%,#081510 42%,#030705 100%); color:#e9f8f1; z-index:1102; }
      .neptune-player-loading:before,.neptune-player-loading:after { content:""; position:absolute; width:72%; aspect-ratio:1; border-radius:50%; background:conic-gradient(from 10deg,transparent 0 16%,rgba(47,218,166,.16) 28%,rgba(235,190,83,.56) 44%,transparent 60%); filter:blur(8px); animation:neptunePlayerAurora 4.5s ease-in-out infinite; }
      .neptune-player-loading:after { width:48%; animation-delay:-2.2s; opacity:.72; }
      .neptune-player-orbit { position:absolute; width:min(25vw,180px); aspect-ratio:1; border:1px solid rgba(215,182,93,.58); border-left-color:transparent; border-radius:50%; animation:neptunePlayerOrbit 5s linear infinite; }
      .neptune-player-orbit:after { content:""; position:absolute; width:8px; height:8px; right:12%; top:10%; border-radius:50%; background:#f1c868; box-shadow:0 0 14px 5px rgba(241,200,104,.58); }
      .neptune-player-orbit-inner { position:absolute; width:min(18vw,128px); aspect-ratio:1; border:1px solid rgba(40,201,154,.55); border-right-color:transparent; border-radius:50%; animation:neptunePlayerCounterOrbit 3.8s linear infinite; }
      .neptune-player-logo { position:relative; z-index:1; width:min(12vw,86px); height:min(12vw,86px); object-fit:contain; animation:neptunePlayerLogo 2.1s ease-in-out infinite; }
      .neptune-player-countdown { position:relative; z-index:2; width:min(25vw,180px); height:min(25vw,180px); display:flex; align-items:center; justify-content:center; color:#f5d475; font:900 min(21vw,154px)/.8 MiSans,Inter,"Microsoft YaHei",sans-serif; letter-spacing:-.08em; text-indent:-.08em; -webkit-text-stroke:1px rgba(255,238,173,.32); animation:neptunePlayerCountdown .82s cubic-bezier(.16,.84,.3,1) both,neptunePlayerCountdownGlow 1s ease-in-out infinite; }
      .neptune-player-copy { position:relative; z-index:1; text-align:center; letter-spacing:.14em; text-transform:uppercase; font:700 11px MiSans,Inter,"Microsoft YaHei",sans-serif; color:#f0d48a; text-shadow:0 1px 18px rgba(0,0,0,.85); }
      .neptune-player-subcopy { position:relative; z-index:1; max-width:76%; overflow:hidden; white-space:nowrap; text-overflow:ellipsis; font:500 11px MiSans,Inter,"Microsoft YaHei",sans-serif; color:rgba(202,237,224,.7); }
      .neptune-player-thumbnail { position:relative; z-index:1; display:block; width:min(78vw,340px); aspect-ratio:16/9; margin-top:3px; border:1px solid rgba(241,200,104,.42); border-radius:10px; object-fit:cover; background:#06100c; box-shadow:0 12px 34px rgba(0,0,0,.42),0 0 24px rgba(40,201,154,.12); animation:neptunePlayerThumbnail .55s ease-out both; }
      .neptune-player-signal { position:absolute; bottom:14%; width:45%; height:1px; overflow:hidden; background:rgba(65,181,143,.2); }
      .neptune-player-signal:after { content:""; position:absolute; inset:0; width:44%; background:linear-gradient(90deg,transparent,#31d4a2,#f1c868,transparent); animation:neptunePlayerSignal 1.65s ease-in-out infinite; }
      @media (prefers-reduced-motion:reduce) { .neptune-player-loading *,.neptune-player-loading:before,.neptune-player-loading:after { animation:none !important; } }
    `),
    createElement("div", { key: "orbit", className: "neptune-player-orbit" }),
    createElement("div", { key: "inner-orbit", className: "neptune-player-orbit-inner" }),
    dramaticCountdown
      ? createElement("div", { key: `countdown-${dramaticCountdown}`, className: "neptune-player-countdown", "aria-hidden": "true" }, dramaticCountdown)
      : createElement("img", { key: "logo", className: "neptune-player-logo", src: desktopPlayerLoadingLogo, alt: "" }),
    createElement("div", { key: "copy", className: "neptune-player-copy" }, source === "cache" ? "SEAKING · LOCAL CACHE" : "SEAKING · PREPARING THE STAGE"),
    createElement("div", { key: "source", className: "neptune-player-copy" }, visibleStatus),
    createElement("div", { key: "subcopy", className: "neptune-player-subcopy" }, title),
    thumbnail
      ? createElement("img", {
          key: "thumbnail",
          className: "neptune-player-thumbnail",
          src: thumbnail,
          alt: `${title} 视频封面`,
        })
      : null,
    createElement("div", { key: "signal", className: "neptune-player-signal" }),
  ]);
}

function Brand({ onPress }: { onPress?: () => void }) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="打开客户端下载二维码"
      onPress={onPress}
      style={({ pressed }) => [styles.brand, pressed && styles.brandPressed]}
    >
      <Image
        source={
          Platform.OS === "web"
            ? { uri: `/tiktok/i18n/logos/${siteLocale}.png` }
            : require("../../../assets/haihuang-logo-gold.png")
        }
        style={styles.brandLogo}
        resizeMode="contain"
      />
    </Pressable>
  );
}

function SidebarItem({
  icon,
  label,
  active = false,
  onPress,
  accessibilityLabel,
}: {
  icon: string;
  label: string;
  active?: boolean;
  onPress?: () => void;
  accessibilityLabel?: string;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || label}
      style={[styles.sidebarItem, active && styles.sidebarItemActive]}
      onPress={onPress}
    >
      <Feather
        name={icon as never}
        size={20}
        color={active ? "#28c99a" : "#a9b2af"}
      />
      <Text style={[styles.sidebarLabel, active && styles.sidebarLabelActive]}>
        {label}
      </Text>
    </Pressable>
  );
}

type HoverPreviewStatus = "loading" | "playing" | "blocked" | "error";

const isHlsUrl = (url: string) => /\.m3u8(?:$|[?#])/i.test(url);

const isDirectAv1Mp4 = (variant: {
  url: string;
  codec?: string;
  label?: string;
}) =>
  /\.mp4(?:$|[?#])/i.test(variant.url) &&
  /av1|av01/i.test(`${variant.codec || ""} ${variant.label || ""}`);

const cardThumbnailUrl = (url: string) => {
  if (!url) return url;
  const normalized = url.replace(
    /\/media\/thumbnails-v2\//gi,
    "/media/thumbnails/",
  );
  return `${normalized}${normalized.includes("?") ? "&" : "?"}v=thumb-v2`;
};

function hoverPreviewUrl(item: ColumnVideo): string {
  const variants = (item.mediaVariants || []).filter(
    (variant) => variant.kind !== "audio" && Boolean(variant.url),
  ).map(mergeLandscapeVariantIntoDesktop);
  const directHls = variants.filter(
    (variant) =>
      isHlsUrl(variant.url) && !/master\.m3u8(?:$|[?#])/i.test(variant.url),
  );
  const qualityText = (variant: (typeof variants)[number]) =>
    `${variant.quality} ${variant.resolution} ${variant.label}`.toLowerCase();
  const byKindAndQuality = (
    kind: "desktop" | "mobile",
    quality: "480p" | "720p",
  ) =>
    directHls.find(
      (variant) =>
        variant.kind === kind && qualityText(variant).includes(quality),
    );

  const preferredHls =
    byKindAndQuality("desktop", "480p") ||
    byKindAndQuality("desktop", "720p") ||
    directHls.find((variant) => variant.kind === "desktop") ||
    byKindAndQuality("mobile", "480p") ||
    byKindAndQuality("mobile", "720p") ||
    directHls.find((variant) => variant.kind === "mobile");
  if (preferredHls) {
    return preferredHls.url;
  }

  const directFile =
    variants.find(
      (variant) =>
        variant.kind === "desktop" &&
        /\.(?:mp4|webm)(?:$|[?#])/i.test(variant.url),
    ) ||
    variants.find((variant) => /\.(?:mp4|webm)(?:$|[?#])/i.test(variant.url));
  return directFile?.url || item.desktopVideoUrl || item.videoUrl;
}

function usesPortraitPreview(item: ColumnVideo): boolean {
  const previewUrl = hoverPreviewUrl(item);
  const variant = (item.mediaVariants || []).find(
    (candidate) => candidate.url === previewUrl,
  );
  const match = /^(\d+)\s*x\s*(\d+)$/i.exec(variant?.resolution || "");
  return Boolean(match && Number(match[1]) < Number(match[2]));
}

function cardThumbnailForVideo(item: ColumnVideo): string {
  return usesPortraitPreview(item) && item.portraitThumbnail
    ? item.portraitThumbnail
    : item.thumbnail;
}

function DesktopHoverPreview({ item }: { item: ColumnVideo }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<HoverPreviewStatus>("loading");
  const sourceUrl = useMemo(() => hoverPreviewUrl(item), [item]);
  const portraitPreview = useMemo(() => usesPortraitPreview(item), [item]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !sourceUrl) {
      setStatus("error");
      return;
    }

    let disposed = false;
    let hls: Hls | null = null;
    setStatus("loading");
    video.defaultMuted = false;
    video.muted = false;
    video.volume = 1;

    const markPlaying = () => {
      if (!disposed) setStatus("playing");
    };
    const markError = () => {
      if (!disposed) setStatus("error");
    };
    const startPlayback = () => {
      if (disposed) return;
      video.defaultMuted = false;
      video.muted = false;
      video.volume = 1;
      const playRequest = video.play();
      playRequest?.catch((error: { name?: string }) => {
        if (disposed) return;
        setStatus(error?.name === "NotAllowedError" ? "blocked" : "error");
      });
    };

    video.addEventListener("playing", markPlaying);
    video.addEventListener("error", markError);

    if (
      isHlsUrl(sourceUrl) &&
      !video.canPlayType("application/vnd.apple.mpegurl")
    ) {
      if (!Hls.isSupported()) {
        setStatus("error");
      } else {
        hls = new Hls({
          startLevel: 0,
          capLevelToPlayerSize: true,
          maxBufferLength: 6,
          maxMaxBufferLength: 10,
        });
        hls.loadSource(sourceUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, startPlayback);
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) markError();
        });
      }
    } else {
      video.src = sourceUrl;
      video.addEventListener("canplay", startPlayback, { once: true });
      video.load();
    }

    return () => {
      disposed = true;
      video.removeEventListener("playing", markPlaying);
      video.removeEventListener("error", markError);
      video.removeEventListener("canplay", startPlayback);
      video.pause();
      hls?.destroy();
      video.removeAttribute("src");
      video.load();
    };
  }, [sourceUrl]);

  return (
    <View style={styles.hoverPreviewLayer} accessibilityLabel="有声悬停预览">
      {createElement("video", {
        ref: videoRef,
        autoPlay: true,
        controls: false,
        loop: true,
        muted: false,
        playsInline: true,
        preload: "auto",
        poster: cardThumbnailUrl(cardThumbnailForVideo(item)),
        "aria-label": `${item.title} 有声悬停预览`,
        "data-hover-preview-video": item.id,
        style: {
          width: "100%",
          height: "100%",
          backgroundColor: "black",
          display: "block",
          objectFit: portraitPreview ? "contain" : "cover",
          opacity: status === "playing" ? 1 : 0,
          pointerEvents: "none",
          transition: "opacity 140ms ease",
        },
      })}
      {status === "loading" && (
        <View style={styles.hoverPreviewMessage}>
          <Text style={styles.hoverPreviewMessageText}>有声预览加载中…</Text>
        </View>
      )}
      {status === "blocked" && (
        <View style={styles.hoverPreviewMessage}>
          <Text style={styles.hoverPreviewMessageText}>
            浏览器已拦截声音，请先点击页面后再悬停
          </Text>
        </View>
      )}
      {status === "playing" && (
        <View style={styles.hoverPreviewBadge}>
          <Feather name="volume-2" size={12} color="#17120b" />
          <Text style={styles.hoverPreviewBadgeText}>有声预览</Text>
        </View>
      )}
    </View>
  );
}

function VideoCard({
  item,
  width,
  onPress,
  previewActive,
  onHoverIn,
  onHoverOut,
  contextLabel,
  horizontal = false,
  thumbnailWidth = 240,
  coverOnly = false,
  onLayout,
}: {
  item: ColumnVideo;
  width: number;
  onPress: () => void;
  previewActive: boolean;
  onHoverIn: () => void;
  onHoverOut: () => void;
  contextLabel?: string;
  horizontal?: boolean;
  thumbnailWidth?: number;
  coverOnly?: boolean;
  onLayout?: (event: { nativeEvent: { layout: { y: number } } }) => void;
}) {
  const portraitPreview = usesPortraitPreview(item);
  return (
    <Pressable
      style={[
        styles.card,
        { width },
        coverOnly && { paddingBottom: 0 },
        horizontal && { flexDirection: "row", alignItems: "center" },
      ]}
      onPress={onPress}
      accessibilityLabel={item.title}
      onLayout={onLayout}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
    >
      <View
        style={[
          styles.thumbnailWrap,
          horizontal && {
            width: Math.min(thumbnailWidth, width * 0.42),
            flexShrink: 0,
          },
        ]}
      >
        {item.vipLocked ? (
          <View style={styles.vipLockedPlaceholder}>
            <View style={styles.vipLockedIcon}>
              <Feather name="lock" size={25} color="#17120b" />
            </View>
            <Text style={styles.vipLockedTitle}>VIP 专属内容</Text>
            <Text style={styles.vipLockedHint}>注册并升级 VIP 后解锁</Text>
          </View>
        ) : (
          <Image
            source={{ uri: cardThumbnailUrl(cardThumbnailForVideo(item)) }}
            style={[styles.thumbnail, portraitPreview && { backgroundColor: "black" }]}
            resizeMode={portraitPreview ? "contain" : "cover"}
          />
        )}
        {previewActive && !item.vipLocked && (
          <DesktopHoverPreview item={item} />
        )}
        {item.isAdvertisement ? (
          <View style={styles.cardAdBadge}>
            <Feather name="radio" size={12} color="#fff4dd" />
            <Text style={styles.cardAdBadgeText}>广告</Text>
          </View>
        ) : null}
        {item.isPaid && Number(item.coinPrice) > 0 ? (
          <View
            style={[
              styles.cardPaidBadge,
              item.isAdvertisement && styles.cardPaidBadgeWithAd,
            ]}
          >
            <Feather name="lock" size={12} color="#17120b" />
            <Text style={styles.cardPaidBadgeText} numberOfLines={1}>
              收费 · {item.coinPrice} 币
            </Text>
          </View>
        ) : null}
        {item.vipOnly && !item.vipLocked ? (
          <View style={styles.cardVipBadge}>
            <Feather name="shield" size={12} color="#04110d" />
            <Text style={styles.cardVipBadgeText}>VIP 专属</Text>
          </View>
        ) : null}
        {contextLabel ? (
          <View style={styles.cardContextBadge}>
            <Text style={styles.cardContextText} numberOfLines={1}>
              {contextLabel}
            </Text>
          </View>
        ) : null}
        <View style={styles.durationBadge}>
          <Text style={styles.durationText}>{item.duration}</Text>
        </View>
      </View>
      {!coverOnly && (
        <View style={[styles.cardInfo, horizontal && { flex: 1, minWidth: 0 }]}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>
              {item.channel.trim().slice(0, 1).toUpperCase() || "D"}
            </Text>
          </View>
          <View style={styles.cardCopy}>
            <Text style={styles.cardTitle} numberOfLines={2}>
              {item.title}
            </Text>
            <Text style={styles.channel} numberOfLines={1}>
              {item.channel}
            </Text>
            <Text style={styles.meta} numberOfLines={1}>
              {item.meta}
            </Text>
            {item.isAdvertisement ? (
              <Text style={styles.adDeliveryHint} numberOfLines={1}>
                广告 ·{" "}
                {advertisementDisplayModeLabels[item.adDisplayMode || "smart"]}{" "}
                · 播放 {item.adPlayCount || 0}/{item.adMaxPlays || 1000} · 剩余{" "}
                {item.adRemainingPlays || 0}
              </Text>
            ) : null}
            {item.ranking && (
              <Text style={styles.rankingHint} numberOfLines={1}>
                {`#${item.ranking.position} · ${item.ranking.reason} · 点击 ${
                  item.ranking.metrics.clicks
                } · 播放 ${Math.round(
                  item.ranking.metrics.watchSeconds / 60,
                )} 分钟 · 评论 ${item.ranking.metrics.comments}`}
              </Text>
            )}
          </View>
          <Feather name="more-vertical" size={19} color="#606060" />
        </View>
      )}
    </Pressable>
  );
}

function DesktopHlsPlayer({
  video: item,
  identity,
  initialResumePosition = 0,
  onHistoryCommitted,
  onPlaybackFinish,
  onRequirePaidLogin,
  windowControls,
  windowControlsHost,
  headerControlsHost,
  actionControlsHost,
  shareControlsHost,
  cacheDiagnosticsHost,
  showCacheDiagnostics = false,
  intelligentCachePlan = [],
  playerOpenMode,
  onPlayerOpenModeChange,
  maximizeVideoArea = false,
  fitBrowserViewport = false,
  startVideoFullscreen = false,
  onVideoFullscreenChange,
}: {
  video: ColumnVideo;
  identity: AccessIdentity;
  initialResumePosition?: number;
  onHistoryCommitted?: () => void;
  onPlaybackFinish?: (mode: LoopMode) => void;
  onRequirePaidLogin?: () => void;
  windowControls?: ReactNode;
  windowControlsHost?: Element | null;
  headerControlsHost?: Element | null;
  actionControlsHost?: Element | null;
  shareControlsHost?: Element | null;
  cacheDiagnosticsHost?: Element | null;
  showCacheDiagnostics?: boolean;
  intelligentCachePlan?: IntelligentCacheTarget<ColumnVideo>[];
  playerOpenMode: "floating" | "window";
  onPlayerOpenModeChange: (mode: "floating" | "window") => void;
  maximizeVideoArea?: boolean;
  fitBrowserViewport?: boolean;
  startVideoFullscreen?: boolean;
  onVideoFullscreenChange?: (fullscreen: boolean) => void;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const { height: viewportHeight } = useWindowDimensions();
  const windowsNativePlayback = isWindowsNativePlaybackActive();
  const loopMode = useSelector((state: RootState) => state.playback.loopMode);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoFullscreen, setVideoFullscreen] = useState(startVideoFullscreen);
  useEffect(() => {
    onVideoFullscreenChange?.(videoFullscreen);
  }, [videoFullscreen, onVideoFullscreenChange]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const nativeSlotRef = useRef<HTMLDivElement | null>(null);
  const [initialSoundPreference] = useState(() => readDesktopSoundPreference());
  const resumeTimeRef = useRef(0);
  const initialSavedResumePosition = getHlsCacheSettings().resumePlayback
    ? Math.max(getResumePosition(item.id), initialResumePosition)
    : 0;
  const resumePromptDismissedRef = useRef(false);
  const [resumePromptPosition, setResumePromptPosition] = useState(
    initialSavedResumePosition,
  );
  const [resumePromptSecondsLeft, setResumePromptSecondsLeft] = useState(15);
  const resumePlayingRef = useRef(true);
  const sourceSwitchingRef = useRef(false);
  const adaptiveBandwidthRef = useRef(browserPlaybackNetwork().bandwidthBps);
  const adaptiveStableSinceRef = useRef(Date.now());
  const adaptiveLastSwitchRef = useRef(0);
  const adaptiveStallsRef = useRef<number[]>([]);
  const adaptiveNodeSwitchRef = useRef(0);
  const attemptedPlaybackUrlsRef = useRef<Set<string>>(new Set());
  const lastResumeSaveRef = useRef(0);
  const completedCacheRequestedRef = useRef("");
  const cacheCheckpointRef = useRef({ url: "", milestone: 0 });
  const initializedPlayerItemRef = useRef("");
  const smartBufferingRef = useRef(false);
  const smartBufferResumeRef = useRef(true);
  const beginSmartBufferingRef = useRef<(reason: string) => void>(() => {});
  const mediaSourceRecoveryRef = useRef<(() => boolean) | null>(null);
  const measuredMediaBitrateRef = useRef({ variantId: "", bps: 0 });
  const mediaBitrateFallbackRef = useRef<Set<string>>(new Set());
  const smartBufferProgressRef = useRef({
    seconds: 0,
    progressedAt: Date.now(),
    recoveredAt: 0,
    attempts: 0,
  });
  const soundUnlockedRef = useRef(initialSoundPreference.soundEnabled);
  const soundVolumeRef = useRef(initialSoundPreference.volume);
  const playbackPreferenceOwnerKey = playbackPreferenceOwner(
    identity?.uid,
    getAccessVisitorId(),
  );
  const videoVariants = useMemo(() => {
    const allVideoVariants = (item.mediaVariants || []).filter(
      (variant) =>
        variant.kind !== "audio" &&
        !isDirectAv1Mp4(variant) &&
        (!windowsNativePlayback || isWindowsCompatibleMediaVariant(variant)),
    ).map(mergeLandscapeVariantIntoDesktop);
    return [...allVideoVariants].sort((left, right) => {
      if (left.kind === right.kind) {
        return 0;
      }
      return left.kind === "desktop" ? -1 : right.kind === "desktop" ? 1 : 0;
    });
  }, [item.mediaVariants, windowsNativePlayback]);
  const audioVariants = useMemo(
    () =>
      (item.mediaVariants || []).filter((variant) => variant.kind === "audio"),
    [item.mediaVariants],
  );
  const defaultVideoVariant = useMemo(
    () =>
      preferredPlaybackVariant(
        videoVariants,
        item.desktopVideoUrl || item.videoUrl,
        "desktop",
      ) || preferredPlaybackVariant(videoVariants, item.videoUrl, "mobile"),
    [item.desktopVideoUrl, item.videoUrl, videoVariants],
  );
  const vpsFallbackVariant = useMemo(() => {
    const vpsVariants = videoVariants.filter(
      (variant) =>
        !/\/frp-media\//i.test(variant.url) &&
        !/\/api\/videos\/[^/]+\/(?:mobile|desktop)-master\.m3u8(?:$|[?#])/i.test(
          variant.url,
        ) &&
        /^https?:\/\//i.test(variant.url),
    );
    return (
      vpsVariants.find(
        (variant) =>
          variant.kind === "mobile" &&
          /(?:^|\D)480p(?:\D|$)/i.test(
            `${variant.quality} ${variant.resolution} ${variant.label}`,
          ),
      ) ||
      vpsVariants.find((variant) => variant.kind === "mobile") ||
      vpsVariants[0]
    );
  }, [videoVariants]);
  const [videoSelectionMode, setVideoSelectionMode] = useState<
    "auto" | "variant"
  >(() => {
    return readPlaybackVariantPreference(playbackPreferenceOwnerKey).videoMode;
  });
  const [selectedVideoId, setSelectedVideoId] = useState(() => {
    const preference = readPlaybackVariantPreference(
      playbackPreferenceOwnerKey,
    );
    if (preference.videoMode === "auto") {
      const network = browserPlaybackNetwork();
      return (
        initialAdaptiveVariant(
          videoVariants,
          network.bandwidthBps,
          viewportHeight,
          network.saveData,
        )?.id ||
        defaultVideoVariant?.id ||
        ""
      );
    }
    return (
      matchPlaybackVariantPreference(videoVariants, preference.video)?.id ||
      defaultVideoVariant?.id ||
      ""
    );
  });
  const [selectedAudioId, setSelectedAudioId] = useState(() => {
    const preference = readPlaybackVariantPreference(
      playbackPreferenceOwnerKey,
    );
    if (preference.audioMode !== "variant") return "";
    return (
      matchPlaybackVariantPreference(audioVariants, preference.audio)?.id || ""
    );
  });
  const [mediaControlPanel, setMediaControlPanel] = useState<
    "video" | "audio" | null
  >(null);
  const [sourceError, setSourceError] = useState("");
  const [videoSourceError, setVideoSourceError] = useState("");
  const [faultToleranceStatus, setFaultToleranceStatus] = useState("");
  const [downgradeLevel, setDowngradeLevel] = useState(readDowngradeLevel);
  const downgradeLevelRef = useRef(downgradeLevel);
  const downgradePressureRef = useRef<DowngradePressure>({ since: null, lastSampleAt: null });
  const adaptivePressureRef = useRef<DowngradePressure>({ since: null, lastSampleAt: null });
  const [cachePrefetchLevel, setCachePrefetchLevel] = useState(
    () => getDesktopCachePolicy().policy.prefetchTracks ?? 3,
  );
  const [cachePolicyStatus, setCachePolicyStatus] = useState("");
  const cachePolicySaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const downgradePolicy = playbackDowngradePolicy(downgradeLevel);
  const changeDowngradeLevel = (value: number) => {
    const next = playbackDowngradePolicy(value).level;
    downgradeLevelRef.current = next;
    downgradePressureRef.current = { since: null, lastSampleAt: null };
    adaptivePressureRef.current = { since: null, lastSampleAt: null };
    setDowngradeLevel(next);
    try {
      window.localStorage.setItem(DOWNGRADE_LEVEL_STORAGE_KEY, String(next));
    } catch {
      // Storage may be unavailable; the setting still applies to this player.
    }
  };
  useEffect(() => {
    const sync = () =>
      setCachePrefetchLevel(getDesktopCachePolicy().policy.prefetchTracks ?? 3);
    window.addEventListener("playback-cache-policy-changed", sync);
    return () => {
      window.removeEventListener("playback-cache-policy-changed", sync);
      if (cachePolicySaveTimerRef.current) clearTimeout(cachePolicySaveTimerRef.current);
    };
  }, []);
  const changeCachePrefetchLevel = (value: number) => {
    const next = Math.max(0, Math.min(10, Math.round(value)));
    setCachePrefetchLevel(next);
    setCachePolicyStatus("正在保存缓存策略…");
    if (cachePolicySaveTimerRef.current) clearTimeout(cachePolicySaveTimerRef.current);
    cachePolicySaveTimerRef.current = setTimeout(() => {
      void saveDesktopPrefetchTracks(next)
        .then((profile) => {
          setCachePrefetchLevel(profile.policy.prefetchTracks ?? next);
          setCachePolicyStatus("缓存策略已保存");
        })
        .catch((error) =>
          setCachePolicyStatus(`缓存策略保存失败：${String(error?.message || error)}`),
        );
    }, 350);
  };
  const [soundUnlocked, setSoundUnlocked] = useState(
    initialSoundPreference.soundEnabled,
  );
  const [soundVolume, setSoundVolume] = useState(initialSoundPreference.volume);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [commerceTimeSeconds, setCommerceTimeSeconds] = useState(0);
  const [productPlacements, setProductPlacements] = useState(
    item.productPlacements || [],
  );
  const [productEditorOpen, setProductEditorOpen] = useState(false);
  const selectedVideo =
    videoVariants.find((variant) => variant.id === selectedVideoId) ||
    defaultVideoVariant;
  const selectedAudio = audioVariants.find(
    (variant) => variant.id === selectedAudioId,
  );
  const selectedVideoBitrateRef = useRef(3_500_000);
  if (measuredMediaBitrateRef.current.variantId !== selectedVideo?.id) {
    measuredMediaBitrateRef.current = {
      variantId: selectedVideo?.id || "",
      bps: 0,
    };
  }
  selectedVideoBitrateRef.current =
    measuredMediaBitrateRef.current.bps ||
    (selectedVideo ? adaptiveVariantBitrate(selectedVideo) : 3_500_000);
  const rememberSoundPreference = (soundEnabled: boolean, volume: number) => {
    const preference = writeDesktopSoundPreference({ soundEnabled, volume });
    soundUnlockedRef.current = preference.soundEnabled;
    soundVolumeRef.current = preference.volume;
    setSoundUnlocked(preference.soundEnabled);
    setSoundVolume(preference.volume);
    return preference;
  };
  const uri = selectedVideo?.url || item.desktopVideoUrl || item.videoUrl;
  const [playbackSource, setPlaybackSource] = useState({
    sourceUri: uri,
    url: uri,
  });
  // The previous edge URL must not be loaded with a newly selected variant.
  const playbackUri =
    playbackSource.sourceUri === uri ? playbackSource.url : uri;
  const initialEdgePreference = useMemo(readPlaybackEdgePreference, []);
  const [edgeSelectionMode, setEdgeSelectionMode] =
    useState<PlaybackEdgeMode>(initialEdgePreference.mode);
  const [edgePriorityIds, setEdgePriorityIds] = useState<string[]>(
    initialEdgePreference.priorityIds,
  );
  const [manualEdgeId, setManualEdgeId] = useState(
    initialEdgePreference.manualEdgeId,
  );
  const availableEdges = useMemo(
    () => mediaEdgeOptions(uri, edgePriorityIds),
    [edgePriorityIds, uri],
  );
  const saveEdgePreference = (
    mode: PlaybackEdgeMode,
    priorityIds: string[],
    selectedManualEdgeId: string,
  ) => {
    writePlaybackEdgePreference({
      mode,
      priorityIds,
      manualEdgeId: selectedManualEdgeId,
    });
  };
  const chooseEdgeMode = (mode: PlaybackEdgeMode) => {
    setEdgeSelectionMode(mode);
    saveEdgePreference(mode, edgePriorityIds, manualEdgeId);
  };
  const chooseManualEdge = (edgeId: string) => {
    setManualEdgeId(edgeId);
    setEdgeSelectionMode("manual");
    saveEdgePreference("manual", edgePriorityIds, edgeId);
  };
  const moveAutomaticEdge = (edgeId: string, direction: -1 | 1) => {
    const orderedIds = availableEdges.map((edge) => edge.id);
    const index = orderedIds.indexOf(edgeId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= orderedIds.length) return;
    [orderedIds[index], orderedIds[target]] = [
      orderedIds[target],
      orderedIds[index],
    ];
    setEdgePriorityIds(orderedIds);
    setEdgeSelectionMode("auto");
    saveEdgePreference("auto", orderedIds, manualEdgeId);
  };
  const [playerLoading, setPlayerLoading] = useState(true);
  const [smartBufferStatus, setSmartBufferStatus] = useState(
    "智能缓冲 · 正在测算网速",
  );
  const [loadingSource, setLoadingSource] = useState<"cache" | "network" | "">("");
  const [cacheDiagnostics, setCacheDiagnostics] = useState<PlaybackCacheDiagnosticRow[]>([]);
  const [playbackHealth, setPlaybackHealth] = useState<PlaybackHealthSample>(() => ({
    stalls: 0,
    retries: 0,
    failures: 0,
    nodeSwitches: 0,
    bandwidthBps: browserPlaybackNetwork().bandwidthBps,
    mediaBitrateBps: 0,
    cacheRequests: 0,
    cacheHits: 0,
  }));
  const [edgeStatus, setEdgeStatus] = useState("");
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    const onCacheSource = (event: MessageEvent) => {
      const message = event.data;
      if (message?.type !== "HLS_PLAYBACK_SOURCE" && message?.type !== "HLS_CACHE_STORED") return;
      try {
        const current = new URL(playbackUri), actual = new URL(message.url);
        const matches = /\.m3u8(?:$|[?#])/i.test(playbackUri)
          ? actual.origin === current.origin && actual.pathname.startsWith(current.pathname.slice(0, current.pathname.lastIndexOf("/") + 1))
          : actual.href === current.href;
        if (!matches) return;
        if (message.type === "HLS_PLAYBACK_SOURCE") {
          setPlaybackHealth((current) => ({
            ...current,
            cacheRequests: current.cacheRequests + 1,
            cacheHits: current.cacheHits + (message.source === "cache" ? 1 : 0),
          }));
        }
        if (showCacheDiagnostics) {
          const resource = message.resource === "playlist"
            ? "playlist"
            : message.resource === "segment"
              ? "segment"
              : "video";
          const url = actual.href;
          const name = decodeURIComponent(actual.pathname.split("/").filter(Boolean).pop() || actual.pathname || "媒体资源");
          setCacheDiagnostics((rows) => {
            const existing = rows.find((row) => row.url === url);
            const stored = message.type === "HLS_CACHE_STORED";
            const hit = message.type === "HLS_PLAYBACK_SOURCE" && message.source === "cache";
            const next: PlaybackCacheDiagnosticRow = {
              url,
              name,
              resource,
              requests: (existing?.requests || 0) + (message.type === "HLS_PLAYBACK_SOURCE" ? 1 : 0),
              hits: (existing?.hits || 0) + (hit ? 1 : 0),
              cached: Boolean(existing?.cached || stored || hit),
              storing: stored || hit ? false : message.type === "HLS_PLAYBACK_SOURCE" && message.source === "network",
              bytes: Number(message.bytes) || existing?.bytes || 0,
              source: hit ? "cache" : existing?.source === "cache" && stored ? "cache" : "network",
              at: Number(message.at) || Date.now(),
            };
            return [next, ...rows.filter((row) => row.url !== url)]
              .sort((left, right) => right.at - left.at)
              .slice(0, 80);
          });
        }
        if (message.type === "HLS_CACHE_STORED") return;
        setLoadingSource(message.source === "cache" ? "cache" : "network");
        if (message.resource === "playlist") return;
        setEdgeDetails(value => ({ ...value,
          label: message.source === "cache" ? "本机缓存" : mediaEdgeLabelForUrl(playbackUri),
          status: edgeSelectionMode === "manual" ? "手动锁定" : "正常",
          bandwidthMbps: message.source === "cache" ? 0 : value.bandwidthMbps,
          reason: message.source === "cache" ? "正在复用已播放内容，无需重复下载此分片" : "当前分片来自网络，已播放内容按账号额度缓存",
        }));
      } catch {}
    };
    navigator.serviceWorker.addEventListener("message", onCacheSource);
    return () => navigator.serviceWorker.removeEventListener("message", onCacheSource);
  }, [edgeSelectionMode, playbackUri, showCacheDiagnostics]);
  useEffect(() => {
    setCacheDiagnostics([]);
  }, [item.id, playbackUri]);
  useEffect(() => {
    setPlaybackHealth({
      stalls: 0,
      retries: 0,
      failures: 0,
      nodeSwitches: 0,
      bandwidthBps: browserPlaybackNetwork().bandwidthBps,
      mediaBitrateBps: 0,
      cacheRequests: 0,
      cacheHits: 0,
    });
  }, [item.id]);
  const [edgeDetailsOpen, setEdgeDetailsOpen] = useState(false);
  const [edgeDetails, setEdgeDetails] = useState({
    label: mediaEdgeLabelForUrl(uri),
    latencyMs: 0,
    bandwidthMbps: 0,
    priority: 1,
    status: "正在检测",
    reason: "正在下载媒体样本测量真实网速",
  });
  const [adaptiveStatus, setAdaptiveStatus] = useState(
    "自动画质：正在监测带宽、缓冲和卡顿",
  );
  const [p2pActive, setP2pActive] = useState(item.p2pEnabled === true);
  const paidPrice = Math.max(1, Math.floor(Number(item.coinPrice) || 1));
  const paidEntitledRef = useRef(item.isPaid !== true);
  const paidGateCheckingRef = useRef(false);
  const paidGateResumeRef = useRef(false);
  const [paidEntitlement, setPaidEntitlement] =
    useState<PaidEntitlement | null>(null);
  const [paidGateVisible, setPaidGateVisible] = useState(false);
  const [paidGateMessage, setPaidGateMessage] = useState("");
  const [paidPurchaseSaving, setPaidPurchaseSaving] = useState(false);
  const desktopPlayerVariantLabel = (
    variant: NonNullable<ColumnVideo["mediaVariants"]>[number],
  ) => mediaVariantLabel(variant);

  beginSmartBufferingRef.current = (reason: string) => {
    const video = videoRef.current;
    if (!video || windowsNativePlayback || video.ended) return;
    if (!smartBufferingRef.current) {
      smartBufferProgressRef.current = {
        seconds: bufferedSecondsAhead(video),
        progressedAt: Date.now(),
        recoveredAt: 0,
        attempts: 0,
      };
    }
    smartBufferingRef.current = true;
    smartBufferResumeRef.current = true;
    resumePlayingRef.current = true;
    video.pause();
    setPlayerLoading(true);
    setSmartBufferStatus(`智能缓冲 · ${reason}`);
  };

  useEffect(() => {
    if (windowsNativePlayback) return;
    const timer = window.setInterval(() => {
      if (!smartBufferingRef.current) return;
      const video = videoRef.current;
      if (!video || video.ended) return;
      const bufferAhead = bufferedSecondsAhead(video);
      const target = smartBufferTargetSeconds({
        bandwidthBps: adaptiveBandwidthRef.current,
        bitrateBps: selectedVideoBitrateRef.current,
        recentStalls: adaptiveStallsRef.current.length,
      });
      const remaining = Number.isFinite(video.duration)
        ? Math.max(0, video.duration - (video.currentTime || 0))
        : Number.POSITIVE_INFINITY;
      const required = Math.min(target, remaining);
      const mbps = (adaptiveBandwidthRef.current / 1_000_000).toFixed(1);
      const now = Date.now();
      const progress = smartBufferProgressRef.current;
      if (bufferAhead > progress.seconds + 0.15) {
        progress.seconds = bufferAhead;
        progress.progressedAt = now;
        progress.attempts = 0;
      } else if (
        now - progress.progressedAt >= 7_000 &&
        now - progress.recoveredAt >= 7_000
      ) {
        progress.progressedAt = now;
        progress.recoveredAt = now;
        progress.attempts += 1;
        adaptiveBandwidthRef.current = Math.max(
          250_000,
          adaptiveBandwidthRef.current * 0.65,
        );
        const previousStall =
          adaptiveStallsRef.current[adaptiveStallsRef.current.length - 1] || 0;
        if (now - previousStall >= 6_000) {
          adaptiveStallsRef.current.push(now);
        }
        const restarted = mediaSourceRecoveryRef.current?.() === true;
        setSmartBufferStatus(
          `智能缓冲停滞 · 第 ${progress.attempts} 次恢复下载 · 目标水位已延长`,
        );
        video.dispatchEvent(new Event("stalled"));
        if (!restarted) {
          setVideoSourceError("缓冲无进度，正在切换播放节点…");
        }
        return;
      }
      const waitSeconds = estimateSmartBufferWaitSeconds({
        bufferedSeconds: bufferAhead,
        targetSeconds: required,
        bandwidthBps: adaptiveBandwidthRef.current,
        bitrateBps: selectedVideoBitrateRef.current,
      });
      setSmartBufferStatus(
        `智能缓冲 · ${bufferAhead.toFixed(1)}/${required.toFixed(1)} 秒 · 预计 ${waitSeconds} 秒 · ${mbps} Mbps`,
      );
      if (bufferAhead + 0.15 < required) return;

      smartBufferingRef.current = false;
      setPlayerLoading(false);
      if (!smartBufferResumeRef.current) return;
      resumePlayingRef.current = true;
      video.play().catch(() => {
        resumePlayingRef.current = false;
        setAutoplayBlocked(true);
      });
    }, 500);
    return () => window.clearInterval(timer);
  }, [item.id, windowsNativePlayback]);

  const dismissResumePrompt = () => {
    resumePromptDismissedRef.current = true;
    setResumePromptPosition(0);
    setResumePromptSecondsLeft(0);
  };

  const continueFromSavedPosition = () => {
    const position = Math.max(0, resumePromptPosition);
    if (!position) return dismissResumePrompt();
    resumeTimeRef.current = position;
    if (windowsNativePlayback) {
      notifyWindowsPlayerCommand("seek-to", position);
    } else if (videoRef.current) {
      videoRef.current.currentTime = position;
      if (audioRef.current) audioRef.current.currentTime = position;
    }
    setCommerceTimeSeconds(position);
    dismissResumePrompt();
  };

  const restartFromBeginning = () => {
    resumeTimeRef.current = 0;
    if (windowsNativePlayback) {
      notifyWindowsPlayerCommand("seek-to", 0);
    } else if (videoRef.current) {
      videoRef.current.currentTime = 0;
      if (audioRef.current) audioRef.current.currentTime = 0;
    }
    setCommerceTimeSeconds(0);
    dismissResumePrompt();
  };

  useEffect(() => {
    if (resumePromptPosition <= 0) return;
    const deadline = Date.now() + 15_000;
    setResumePromptSecondsLeft(15);
    const countdown = window.setInterval(() => {
      setResumePromptSecondsLeft(
        Math.max(0, Math.ceil((deadline - Date.now()) / 1000)),
      );
    }, 250);
    const timeout = window.setTimeout(() => {
      resumePromptDismissedRef.current = true;
      setResumePromptPosition(0);
      setResumePromptSecondsLeft(0);
    }, 15_000);
    return () => {
      window.clearInterval(countdown);
      window.clearTimeout(timeout);
    };
  }, [item.id, resumePromptPosition]);

  useEffect(() => {
    if (resumePromptDismissedRef.current) return;
    const savedPosition = getHlsCacheSettings().resumePlayback
      ? Math.max(getResumePosition(item.id), initialResumePosition)
      : 0;
    if (savedPosition > resumePromptPosition) {
      setResumePromptPosition(savedPosition);
    }
  }, [initialResumePosition, item.id, resumePromptPosition]);

  useEffect(() => {
    if (!windowsNativePlayback) return;
    const slot = nativeSlotRef.current;
    if (!slot) return;
    let frame = 0;
    let stopped = false;
    let previousSnapshot = "";
    const report = () => {
      if (stopped) return;
      const rect = slot.getBoundingClientRect();
      const round = (value: number) => Math.round(value * 2) / 2;
      const snapshot = {
        x: round(rect.left),
        y: round(rect.top),
        width: round(rect.width),
        height: round(rect.height),
        visible:
          rect.width >= 80 &&
          rect.height >= 45 &&
          rect.right > 0 &&
          rect.left < window.innerWidth &&
          rect.bottom > 0 &&
          rect.top < window.innerHeight,
      };
      const serialized = JSON.stringify(snapshot);
      if (serialized !== previousSnapshot) {
        previousSnapshot = serialized;
        notifyWindowsPlayerSlot(item.id, {
          ...snapshot,
        });
      }
      frame = requestAnimationFrame(report);
    };
    report();
    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      notifyWindowsPlayerSlot(item.id, {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        visible: false,
      });
    };
  }, [item.id, windowsNativePlayback]);

  const resumeAfterPaidGate = () => {
    const video = videoRef.current;
    if (!video) return;
    resumePlayingRef.current = true;
    video.play().catch(() => {
      resumePlayingRef.current = false;
      setAutoplayBlocked(true);
    });
  };

  const refreshPaidEntitlement = async (resumeWhenEntitled = false) => {
    if (item.isPaid !== true) {
      paidEntitledRef.current = true;
      return true;
    }
    if (paidEntitledRef.current) {
      if (resumeWhenEntitled) resumeAfterPaidGate();
      return true;
    }
    if (paidGateCheckingRef.current) {
      if (resumeWhenEntitled) paidGateResumeRef.current = true;
      return false;
    }
    paidGateCheckingRef.current = true;
    paidGateResumeRef.current ||= resumeWhenEntitled;
    setPaidGateMessage("正在验证账号和购买记录…");
    try {
      const entitlement = await fetchPaidVideoEntitlement(item.id);
      setPaidEntitlement(entitlement);
      paidEntitledRef.current = entitlement.entitled;
      if (entitlement.entitled) {
        setPaidGateVisible(false);
        setPaidGateMessage(
          entitlement.purchased ? "已购买，正在继续播放" : "验证通过",
        );
        if (paidGateResumeRef.current) resumeAfterPaidGate();
        return true;
      }
      setPaidGateVisible(true);
      setPaidGateMessage(
        entitlement.authenticated
          ? `账号已验证，余额 ${entitlement.account?.balance || 0} 币`
          : "请先验证登录账号，再使用虚拟币购买",
      );
      return false;
    } catch (error) {
      setPaidGateVisible(true);
      setPaidGateMessage(
        error instanceof Error ? error.message : "付费账号验证失败，请重试",
      );
      return false;
    } finally {
      paidGateCheckingRef.current = false;
      paidGateResumeRef.current = false;
    }
  };

  const completePaidPurchase = async () => {
    if (!paidEntitlement?.authenticated) {
      setPaidGateMessage("请先验证登录账号");
      onRequirePaidLogin?.();
      return;
    }
    setPaidPurchaseSaving(true);
    setPaidGateMessage(`正在支付 ${paidPrice} 币…`);
    try {
      const result = await purchasePaidVideo(item.id);
      if (!result.entitled) throw new Error("购买未完成，请重试");
      paidEntitledRef.current = true;
      setPaidEntitlement((current) =>
        current
          ? {
              ...current,
              entitled: true,
              purchased: true,
              account: result.account || current.account,
            }
          : current,
      );
      setPaidGateVisible(false);
      setPaidGateMessage(
        result.duplicate ? "已购买，继续播放" : "支付成功，继续播放",
      );
      resumeAfterPaidGate();
    } catch (error) {
      if (error instanceof PaidVideoApiError) {
        const paidErrorCode = error.code;
        const paidErrorAccount = error.account;
        setPaidEntitlement((current) =>
          current
            ? {
                ...current,
                authenticated: paidErrorCode !== "PAID_SESSION_REQUIRED",
                account: paidErrorAccount || current.account,
              }
            : current,
        );
        if (paidErrorCode === "PAID_SESSION_REQUIRED") onRequirePaidLogin?.();
      }
      setPaidGateMessage(
        error instanceof Error ? error.message : "购买失败，请重试",
      );
    } finally {
      setPaidPurchaseSaving(false);
    }
  };

  useEffect(() => {
    paidEntitledRef.current = item.isPaid !== true;
    paidGateCheckingRef.current = false;
    paidGateResumeRef.current = false;
    setPaidEntitlement(null);
    setPaidGateVisible(false);
    setPaidGateMessage("");
    setPaidPurchaseSaving(false);
    if (item.isPaid === true) void refreshPaidEntitlement(false);
  }, [item.id, item.isPaid, item.coinPrice]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(LOOP_MODE_STORAGE_KEY, loopMode);
    } catch {
      // Private browsing may deny storage; the current session still works.
    }
  }, [loopMode]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const openedAt = Date.now();
    const playbackId =
      globalThis.crypto?.randomUUID?.() || `${item.id}-${openedAt}`;
    let activePlaybackStartedAt = 0;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    const stopHeartbeat = () => {
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    };
    const takeWatchDelta = () => {
      if (!activePlaybackStartedAt) return 0;
      const delta = Math.max(
        0,
        Math.min((Date.now() - activePlaybackStartedAt) / 1000, 30),
      );
      activePlaybackStartedAt = video.paused ? 0 : Date.now();
      return delta;
    };
    const emit = (
      event:
        | "video_play"
        | "video_pause"
        | "video_seek"
        | "video_end"
        | "video_heartbeat",
      watchSecondsDelta = 0,
    ) =>
      recordAccessEvent(
        {
          event,
          videoId: item.id,
          videoTitle: item.title,
          positionSeconds: video.currentTime || 0,
          watchSecondsDelta,
          playbackId,
          clientEventId:
            globalThis.crypto?.randomUUID?.() ||
            `${playbackId}-${event}-${Date.now()}`,
        },
        identity,
      );
    const heartbeat = () => emit("video_heartbeat", takeWatchDelta());
    const onPlay = () => {
      activePlaybackStartedAt = Date.now();
      emit("video_play");
      stopHeartbeat();
      heartbeatTimer = setInterval(heartbeat, 15_000);
    };
    const onPause = () => {
      emit("video_pause", takeWatchDelta());
      activePlaybackStartedAt = 0;
      stopHeartbeat();
    };
    const onSeek = () => emit("video_seek", takeWatchDelta());
    const onEnd = () => {
      emit("video_end", takeWatchDelta());
      activePlaybackStartedAt = 0;
      stopHeartbeat();
    };
    recordAccessEvent(
      {
        event: "video_open",
        videoId: item.id,
        videoTitle: item.title,
        playbackId,
        clientEventId:
          globalThis.crypto?.randomUUID?.() || `${playbackId}-open`,
      },
      identity,
    );
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("seeked", onSeek);
    video.addEventListener("ended", onEnd);
    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("seeked", onSeek);
      video.removeEventListener("ended", onEnd);
      stopHeartbeat();
      void recordAccessEvent(
        {
          event: "video_close",
          videoId: item.id,
          videoTitle: item.title,
          durationMs: Date.now() - openedAt,
          positionSeconds: video.currentTime || 0,
          watchSecondsDelta: takeWatchDelta(),
          playbackId,
          clientEventId:
            globalThis.crypto?.randomUUID?.() || `${playbackId}-close`,
        },
        identity,
      ).finally(() => onHistoryCommitted?.());
    };
  }, [identity?.uid, item.id, item.title]);

  useEffect(() => {
    let active = true;
    const refresh = () => {
      fetchP2pStatus(item.id)
        .then((status) => {
          if (active) setP2pActive(status.active);
        })
        .catch(() => {});
    };
    setP2pActive(item.p2pEnabled === true);
    refresh();
    const timer = setInterval(refresh, 10_000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [item.id, item.p2pEnabled]);

  useEffect(() => {
    // The same video's media list and viewer identity may refresh while it is
    // playing. Initialize once so a manual quality lock is not reset to auto.
    if (initializedPlayerItemRef.current === item.id) return;
    initializedPlayerItemRef.current = item.id;
    const soundPreference = readDesktopSoundPreference();
    const variantPreference = readPlaybackVariantPreference(
      playbackPreferenceOwnerKey,
    );
    const preferredVideo = matchPlaybackVariantPreference(
      videoVariants,
      variantPreference.video,
    );
    const network = browserPlaybackNetwork();
    const automaticVideo = initialAdaptiveVariant(
      videoVariants,
      network.bandwidthBps,
      viewportHeight,
      network.saveData,
    );
    const preferredAudio =
      variantPreference.audioMode === "variant"
        ? matchPlaybackVariantPreference(audioVariants, variantPreference.audio)
        : undefined;
    // A newly opened video always starts at zero. The stored position is only
    // applied after the viewer explicitly accepts the resume prompt.
    resumeTimeRef.current = 0;
    setVideoSelectionMode(variantPreference.videoMode);
    setSelectedVideoId(
      variantPreference.videoMode === "auto"
        ? automaticVideo?.id || defaultVideoVariant?.id || ""
        : preferredVideo?.id || defaultVideoVariant?.id || "",
    );
    setSelectedAudioId(preferredAudio?.id || "");
    setSourceError("");
    setVideoSourceError("");
    setFaultToleranceStatus("");
    mediaBitrateFallbackRef.current.clear();
    soundUnlockedRef.current = soundPreference.soundEnabled;
    soundVolumeRef.current = soundPreference.volume;
    setSoundUnlocked(soundPreference.soundEnabled);
    setSoundVolume(soundPreference.volume);
    setAutoplayBlocked(false);
    setCommerceTimeSeconds(0);
    setProductPlacements(item.productPlacements || []);
    setProductEditorOpen(false);
    adaptiveBandwidthRef.current = network.bandwidthBps;
    adaptiveStableSinceRef.current = Date.now();
    adaptiveLastSwitchRef.current = Date.now();
    adaptiveStallsRef.current = [];
    smartBufferingRef.current = false;
    setSmartBufferStatus("智能缓冲 · 正在测算网速");
    setAdaptiveStatus("自动画质：正在监测带宽、缓冲和卡顿");
  }, [
    audioVariants,
    defaultVideoVariant?.id,
    initialResumePosition,
    item.id,
    playbackPreferenceOwnerKey,
    videoVariants,
  ]);

  useEffect(() => {
    if (!windowsNativePlayback) return;
    return subscribeWindowsPlayerProgress(
      item.id,
      (positionSeconds, durationSeconds) => {
        resumeTimeRef.current = positionSeconds;
        setCommerceTimeSeconds(positionSeconds);
        if (Date.now() - lastResumeSaveRef.current < 4000) return;
        lastResumeSaveRef.current = Date.now();
        saveResumePosition(item.id, positionSeconds, durationSeconds);
      },
    );
  }, [item.id, windowsNativePlayback]);

  useEffect(() => {
    let active = true;
    attemptedPlaybackUrlsRef.current.clear();
    setLoadingSource("");
    setPlaybackSource({ sourceUri: uri, url: uri });
    setEdgeStatus(
      edgeSelectionMode === "manual"
        ? "正在连接手动选择的播放节点…"
        : "正在按优先级检测播放节点…",
    );
    setEdgeDetails({
      label: mediaEdgeLabelForUrl(uri),
      latencyMs: 0,
      bandwidthMbps: 0,
      priority: 1,
      status: edgeSelectionMode === "manual" ? "手动锁定" : "正在检测",
      reason:
        edgeSelectionMode === "manual"
          ? "正在连接手动指定节点"
          : "按照手动配置的优先级依次检测节点",
    });
    const selectionRequest =
      edgeSelectionMode === "manual"
        ? Promise.resolve(
            mediaEdgeById(uri, manualEdgeId, edgePriorityIds) ||
              availableEdges[0],
          )
        : selectMediaEdge(uri, edgePriorityIds);
    selectionRequest
      .then((edge) => {
        if (!active || !edge) return;
        setPlaybackSource({ sourceUri: uri, url: edge.url });
        setEdgeDetails({
          label: edge.label,
          latencyMs: edge.latencyMs,
          bandwidthMbps: edge.bandwidthMbps || 0,
          priority: edge.priority || 1,
          status: edgeSelectionMode === "manual" ? "手动锁定" : "正常",
          reason:
            edgeSelectionMode === "manual"
              ? "用户手动指定此播放节点"
              : "按配置优先级选中的首个可用节点",
        });
        setEdgeStatus(
          edge.latencyMs > 0
            ? `播放节点：${edge.label} · ${edge.latencyMs}ms`
            : `播放节点：${edge.label}`,
        );
      })
      .catch(() => {
        if (active) {
          const label = mediaEdgeLabelForUrl(uri);
          setEdgeDetails({
            label,
            latencyMs: 0,
            bandwidthMbps: 0,
            priority: 1,
            status: "主线路",
            reason: "媒体下载测速不可用，已使用默认播放线路",
          });
          setEdgeStatus(`播放节点：${label}`);
        }
      });
    return () => {
      active = false;
    };
  }, [
    availableEdges,
    edgePriorityIds,
    edgeSelectionMode,
    manualEdgeId,
    uri,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    let active = true;
    if (!windowsNativePlayback) setPlayerLoading(true);
    smartBufferingRef.current = false;
    sourceSwitchingRef.current = true;
    const preservePlaybackState = () => {
      if (sourceSwitchingRef.current) return;
      if (Number.isFinite(video.currentTime)) {
        resumeTimeRef.current = Math.max(0, video.currentTime || 0);
      }
      resumePlayingRef.current =
        smartBufferingRef.current || (!video.paused && !video.ended);
    };

    const restorePlayback = () => {
      if (!active) return;
      if (resumeTimeRef.current > 0) {
        video.currentTime = resumeTimeRef.current;
      }
      const shouldResume = resumePlayingRef.current;
      sourceSwitchingRef.current = false;
      if (shouldResume) {
        video.defaultMuted = !soundUnlockedRef.current;
        video.muted = !soundUnlockedRef.current;
        video.volume = soundVolumeRef.current;
        beginSmartBufferingRef.current("正在建立安全水位");
      } else {
        setPlayerLoading(false);
      }
    };

    downgradePressureRef.current = { since: null, lastSampleAt: null };
    let evaluateMediaDowngrade = () => {};
    const downgradeTimer = window.setInterval(() => evaluateMediaDowngrade(), 1000);
    const disposeSource = attachVideoSource({
      video,
      url: playbackUri,
      createHls: () =>
        createHlsPlayer(
          {
            startLevel: -1,
            capLevelToPlayerSize: true,
            // Keep a meaningful portion of an already cached VOD in MediaSource after
            // reopening. The previous 45/90 second limits made disk-cache hits look
            // identical to a cold start because HLS stopped appending cached segments.
            maxBufferLength: 5 * 60,
            maxMaxBufferLength: 30 * 60,
            maxBufferSize: 512 * 1024 * 1024,
            maxBufferHole: 0.75,
          },
          `neptune-v1:${item.id}:${selectedVideo?.id || "desktop-auto"}`,
          p2pActive,
        ),
      onBandwidthEstimate: (bitsPerSecond) => {
        if (!Number.isFinite(bitsPerSecond) || bitsPerSecond <= 0) return;
        adaptiveBandwidthRef.current = adaptiveBandwidthRef.current
          ? adaptiveBandwidthRef.current * 0.7 + bitsPerSecond * 0.3
          : bitsPerSecond;
        setPlaybackHealth((current) => ({
          ...current,
          bandwidthBps: adaptiveBandwidthRef.current,
        }));
        setEdgeDetails((current) => ({
          ...current,
          label: mediaEdgeLabelForUrl(playbackUri),
          bandwidthMbps:
            Math.round((adaptiveBandwidthRef.current / 1_000_000) * 10) / 10,
          status: edgeSelectionMode === "manual" ? "手动锁定" : "正常",
          reason:
            edgeSelectionMode === "manual"
              ? "手动节点实际媒体分片下载速度"
              : "播放器实际媒体分片下载速度",
        }));
      },
      onMediaBitrateEstimate: (bitsPerSecond) => {
        if (!Number.isFinite(bitsPerSecond) || bitsPerSecond <= 0) return;
        const measured = measuredMediaBitrateRef.current;
        measured.bps = measured.bps
          ? measured.bps * 0.65 + bitsPerSecond * 0.35
          : bitsPerSecond;
        selectedVideoBitrateRef.current = measured.bps;
        setPlaybackHealth((current) => ({
          ...current,
          mediaBitrateBps: measured.bps,
        }));
        evaluateMediaDowngrade = () => {
        const current = videoVariants.find(
          (variant) => variant.id === selectedVideoId,
        );
        if (
          !evaluateDowngradePressure({
            level: downgradeLevelRef.current,
            now: Date.now(),
            pressure: downgradePressureRef.current,
            bandwidthBps: adaptiveBandwidthRef.current,
            bitrateBps: measured.bps,
            bufferSeconds: bufferedSecondsAhead(video),
            // Auto quality has its own single downgrade decision path below. Keep this
            // measured-bitrate fallback for manually locked quality only, otherwise both
            // paths can switch variants in the same pressure window and reload twice.
            suspended: videoSelectionMode === "auto" || !active || video.ended || video.seeking ||
              sourceSwitchingRef.current || (video.paused && !smartBufferingRef.current) ||
              document.hidden,
            sinceSwitchMs: Date.now() - adaptiveLastSwitchRef.current,
          }) ||
          !current ||
          mediaBitrateFallbackRef.current.has(current.id)
        ) {
          return;
        }

        const compactPattern = /高压缩|compact|crf\s*2[6-9]/i;
        const currentText = `${current.label} ${current.bitrate}`;
        const sameResolutionCompact = !compactPattern.test(currentText)
          ? videoVariants.find(
              (variant) =>
                variant.id !== current.id &&
                variant.kind === current.kind &&
                variant.quality === current.quality &&
                variant.resolution === current.resolution &&
                compactPattern.test(`${variant.label} ${variant.bitrate}`),
            )
          : undefined;
        const currentHeight = adaptiveVariantHeight(current);
        const lowerCandidates = videoVariants.filter(
          (variant) =>
            variant.id !== current.id &&
            adaptiveVariantHeight(variant) < currentHeight,
        );
        const nextHeight = lowerCandidates.length
          ? Math.max(...lowerCandidates.map(adaptiveVariantHeight))
          : 0;
        const nextTier = lowerCandidates.filter(
          (variant) => adaptiveVariantHeight(variant) === nextHeight,
        );
        const lowerQuality =
          nextTier.find((variant) =>
            /高质量|quality|crf\s*(?:1[6-9]|2[0-2])/i.test(
              `${variant.label} ${variant.bitrate}`,
            ),
          ) || nextTier[0];
        const fallback = sameResolutionCompact || lowerQuality;
        if (!fallback) return;

        mediaBitrateFallbackRef.current.add(current.id);
        adaptiveLastSwitchRef.current = Date.now();
        adaptiveStableSinceRef.current = Date.now();
        downgradePressureRef.current = { since: null, lastSampleAt: null };
        resumeTimeRef.current = Math.max(0, video.currentTime || 0);
        resumePlayingRef.current = true;
        const mediaMbps = (measured.bps / 1_000_000).toFixed(1);
        const networkMbps = (
          adaptiveBandwidthRef.current / 1_000_000
        ).toFixed(1);
        const message = `容错降级：当前版本约 ${mediaMbps} Mbps，实测网络 ${networkMbps} Mbps，临时切换到 ${desktopPlayerVariantLabel(fallback)}`;
        setFaultToleranceStatus(message);
        setAdaptiveStatus(message);
        setSelectedVideoId(fallback.id);
        };
      },
      onReady: restorePlayback,
      onHealthy: () => setVideoSourceError(""),
      onRetry: (detail) => {
        setVideoSourceError(detail);
        setPlaybackHealth((current) => ({
          ...current,
          retries: current.retries + 1,
        }));
      },
      onFatal: (detail) => {
        if (!active) return;
        setPlaybackHealth((current) => ({
          ...current,
          failures: current.failures + 1,
        }));
        if (detail === "浏览器不支持 HLS 播放") {
          setVideoSourceError(detail);
          return;
        }
        reportMediaEdgeFailure(playbackUri);

        // Some Chromium builds can preview the AV1 CMAF/HLS stream but never
        // reach readyState for the equivalent direct MP4 (especially with
        // 10-bit AV1). Keep AV1 and retry the exact same rendition through
        // its HLS companion before changing quality or node.
        const currentVariant = videoVariants.find(
          (variant) => variant.id === selectedVideoId,
        );
        const currentLabel = currentVariant
          ? `${currentVariant.quality} ${currentVariant.resolution} ${currentVariant.label}`.toLowerCase()
          : "";
        const av1HlsFallback =
          currentVariant &&
          !/\.m3u8(?:$|[?#])/i.test(currentVariant.url) &&
          /av1|av01/i.test(
            `${currentVariant.codec || ""} ${currentLabel}`,
          )
            ? videoVariants.find((variant) => {
                if (!/\.m3u8(?:$|[?#])/i.test(variant.url)) return false;
                if (variant.kind !== currentVariant.kind) return false;
                if (variant.quality !== currentVariant.quality) return false;
                if (variant.resolution !== currentVariant.resolution) return false;
                return /av1|av01/i.test(
                  `${variant.codec || ""} ${variant.label}`,
                );
              })
            : undefined;
        if (av1HlsFallback && selectedVideoId !== av1HlsFallback.id) {
          setVideoSourceError("AV1 直链加载失败，正在切换 AV1 HLS 播放…");
          setSelectedVideoId(av1HlsFallback.id);
          return;
        }
        attemptedPlaybackUrlsRef.current.add(playbackUri);
        if (edgeSelectionMode === "auto") {
          const fallbackEdge = nextMediaEdge(
            uri,
            attemptedPlaybackUrlsRef.current,
            edgePriorityIds,
          );
          if (fallbackEdge) {
            setPlaybackHealth((current) => ({
              ...current,
              nodeSwitches: current.nodeSwitches + 1,
            }));
            setVideoSourceError(
              `当前节点加载失败，正在切换${fallbackEdge.label}…`,
            );
            setEdgeDetails({
              label: fallbackEdge.label,
              latencyMs: 0,
              bandwidthMbps: 0,
              priority: fallbackEdge.priority || 2,
              status: "正在切换",
              reason: "当前节点加载失败，切换到下一优先级",
            });
            setPlaybackSource({ sourceUri: uri, url: fallbackEdge.url });
            return;
          }
        } else {
          setVideoSourceError(`手动节点加载失败（${detail}），请选择其他节点`);
          return;
        }
        if (videoSelectionMode === "auto") {
          const decision = decideAdaptiveVariant({
            variants: videoVariants,
            currentId: selectedVideoId,
            bandwidthBps: adaptiveBandwidthRef.current,
            bufferAheadSeconds: 0,
            recentStalls: 3,
            stableForMs: 0,
            sinceSwitchMs: Date.now() - adaptiveLastSwitchRef.current,
            viewportHeight,
            fatal: true,
          });
          if (decision.variant && decision.variant.id !== selectedVideoId) {
            adaptiveLastSwitchRef.current = Date.now();
            adaptiveStableSinceRef.current = Date.now();
            attemptedPlaybackUrlsRef.current.clear();
            setAdaptiveStatus(decision.reason);
            setSelectedVideoId(decision.variant.id);
            return;
          }
        }
        if (vpsFallbackVariant && selectedVideoId !== vpsFallbackVariant.id) {
          setVideoSourceError("当前版本加载失败，正在尝试备用清晰度…");
          setSelectedVideoId(vpsFallbackVariant.id);
        } else {
          setVideoSourceError(
            `视频加载失败，请切换清晰度或重新打开（${detail}）`,
          );
        }
      },
    });
    mediaSourceRecoveryRef.current = disposeSource.recover;
    return () => {
      active = false;
      window.clearInterval(downgradeTimer);
      preservePlaybackState();
      smartBufferingRef.current = false;
      if (mediaSourceRecoveryRef.current === disposeSource.recover) {
        mediaSourceRecoveryRef.current = null;
      }
      sourceSwitchingRef.current = true;
      disposeSource();
    };
  }, [
    p2pActive,
    playbackUri,
    edgePriorityIds,
    edgeSelectionMode,
    selectedVideoId,
    uri,
    videoSelectionMode,
    videoVariants,
    viewportHeight,
    vpsFallbackVariant?.id,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || windowsNativePlayback || edgeSelectionMode !== "auto") {
      return;
    }
    let active = true;
    let nodeSelectionRunning = false;
    let sustainedStallTimer = 0;
    adaptivePressureRef.current = { since: null, lastSampleAt: null };

    const switchNodeAfterPressure = async () => {
      if (
        nodeSelectionRunning ||
        Date.now() - adaptiveNodeSwitchRef.current < 20_000
      ) {
        return;
      }
      nodeSelectionRunning = true;
      adaptiveNodeSwitchRef.current = Date.now();
      reportMediaEdgeFailure(playbackUri);
      setEdgeStatus("检测到链路卡顿，正在重新选择播放节点…");
      setEdgeDetails((current) => ({
        ...current,
        status: "波动，正在切换",
        reason: "检测到连续卡顿，正在重新测速",
      }));
      try {
        const edge = await selectMediaEdge(uri, edgePriorityIds);
        if (!active) return;
        setEdgeDetails({
          label: edge.label,
          latencyMs: edge.latencyMs,
          bandwidthMbps: edge.bandwidthMbps || 0,
          priority: edge.priority || 1,
          status: "正常",
          reason: "检测到卡顿后按媒体下载速度动态选择",
        });
        setEdgeStatus(
          "播放节点：" +
            edge.label +
            (edge.latencyMs > 0 ? " · " + edge.latencyMs + "ms" : "") +
            " · 动态选择",
        );
        if (edge.url !== playbackUri) {
          setPlaybackHealth((current) => ({
            ...current,
            nodeSwitches: current.nodeSwitches + 1,
          }));
          attemptedPlaybackUrlsRef.current.clear();
          setPlaybackSource({ sourceUri: uri, url: edge.url });
        }
      } catch {
        if (active) {
          const label = mediaEdgeLabelForUrl(uri);
          setEdgeDetails({
            label,
            latencyMs: 0,
            bandwidthMbps: 0,
            priority: 1,
            status: "主线路",
            reason: "动态测速失败，已回到默认播放线路",
          });
          setEdgeStatus("播放节点：" + label + " · 已重新检测");
        }
      } finally {
        nodeSelectionRunning = false;
      }
    };

    const evaluate = (fromStall = false) => {
      if (
        !active ||
        sourceSwitchingRef.current ||
        (!fromStall && video.paused) ||
        video.ended
      ) {
        return;
      }
      const now = Date.now();
      adaptiveStallsRef.current = adaptiveStallsRef.current.filter(
        (time) => now - time <= 60_000,
      );
      if (fromStall) {
        const previousStall =
          adaptiveStallsRef.current[adaptiveStallsRef.current.length - 1] || 0;
        if (now - previousStall >= 6_000) {
          adaptiveStallsRef.current.push(now);
          adaptiveStableSinceRef.current = now;
        }
      }
      const shouldSwitchNode =
        fromStall && adaptiveStallsRef.current.length >= 2;
      const bufferAhead = bufferedSecondsAhead(video);
      let qualitySwitched = false;
      if (videoSelectionMode === "auto") {
        const decision = decideAdaptiveVariant({
          variants: videoVariants,
          currentId: selectedVideoId,
          bandwidthBps: adaptiveBandwidthRef.current,
          bufferAheadSeconds: bufferAhead,
          allowDowngrade: evaluateDowngradePressure({
            level: downgradeLevelRef.current,
            now,
            pressure: adaptivePressureRef.current,
            bandwidthBps: adaptiveBandwidthRef.current,
            bitrateBps: selectedVideoBitrateRef.current,
            bufferSeconds: bufferAhead,
            suspended: video.seeking || document.hidden || (video.paused && !smartBufferingRef.current),
            sinceSwitchMs: now - adaptiveLastSwitchRef.current,
          }),
          recentStalls: adaptiveStallsRef.current.length,
          stableForMs: now - adaptiveStableSinceRef.current,
          sinceSwitchMs: now - adaptiveLastSwitchRef.current,
          viewportHeight,
        });
        if (decision.variant && decision.variant.id !== selectedVideoId) {
          qualitySwitched = true;
          resumeTimeRef.current = Math.max(0, video.currentTime || 0);
          resumePlayingRef.current = true;
          adaptiveLastSwitchRef.current = now;
          adaptiveStableSinceRef.current = now;
          adaptiveStallsRef.current = [];
          attemptedPlaybackUrlsRef.current.clear();
          setAdaptiveStatus(
            decision.reason +
              " · 当前 " +
              adaptiveVariantHeight(decision.variant) +
              "p",
          );
          setSelectedVideoId(decision.variant.id);
        } else {
          const current = selectedVideo || videoVariants[0];
          const currentHeight = current ? adaptiveVariantHeight(current) : 0;
          const mbps = (adaptiveBandwidthRef.current / 1_000_000).toFixed(1);
          setAdaptiveStatus(
            "自动画质：" +
              (currentHeight ? currentHeight + "p" : "检测中") +
              " · 缓冲 " +
              bufferAhead.toFixed(1) +
              "秒 · " +
              mbps +
              "Mbps",
          );
        }
      } else {
        setAdaptiveStatus(
          `手动画质已锁定 · 缓冲 ${bufferAhead.toFixed(1)}秒 · 节点自动切换有效`,
        );
      }
      if (shouldSwitchNode && !qualitySwitched) {
        void switchNodeAfterPressure();
      }
    };

    let lastDiagnosticStallAt = 0;
    const onWaiting = () => {
      const now = Date.now();
      if (now - lastDiagnosticStallAt >= 1_500) {
        lastDiagnosticStallAt = now;
        setPlaybackHealth((current) => ({
          ...current,
          stalls: current.stalls + 1,
        }));
      }
      evaluate(true);
      window.clearTimeout(sustainedStallTimer);
      sustainedStallTimer = window.setTimeout(() => {
        if (
          active &&
          !sourceSwitchingRef.current &&
          !video.paused &&
          !video.ended &&
          video.readyState < video.HAVE_FUTURE_DATA &&
          bufferedSecondsAhead(video) < 1.5
        ) {
          void switchNodeAfterPressure();
        }
      }, 3500);
    };
    const onPlaying = () => window.clearTimeout(sustainedStallTimer);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("stalled", onWaiting);
    video.addEventListener("playing", onPlaying);
    const timer = window.setInterval(() => evaluate(false), 2_500);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.clearTimeout(sustainedStallTimer);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("stalled", onWaiting);
      video.removeEventListener("playing", onPlaying);
    };
  }, [
    edgePriorityIds,
    edgeSelectionMode,
    playbackUri,
    selectedVideoId,
    uri,
    videoSelectionMode,
    videoVariants,
    viewportHeight,
    windowsNativePlayback,
  ]);

  useEffect(() => {
    const audio = audioRef.current;
    const video = videoRef.current;
    if (!audio || !video) {
      return;
    }
    if (!selectedAudio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      video.defaultMuted = !soundUnlocked;
      video.muted = !soundUnlocked;
      video.volume = soundVolumeRef.current;
      return;
    }
    audio.src = selectedAudio.url;
    audio.currentTime = video.currentTime || 0;
    audio.volume = soundVolumeRef.current;
    audio.muted = !soundUnlocked;
    video.muted = true;
    const startSelectedAudio = () => {
      if (video.paused) {
        return;
      }
      audio.currentTime = video.currentTime || 0;
      audio.play().catch(() => {
        setSourceError("所选音质不可用，已恢复视频自带声音");
        setSelectedAudioId("");
      });
    };
    audio.addEventListener("canplay", startSelectedAudio, { once: true });
    startSelectedAudio();
    return () => {
      audio.removeEventListener("canplay", startSelectedAudio);
    };
  }, [selectedAudio?.id, selectedAudio?.url, soundUnlocked]);

  const savePlaybackPosition = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    resumeTimeRef.current = video.currentTime || 0;
    resumePlayingRef.current = !video.paused;
  };

  const selectVideoVariant = (nextId: string) => {
    savePlaybackPosition();
    setSourceError("");
    setVideoSourceError("");
    setFaultToleranceStatus("");
    mediaBitrateFallbackRef.current.clear();
    if (nextId === AUTO_VIDEO_VARIANT_ID) {
      const network = browserPlaybackNetwork();
      const automatic = initialAdaptiveVariant(
        videoVariants,
        network.bandwidthBps,
        viewportHeight,
        network.saveData,
      );
      rememberVideoVariantPreference(playbackPreferenceOwnerKey, null);
      adaptiveBandwidthRef.current = network.bandwidthBps;
      adaptiveStableSinceRef.current = Date.now();
      adaptiveLastSwitchRef.current = Date.now();
      adaptiveStallsRef.current = [];
      setVideoSelectionMode("auto");
      setAdaptiveStatus("自动画质：正在监测带宽、缓冲和卡顿");
      if (automatic) {
        setSelectedVideoId(automatic.id);
        if (windowsNativePlayback) {
          notifyWindowsPlayerSourceSelection(item, automatic.id);
        }
      }
      return;
    }
    const nextVariant = videoVariants.find((variant) => variant.id === nextId);
    if (nextVariant) {
      rememberVideoVariantPreference(playbackPreferenceOwnerKey, nextVariant);
    }
    setVideoSelectionMode("variant");
    setSelectedVideoId(nextId);
    if (windowsNativePlayback) {
      notifyWindowsPlayerSourceSelection(item, nextId);
    }
  };

  const selectAudioVariant = (nextId: string) => {
    setSourceError("");
    const nextVariant =
      audioVariants.find((variant) => variant.id === nextId) || null;
    rememberAudioVariantPreference(playbackPreferenceOwnerKey, nextVariant);
    setSelectedAudioId(nextId);
  };

  const quickQualitySelect = createElement(
    "select",
    {
      value:
        videoSelectionMode === "auto"
          ? AUTO_VIDEO_VARIANT_ID
          : selectedVideoId,
      onChange: (event: { target: { value: string } }) =>
        selectVideoVariant(event.target.value),
      title: "直接选择播放画质",
      "aria-label": "直接选择播放画质",
      style: styles.playerQualityQuickSelect as never,
    },
    [
      createElement(
        "option",
        { key: AUTO_VIDEO_VARIANT_ID, value: AUTO_VIDEO_VARIANT_ID },
        "● 画质：自动（推荐）",
      ),
      ...videoVariants.map((variant) =>
        createElement(
          "option",
          { key: variant.id, value: variant.id },
          `● 画质：${desktopPlayerVariantLabel(variant)}`,
        ),
      ),
    ],
  );

  const downgradeHintText = windowsNativePlayback
    ? "容错降速 · 原生播放"
    : faultToleranceStatus || `容错降速 ${downgradeLevel}/10`;

  const cacheInlineSlider = createElement(
    "label",
    {
      key: "cache-inline",
      style: styles.playerPolicyInlineControl as never,
      title: `${cachePolicyStatus ? cachePolicyStatus + " · " : ""}缓存策略 ${cachePrefetchLevel}/10：提前缓存后续 ${cachePrefetchLevel * 5} 个视频；0 为仅缓存当前播放，越高预缓存越积极`,
    },
    createElement(
      "span",
      { style: styles.playerPolicyInlineLabel as never },
      createElement(Feather, { name: "database", size: 14, color: "#28c99a" }),
      createElement("span", null, `缓存策略 ${cachePrefetchLevel}/10`),
    ),
    createElement("input", {
      type: "range",
      min: 0,
      max: 10,
      step: 1,
      value: cachePrefetchLevel,
      "aria-label": "缓存策略，设置提前缓存后续视频数量",
      "aria-valuetext": `${cachePrefetchLevel} 档，共 10 档；提前缓存后续 ${cachePrefetchLevel * 5} 个视频`,
      onChange: (event: { target: { value: string } }) =>
        changeCachePrefetchLevel(Number(event.target.value)),
      style: styles.playerPolicyInlineRange as never,
    }),
  );

  const downgradeInlineSlider = createElement(
    "label",
    {
      key: "downgrade-inline",
      style: styles.playerPolicyInlineControl as never,
      title: windowsNativePlayback
        ? "容错降速阈值：Windows 原生播放由系统管理，此设置仅用于网页播放器"
        : `${downgradeHintText}：缓存低于 ${downgradePolicy.bufferSeconds} 秒且带宽持续不足 ${downgradePolicy.pressureMs / 1000} 秒才允许降级；越高越保画质`,
    },
    createElement(
      "span",
      { style: styles.playerPolicyInlineLabel as never },
      createElement(Feather, { name: "shield", size: 14, color: "#28c99a" }),
      createElement("span", null, `容错降速 ${downgradeLevel}/10`),
    ),
    createElement("input", {
      type: "range",
      min: 1,
      max: 10,
      step: 1,
      value: downgradeLevel,
      disabled: windowsNativePlayback,
      "aria-label": "容错降速阈值，越高越保画质",
      "aria-valuetext": `${downgradeLevel} 档，共 10 档；越高越保画质`,
      onChange: (event: { target: { value: string } }) =>
        changeDowngradeLevel(Number(event.target.value)),
      style: styles.playerPolicyInlineRange as never,
    }),
  );

  const headerMediaControlsRow = (
    <View style={styles.playerHeaderMediaControls}>
      {cacheInlineSlider}
      {downgradeInlineSlider}
      {quickQualitySelect}
    </View>
  );

  const playbackDiagnosis = diagnosePlayback(playbackHealth);
  const cacheDiagnosticPanel = showCacheDiagnostics
    ? createElement(
        "details",
        {
          open: true,
          style: styles.playbackCacheDiagnostics as never,
          "data-admin-playback-cache": "true",
        },
        createElement(
          "summary",
          { style: styles.playbackCacheDiagnosticsSummary as never },
          createElement("span", null, "播放诊断 · 智能缓存 · ADMIN"),
          createElement(
            "span",
            { style: styles.playbackCacheDiagnosticsTotals as never },
            `卡顿 ${playbackHealth.stalls} · 失败 ${playbackHealth.failures} · 节点切换 ${playbackHealth.nodeSwitches} · 命中 ${Math.round(playbackDiagnosis.cacheHitRate * 100)}%`,
          ),
        ),
        createElement(
          "div",
          { style: styles.playbackIntelligenceOverview as never },
          createElement(
            "div",
            {
              style: Object.assign({}, styles.playbackDiagnosisCard, {
                borderColor: playbackDiagnosis.severity === "critical"
                  ? "#a83445"
                  : playbackDiagnosis.severity === "warning"
                    ? "#8a6d2f"
                    : "#23775d",
              }) as never,
            },
            createElement("strong", { style: styles.playbackDiagnosisTitle as never }, playbackDiagnosis.title),
            createElement("span", null, playbackDiagnosis.cause),
            createElement("span", { style: styles.playbackDiagnosisAction as never }, playbackDiagnosis.action),
            createElement(
              "span",
              { style: styles.playbackDiagnosisMetrics as never },
              `节点速度 ${(playbackHealth.bandwidthBps / 1_000_000).toFixed(1)} Mbps · 媒体码率 ${playbackHealth.mediaBitrateBps > 0 ? (playbackHealth.mediaBitrateBps / 1_000_000).toFixed(1) : "检测中"} Mbps · 重试 ${playbackHealth.retries}`,
            ),
          ),
          createElement(
            "div",
            { style: styles.intelligentCachePlan as never },
            createElement("strong", { style: styles.playbackDiagnosisTitle as never }, "推荐系统 → 智能缓存目标"),
            intelligentCachePlan.length
              ? createElement(
                  "ol",
                  { style: styles.intelligentCachePlanList as never },
                  ...intelligentCachePlan.slice(0, 5).map((target) =>
                    createElement(
                      "li",
                      { key: target.video.id, style: styles.intelligentCachePlanItem as never },
                      createElement("span", { title: target.video.title }, target.video.title),
                      createElement(
                        "small",
                        null,
                        `${Math.round(target.probability * 100)}% · ${target.quality} · 前 ${target.segmentCount} 分片 · ${target.reasons.slice(0, 2).join("、")}`,
                      ),
                    ),
                  ),
                )
              : createElement("span", { style: styles.playbackDiagnosisAction as never }, "正在等待推荐候选和缓存策略…"),
          ),
        ),
        cacheDiagnostics.length
          ? createElement(
              "div",
              { style: styles.playbackCacheDiagnosticsScroll as never },
              createElement(
                "table",
                { style: styles.playbackCacheDiagnosticsTable as never },
                createElement(
                  "thead",
                  null,
                  createElement(
                    "tr",
                    null,
                    ...["加载资源 / 分片", "网页加载", "本地缓存", "命中", "最近来源"].map((label) =>
                      createElement("th", { key: label, style: styles.playbackCacheDiagnosticsHead as never }, label),
                    ),
                  ),
                ),
                createElement(
                  "tbody",
                  null,
                  ...cacheDiagnostics.map((row) =>
                    createElement(
                      "tr",
                      { key: row.url, title: row.url },
                      createElement(
                        "td",
                        { style: styles.playbackCacheDiagnosticsResource as never },
                        createElement("span", { style: styles.playbackCacheDiagnosticsType as never }, row.resource === "segment" ? "分片" : row.resource === "playlist" ? "清单" : "视频"),
                        createElement("span", { style: styles.playbackCacheDiagnosticsName as never }, row.name),
                      ),
                      createElement("td", { style: styles.playbackCacheDiagnosticsCell as never }, `${row.requests} 次`),
                      createElement("td", { style: styles.playbackCacheDiagnosticsCell as never }, row.cached ? "✓ 已缓存" : row.storing ? "写入中" : "—"),
                      createElement("td", { style: styles.playbackCacheDiagnosticsCell as never }, row.hits ? `✓ ${row.hits} 次` : "—"),
                      createElement("td", { style: styles.playbackCacheDiagnosticsCell as never }, row.source === "cache" ? "本机缓存" : "网络"),
                    ),
                  ),
                ),
              ),
            )
          : createElement("div", { style: styles.playbackCacheDiagnosticsEmpty as never }, "等待网页请求播放清单或媒体分片…"),
      )
    : null;

  return (
    <View
      style={[
        styles.playerStage,
        maximizeVideoArea && styles.playerStageMaximized,
      ]}
    >
      {startVideoFullscreen && (
        <View
          style={{
            position: "fixed" as never,
            top: 12,
            right: 16,
            zIndex: 1102,
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
          }}
        >
          {videoFullscreen && windowControls}
          {createElement(
            "button",
            {
              onClick: () => setVideoFullscreen((value) => !value),
              style: {
                background: "rgba(0,0,0,.65)",
                color: "white",
                border: "1px solid #34745f",
                borderRadius: 6,
                padding: "8px 12px",
                cursor: "pointer",
              },
            },
            videoFullscreen ? "退出视频全屏 / 播放设置" : "视频全屏",
          )}
        </View>
      )}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={[
          styles.playerVariantBarScroll,
          !item.productEditorAllowed &&
            !startVideoFullscreen &&
            styles.playerVariantBarScrollEmpty,
          videoFullscreen && { display: "none" },
        ]}
        contentContainerStyle={[
          styles.playerVariantBar,
          !item.productEditorAllowed &&
            !startVideoFullscreen &&
            styles.playerVariantBarEmpty,
        ]}
      >
        {startVideoFullscreen
          ? headerMediaControlsRow
          : headerControlsHost
            ? createPortal(headerMediaControlsRow, headerControlsHost)
            : null}
        {shareControlsHost
          ? createPortal(
              <VideoShareButton
                video={{
                  id: item.id,
                  title: item.title,
                  thumbnail: item.thumbnail,
                }}
              />,
              shareControlsHost,
            )
          : null}
        {!shareControlsHost && startVideoFullscreen ? (
          <VideoShareButton
            video={{
              id: item.id,
              title: item.title,
              thumbnail: item.thumbnail,
            }}
          />
        ) : null}
        <Modal
          visible={mediaControlPanel !== null}
          transparent
          animationType="fade"
          onRequestClose={() => setMediaControlPanel(null)}
        >
          <Pressable
            style={styles.playerEdgeModalBackdrop}
            onPress={() => setMediaControlPanel(null)}
          >
            <Pressable
              style={styles.playerEdgeModalCard}
              onPress={(event) => event.stopPropagation()}
            >
              <View style={styles.playerEdgeModalHeader}>
                <Text style={styles.playerEdgeModalTitle}>播放设置</Text>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="关闭画质和音质设置"
                  onPress={() => setMediaControlPanel(null)}
                >
                  <Feather name="x" size={20} color="#bdeee0" />
                </Pressable>
              </View>
              <View style={styles.playerMediaRecommendation}>
                <Feather name="zap" size={16} color="#28c99a" />
                <Text style={styles.playerMediaRecommendationText}>
                  建议使用自动画质、音质跟随视频；播放器会根据实时网络调整，并减少额外音轨请求。
                </Text>
              </View>
              <Text style={styles.playerEdgeDetailLabel}>画质</Text>
              {createElement(
                    "select",
                    {
                      value:
                        videoSelectionMode === "auto"
                          ? AUTO_VIDEO_VARIANT_ID
                          : selectedVideoId,
                      onChange: (event: { target: { value: string } }) =>
                        selectVideoVariant(event.target.value),
                      style: styles.playerMediaFullSelect as never,
                    },
                    [
                      createElement(
                        "option",
                        {
                          key: AUTO_VIDEO_VARIANT_ID,
                          value: AUTO_VIDEO_VARIANT_ID,
                        },
                        "自动（推荐）· 实时监测画质与节点",
                      ),
                      ...videoVariants.map((variant) =>
                        createElement(
                          "option",
                          { key: variant.id, value: variant.id },
                          desktopPlayerVariantLabel(variant),
                        ),
                      ),
                    ],
                  )}
              <Text style={styles.playerEdgeDetailValue}>
                {videoSelectionMode === "auto"
                  ? "推荐：实时监测带宽、缓冲和卡顿，并限制频繁切换。"
                  : "已锁定画质；网络波动时仍会自动切换播放节点。"}
              </Text>
              <Text style={styles.playerEdgeDetailLabel}>
                容错降级阈值 · {downgradeLevel}/10 档
              </Text>
              <Text style={styles.playerEdgeDetailValue}>
                阈值滑块已直接放在播放器工具栏（画质与音量之间），可随播放实时调整；1 优先流畅，10 优先保画质（默认 7）。
              </Text>
              <Text style={styles.playerEdgeDetailValue}>
                {windowsNativePlayback
                  ? "Windows 原生播放由系统管理，此阈值仅用于网页播放器。"
                  : `缓存低于 ${downgradePolicy.bufferSeconds} 秒且带宽持续不足 ${downgradePolicy.pressureMs / 1000} 秒才允许降级；缓存充足不因测速偏低切换。自动画质时由自动策略统一降档，手动画质时仅作临时容错，不会两套逻辑重复切换。设置自动保存在本浏览器，播放错误仍会兜底。`}
              </Text>
              <Text style={styles.playerEdgeDetailLabel}>音质</Text>
              {createElement(
                    "select",
                    {
                      value: selectedAudioId,
                      disabled: windowsNativePlayback,
                      onChange: (event: { target: { value: string } }) =>
                        selectAudioVariant(event.target.value),
                      style: styles.playerMediaFullSelect as never,
                    },
                    [
                      createElement(
                        "option",
                        { key: "auto", value: "" },
                        "自动（推荐）· 跟随视频音轨",
                      ),
                      ...audioVariants.map((variant) =>
                        createElement(
                          "option",
                          { key: variant.id, value: variant.id },
                          mediaVariantLabel(variant),
                        ),
                      ),
                    ],
                  )}
              <Text style={styles.playerEdgeDetailValue}>
                {selectedAudio
                  ? "独立音轨会额外请求音频文件，适合需要固定音质的内容。"
                  : "推荐：跟随视频音轨，兼容性最好并减少连接数。"}
              </Text>
              {windowsNativePlayback ? (
                <Text style={styles.playerSourceError}>
                  Windows 原生播放器模式由系统统一管理音轨。
                </Text>
              ) : null}
              <Text style={styles.playerEdgeDetailLabel}>默认打开方式</Text>
              {createElement(
                "fieldset",
                {
                  style: styles.playerOpenModeFieldset as never,
                  "aria-label": "播放窗口默认打开方式",
                },
                ...(
                  [
                    ["floating", "当前浮动窗口"],
                    ["window", "新标签页 · 视频全屏"],
                  ] as const
                ).map(([mode, label]) =>
                  createElement(
                    "label",
                    { key: mode, style: styles.playerOpenModeOption as never },
                    createElement("input", {
                      type: "radio",
                      name: "player-open-mode-settings",
                      value: mode,
                      checked: playerOpenMode === mode,
                      onChange: () => onPlayerOpenModeChange(mode),
                      style: { accentColor: "#28c99a" },
                    }),
                    label,
                  ),
                ),
              )}
              <Text style={styles.playerEdgeDetailValue}>
                新标签页模式会直接打开当前视频并进入视频全屏布局。
              </Text>
            </Pressable>
          </Pressable>
        </Modal>
        {(() => {
          const controls = (
            <View style={styles.interactionPlayerControls}>
        {videoSourceError || sourceError ? (
          <Text style={styles.playerSourceError} numberOfLines={2}>
            {videoSourceError || sourceError}
          </Text>
        ) : (
          <>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`播放节点：${edgeDetails.label}，点击查看详情`}
              style={styles.playerEdgeButton}
              onPress={() => setEdgeDetailsOpen(true)}
            >
              <View
                style={[
                  styles.playerEdgeStatusDot,
                  edgeDetails.status === "正常"
                    || edgeDetails.status === "手动锁定"
                    ? styles.playerEdgeStatusHealthy
                    : styles.playerEdgeStatusChecking,
                ]}
              />
              <Text style={styles.playerEdgeButtonText} numberOfLines={1}>
                播放节点：{edgeSelectionMode === "manual" ? "手动" : "自动"} · {compactPlaybackEdgeLabel(edgeDetails.label)}
                {edgeDetails.bandwidthMbps > 0
                  ? ` · ${edgeDetails.bandwidthMbps.toFixed(1)}Mbps`
                  : ""}
              </Text>
              <Feather name="chevron-down" size={13} color="#8fc7b6" />
            </Pressable>
            <Modal
              visible={edgeDetailsOpen}
              transparent
              animationType="fade"
              onRequestClose={() => setEdgeDetailsOpen(false)}
            >
              <Pressable
                style={styles.playerEdgeModalBackdrop}
                onPress={() => setEdgeDetailsOpen(false)}
              >
                <Pressable
                  style={styles.playerEdgeModalCard}
                  onPress={(event) => event.stopPropagation()}
                >
                  <View style={styles.playerEdgeModalHeader}>
                    <View style={styles.playerEdgeModalTitleRow}>
                      <View
                        style={[
                          styles.playerEdgeStatusDot,
                          edgeDetails.status === "正常"
                            || edgeDetails.status === "手动锁定"
                            ? styles.playerEdgeStatusHealthy
                            : styles.playerEdgeStatusChecking,
                        ]}
                      />
                      <Text style={styles.playerEdgeModalTitle}>
                        播放节点详情
                      </Text>
                    </View>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="关闭播放节点详情"
                      onPress={() => setEdgeDetailsOpen(false)}
                    >
                      <Feather name="x" size={20} color="#bdeee0" />
                    </Pressable>
                  </View>
                  <View style={styles.playerEdgeModeRow}>
                    <Pressable
                      style={[
                        styles.playerEdgeModeButton,
                        edgeSelectionMode === "auto" &&
                          styles.playerEdgeModeButtonActive,
                      ]}
                      onPress={() => chooseEdgeMode("auto")}
                    >
                      <Text style={styles.playerEdgeModeButtonText}>
                        自动模式
                      </Text>
                    </Pressable>
                    <Pressable
                      style={[
                        styles.playerEdgeModeButton,
                        edgeSelectionMode === "manual" &&
                          styles.playerEdgeModeButtonActive,
                      ]}
                      onPress={() => chooseEdgeMode("manual")}
                    >
                      <Text style={styles.playerEdgeModeButtonText}>
                        手动选择
                      </Text>
                    </Pressable>
                  </View>
                  <Text style={styles.playerEdgeDetailLabel}>
                    {edgeSelectionMode === "auto"
                      ? "自动模式优先级（可手动排序）"
                      : "手动锁定节点"}
                  </Text>
                  <ScrollView
                    style={styles.playerEdgeOptionScroller}
                    contentContainerStyle={styles.playerEdgeOptionList}
                  >
                    {availableEdges.map((edge, index) => {
                      const selected =
                        edgeSelectionMode === "manual" &&
                        (manualEdgeId || availableEdges[0]?.id) === edge.id;
                      return (
                        <Pressable
                          key={edge.id}
                          style={[
                            styles.playerEdgeOptionRow,
                            selected && styles.playerEdgeOptionRowActive,
                          ]}
                          onPress={() =>
                            edgeSelectionMode === "manual"
                              ? chooseManualEdge(edge.id)
                              : undefined
                          }
                        >
                          <Text style={styles.playerEdgeOptionOrder}>
                            {edgeSelectionMode === "auto"
                              ? `${index + 1}`
                              : selected
                                ? "●"
                                : "○"}
                          </Text>
                          <View style={styles.playerEdgeOptionCopy}>
                            <Text style={styles.playerEdgeOptionName}>
                              {edge.label}
                            </Text>
                            <Text style={styles.playerEdgeOptionHost}>
                              {playbackEdgeHost(edge.url)}
                            </Text>
                          </View>
                          {edgeSelectionMode === "auto" ? (
                            <View style={styles.playerEdgeOrderButtons}>
                              <Pressable
                                disabled={index === 0}
                                style={styles.playerEdgeOrderButton}
                                onPress={() => moveAutomaticEdge(edge.id, -1)}
                              >
                                <Feather
                                  name="arrow-up"
                                  size={15}
                                  color={index === 0 ? "#36574e" : "#bdeee0"}
                                />
                              </Pressable>
                              <Pressable
                                disabled={index === availableEdges.length - 1}
                                style={styles.playerEdgeOrderButton}
                                onPress={() => moveAutomaticEdge(edge.id, 1)}
                              >
                                <Feather
                                  name="arrow-down"
                                  size={15}
                                  color={
                                    index === availableEdges.length - 1
                                      ? "#36574e"
                                      : "#bdeee0"
                                  }
                                />
                              </Pressable>
                            </View>
                          ) : null}
                        </Pressable>
                      );
                    })}
                  </ScrollView>
                  <Text style={styles.playerEdgeDetailLabel}>节点名称</Text>
                  <Text style={styles.playerEdgeDetailValue}>
                    {edgeDetails.label}
                  </Text>
                  <Text style={styles.playerEdgeDetailLabel}>默认优先级</Text>
                  <Text style={styles.playerEdgeDetailValue}>
                    第 {edgeDetails.priority} 优先
                  </Text>
                  <Text style={styles.playerEdgeDetailLabel}>线路地址</Text>
                  <Text style={styles.playerEdgeDetailValue}>
                    {playbackEdgeHost(playbackUri)}
                  </Text>
                  <Text style={styles.playerEdgeDetailLabel}>节点状态</Text>
                  <Text style={styles.playerEdgeDetailValue}>
                    {edgeDetails.status}
                  </Text>
                  <Text style={styles.playerEdgeDetailLabel}>测速延迟</Text>
                  <Text style={styles.playerEdgeDetailValue}>
                    {edgeDetails.latencyMs > 0
                      ? `${edgeDetails.latencyMs} ms`
                      : "暂无测速结果"}
                  </Text>
                  <Text style={styles.playerEdgeDetailLabel}>实测下载速度</Text>
                  <Text style={styles.playerEdgeDetailValue}>
                    {edgeDetails.bandwidthMbps > 0
                      ? `${edgeDetails.bandwidthMbps.toFixed(1)} Mbps`
                      : "正在采集媒体下载数据"}
                  </Text>
                  <Text style={styles.playerEdgeDetailLabel}>选择原因</Text>
                  <Text style={styles.playerEdgeDetailValue}>
                    {edgeDetails.reason}
                  </Text>
                  <Text style={styles.playerEdgeDetailLabel}>画质策略</Text>
                  <Text style={styles.playerEdgeDetailValue}>
                    {videoSelectionMode === "auto"
                      ? adaptiveStatus
                      : edgeSelectionMode === "auto"
                        ? "手动画质已锁定；播放节点仍会按优先级故障切换"
                        : "手动画质和播放节点均已锁定"}
                  </Text>
                </Pressable>
              </Pressable>
            </Modal>
          </>
        )}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`播放顺序：${desktopLoopModeLabels[loopMode]}，点击切换`}
          style={styles.playerLoopModeButton}
          onPress={() => dispatch(cycleLoopMode())}
        >
          <Feather
            name={desktopLoopModeIcon(loopMode)}
            size={15}
            color="#28c99a"
          />
          <Text style={styles.playerLoopModeText} numberOfLines={1}>
            播放顺序：{desktopLoopModeLabels[loopMode]}
          </Text>
        </Pressable>
        <View style={styles.playerOpenModeInline}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="使用播放器窗口播放"
            style={[
              styles.playerOpenModeInlineButton,
              playerOpenMode === "floating" &&
                styles.playerOpenModeInlineButtonActive,
            ]}
            onPress={() => onPlayerOpenModeChange("floating")}
          >
            <Feather name="monitor" size={14} color="#bff7e6" />
            <Text style={styles.playerOpenModeInlineText}>播放器窗口</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="使用新建窗口播放"
            style={[
              styles.playerOpenModeInlineButton,
              playerOpenMode === "window" &&
                styles.playerOpenModeInlineButtonActive,
            ]}
            onPress={() => onPlayerOpenModeChange("window")}
          >
            <Feather name="external-link" size={14} color="#bff7e6" />
            <Text style={styles.playerOpenModeInlineText}>新建窗口</Text>
          </Pressable>
        </View>
        <View style={styles.playerVolumeControl}>
          <Feather
            name={soundUnlocked && soundVolume > 0 ? "volume-2" : "volume-x"}
            size={16}
            color="#28c99a"
          />
          {createElement("input", {
            type: "range",
            min: 0,
            max: 100,
            step: 1,
            value: Math.round(soundVolume * 100),
            "aria-label": "播放器音量",
            title: `音量 ${Math.round(soundVolume * 100)}%`,
            onChange: (event: { target: { value: string } }) => {
              const volume = Math.max(
                0,
                Math.min(Number(event.target.value) / 100, 1),
              );
              const preference = rememberSoundPreference(volume > 0, volume);
              if (windowsNativePlayback) {
                notifyWindowsPlayerCommand("set-volume", volume);
              }
              const video = videoRef.current;
              const audio = audioRef.current;
              if (video) {
                video.volume = preference.volume;
                video.defaultMuted =
                  Boolean(selectedAudio) || !preference.soundEnabled;
                video.muted =
                  Boolean(selectedAudio) || !preference.soundEnabled;
              }
              if (audio) {
                audio.volume = preference.volume;
                audio.muted = !preference.soundEnabled;
              }
            },
            style: {
              width: 68,
              accentColor: "#28c99a",
              cursor: "pointer",
            },
          }, item.subtitleUrl
            ? createElement("track", {
                kind: "subtitles",
                src: item.subtitleUrl,
                srcLang: "zh",
                label: "自动字幕",
                default: true,
              })
            : null)}
          <Text style={styles.playerVolumeValue}>
            {Math.round(soundVolume * 100)}%
          </Text>
        </View>
            </View>
          );
          return actionControlsHost
            ? createPortal(controls, actionControlsHost)
            : startVideoFullscreen
            ? controls
            : null;
        })()}
        {item.productEditorAllowed ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="插入视频商品"
            style={styles.playerCommerceEditorButton}
            onPress={() => setProductEditorOpen(true)}
          >
            <Feather name="shopping-bag" size={15} color="#17120b" />
            <Text style={styles.playerCommerceEditorText}>插入商品</Text>
          </Pressable>
        ) : null}
        {!videoFullscreen && windowControlsHost
          ? createPortal(
              <View style={styles.interactionTrackControls}>
                {windowControls}
              </View>,
              windowControlsHost,
            )
          : !videoFullscreen && windowControls}
      </ScrollView>
      {resumePromptPosition > 0 ? (
        <View
          style={[
            styles.resumePromptBanner,
            styles.resumePromptBannerBelowDrag,
          ]}
        >
          <View style={styles.resumePromptCopy}>
            <View style={styles.resumePromptIcon}>
              <Feather name="clock" size={18} color="#071510" />
            </View>
            <View style={styles.resumePromptTextBlock}>
              <Text style={styles.resumePromptTitle} numberOfLines={1}>
                上次播放到 {formatPlaybackPosition(resumePromptPosition)}
              </Text>
              <Text style={styles.resumePromptDescription} numberOfLines={1}>
                当前默认从头播放，{resumePromptSecondsLeft} 秒后自动关闭提示
              </Text>
            </View>
          </View>
          <View style={styles.resumePromptActions}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="从头播放"
              style={styles.resumePromptSecondaryButton}
              onPress={restartFromBeginning}
            >
              <Text style={styles.resumePromptSecondaryText}>从头播放</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`从上次位置 ${formatPlaybackPosition(
                resumePromptPosition,
              )} 继续播放`}
              style={styles.resumePromptPrimaryButton}
              onPress={continueFromSavedPosition}
            >
              <Feather name="play" size={15} color="#04110d" />
              <Text style={styles.resumePromptPrimaryText}>继续上次播放</Text>
            </Pressable>
          </View>
        </View>
      ) : null}
      {createElement(
        "div",
        {
          "data-player-media-frame": "viewport-bounded",
          // Bound the stage itself: limiting only the video leaves an empty
          // strip below it when a wide floating window exceeds viewport height.
          style: {
            width: "100%",
            aspectRatio: fitBrowserViewport ? undefined : "16 / 9",
            height: fitBrowserViewport ? "calc(100dvh - 2px)" : "auto",
            maxHeight: fitBrowserViewport ? "100dvh" : undefined,
            minHeight: fitBrowserViewport ? 0 : 220,
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#030705",
            flex: "none",
            flexShrink: 0,
          },
        },
        <>
        {windowsNativePlayback
          ? createElement(
            "div",
            {
              ref: nativeSlotRef,
              "data-neptune-native-player-slot": item.id,
              style: {
                width: "100%",
                height: "100%",
                backgroundColor: "black",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#7fd8be",
                fontFamily:
                  'MiSans, "Inter Variable", Inter, "Microsoft YaHei", sans-serif',
                fontSize: 13,
              },
            },
            "正在连接 VVC 原生解码内核…",
          )
          : createElement("video", {
            ref: videoRef,
            controls: true,
            autoPlay: false,
            preload: "auto",
            playsInline: true,
            muted: !soundUnlocked,
            onLoadStart: () => setPlayerLoading(true),
            onLoadedData: () => {
              if (!smartBufferingRef.current) setPlayerLoading(false);
            },
            onCanPlay: () => {
              if (!smartBufferingRef.current) setPlayerLoading(false);
            },
            onWaiting: () => {
              if (!sourceSwitchingRef.current) {
                beginSmartBufferingRef.current(
                  "网速波动，正在补足安全缓冲",
                );
              }
            },
            onPlay: () => {
              if (!sourceSwitchingRef.current) resumePlayingRef.current = true;
              if (selectedAudio && audioRef.current) {
                audioRef.current.currentTime =
                  videoRef.current?.currentTime || 0;
                audioRef.current.play().catch(() => {});
              }
            },
            onPlaying: () => {
              if (smartBufferingRef.current) {
                videoRef.current?.pause();
                return;
              }
              setPlayerLoading(false);
            },
            onPause: () => {
              if (
                !sourceSwitchingRef.current &&
                !smartBufferingRef.current
              ) {
                resumePlayingRef.current = false;
              }
              audioRef.current?.pause();
            },
            onVolumeChange: () => {
              const video = videoRef.current;
              if (!video) return;
              const volume = Math.max(0, Math.min(video.volume, 1));
              const soundEnabled = selectedAudio
                ? soundUnlockedRef.current
                : !video.muted;
              if (selectedAudio && audioRef.current) {
                audioRef.current.volume = volume;
                audioRef.current.muted = !soundEnabled;
              }
              rememberSoundPreference(soundEnabled, volume);
            },
            onSeeking: () => {
              if (audioRef.current && videoRef.current) {
                audioRef.current.currentTime = videoRef.current.currentTime;
              }
            },
            onTimeUpdate: () => {
              const audio = audioRef.current;
              const video = videoRef.current;
              if (video && !sourceSwitchingRef.current) {
                resumeTimeRef.current = Math.max(0, video.currentTime || 0);
                setCommerceTimeSeconds(video.currentTime || 0);
              }
              if (
                audio &&
                video &&
                !audio.paused &&
                Math.abs(audio.currentTime - video.currentTime) > 0.45
              ) {
                audio.currentTime = video.currentTime;
              }
              if (
                video &&
                !sourceSwitchingRef.current &&
                Date.now() - lastResumeSaveRef.current >= 4000
              ) {
                lastResumeSaveRef.current = Date.now();
                saveResumePosition(
                  item.id,
                  video.currentTime || 0,
                  video.duration || 0,
                );
              }
              if (video && video.duration > 0) {
                const progress = video.currentTime / video.duration;
                const milestone = Math.floor(progress * 10) / 10;
                if (
                  milestone >= 0.1 &&
                  (cacheCheckpointRef.current.url !== playbackUri ||
                    milestone > cacheCheckpointRef.current.milestone)
                ) {
                  cacheCheckpointRef.current = { url: playbackUri, milestone };
                  void cacheHlsPlaybackCheckpoint(playbackUri, milestone);
                  if (selectedAudio?.url) {
                    void cacheHlsPlaybackCheckpoint(selectedAudio.url, milestone);
                  }
                }
              }
              if (
                video &&
                video.duration > 0 &&
                video.currentTime >= video.duration * 0.85 &&
                completedCacheRequestedRef.current !== playbackUri
              ) {
                completedCacheRequestedRef.current = playbackUri;
                void cacheCompletedHlsVideo(playbackUri);
                if (selectedAudio?.url) {
                  void cacheCompletedHlsVideo(selectedAudio.url);
                }
              }
              if (
                video &&
                item.isPaid === true &&
                !paidEntitledRef.current &&
                Number.isFinite(video.duration) &&
                video.duration > 0 &&
                video.currentTime >= video.duration / 4 &&
                !paidGateCheckingRef.current
              ) {
                resumePlayingRef.current = false;
                video.pause();
                audioRef.current?.pause();
                setPaidGateVisible(true);
                void refreshPaidEntitlement(true);
              }
            },
            onEnded: () => {
              if (sourceSwitchingRef.current) return;
              resumeTimeRef.current = 0;
              saveResumePosition(item.id, 0);
              completedCacheRequestedRef.current = playbackUri;
              void cacheCompletedHlsVideo(playbackUri);
              if (selectedAudio?.url) {
                void cacheCompletedHlsVideo(selectedAudio.url);
              }
              const video = videoRef.current;
              if (loopMode === "single" && video) {
                video.currentTime = 0;
                resumePlayingRef.current = true;
                video.play().catch(() => {
                  resumePlayingRef.current = false;
                  setAutoplayBlocked(true);
                });
                return;
              }
              resumePlayingRef.current = false;
              onPlaybackFinish?.(loopMode);
            },
            style: {
              width: "100%",
              aspectRatio: undefined,
              flex: "none",
              flexShrink: 0,
              height: videoFullscreen ? "100dvh" : "100%",
              maxHeight: videoFullscreen || fitBrowserViewport ? "100dvh" : "none",
              maxWidth: "100%",
              boxSizing: "border-box",
              position: videoFullscreen ? "fixed" : "absolute",
              inset: 0,
              zIndex: videoFullscreen ? 1101 : undefined,
              objectFit: "contain",
              backgroundColor: "black",
              display: "block",
            },
          })}
        {!windowsNativePlayback && !videoFullscreen && playerLoading ? (
          <DesktopPlayerLoadingPlaceholder
            title={item.title}
            thumbnail={cardThumbnailUrl(item.thumbnail)}
            source={loadingSource}
            status={smartBufferStatus}
          />
        ) : null}
        </>,
      )}
      {cacheDiagnosticPanel && cacheDiagnosticsHost
        ? createPortal(cacheDiagnosticPanel, cacheDiagnosticsHost)
        : cacheDiagnosticPanel}
      {windowsNativePlayback ? (
        <View style={styles.nativePlayerControlBar}>
          <Pressable
            accessibilityRole="button"
            style={styles.nativePlayerControlButton}
            onPress={() => notifyWindowsPlayerCommand("seek-backward")}
          >
            <Feather name="rotate-ccw" size={15} color="#d9f7ee" />
            <Text style={styles.nativePlayerControlText}>10 秒</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            style={styles.nativePlayerPrimaryButton}
            onPress={() => notifyWindowsPlayerCommand("toggle-pause")}
          >
            <Feather name="play" size={16} color="#04110d" />
            <Text style={styles.nativePlayerPrimaryText}>播放 / 暂停</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            style={styles.nativePlayerControlButton}
            onPress={() => notifyWindowsPlayerCommand("seek-forward")}
          >
            <Feather name="rotate-cw" size={15} color="#d9f7ee" />
            <Text style={styles.nativePlayerControlText}>10 秒</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            style={styles.nativePlayerControlButton}
            onPress={() => notifyWindowsPlayerCommand("mute")}
          >
            <Feather name="volume-x" size={15} color="#d9f7ee" />
            <Text style={styles.nativePlayerControlText}>静音</Text>
          </Pressable>
          <View style={styles.nativePlayerEngineBadge}>
            <Feather name="cpu" size={14} color="#28c99a" />
            <Text style={styles.nativePlayerEngineText}>
              软件原生解码 · 网页融合界面
            </Text>
          </View>
        </View>
      ) : null}
      <ProductSpotlight
        placements={productPlacements}
        currentTimeSeconds={commerceTimeSeconds}
        compact
      />
      {item.productEditorAllowed ? (
        <ProductPlacementEditor
          visible={productEditorOpen}
          videoId={item.id}
          videoTitle={item.title}
          currentTimeSeconds={commerceTimeSeconds}
          initialPlacements={productPlacements}
          onClose={() => setProductEditorOpen(false)}
          onSaved={setProductPlacements}
        />
      ) : null}
      {paidGateVisible ? (
        <View style={styles.paidGateOverlay}>
          <View style={styles.paidGateCard}>
            <View style={styles.paidGateIcon}>
              <Feather name="lock" size={24} color="#17120b" />
            </View>
            <Text style={styles.paidGateTitle}>收费视频 · {paidPrice} 币</Text>
            <Text style={styles.paidGateDescription}>
              已播放到视频的四分之一。验证账号并完成虚拟币结算后，可继续观看完整视频。
            </Text>
            <View style={styles.paidGateAccount}>
              <Text style={styles.paidGateAccountName} numberOfLines={1}>
                {paidEntitlement?.account?.label ||
                  paidEntitlement?.account?.email ||
                  identity?.displayName ||
                  identity?.email ||
                  "账号尚未验证"}
              </Text>
              <Text style={styles.paidGateBalance}>
                {paidEntitlement?.authenticated
                  ? `虚拟币余额：${paidEntitlement.account?.balance || 0} 币`
                  : "登录状态：需要验证"}
              </Text>
            </View>
            <Text style={styles.paidGateMessage}>{paidGateMessage}</Text>
            <View style={styles.paidGateActions}>
              {paidEntitlement?.authenticated ? (
                <Pressable
                  accessibilityRole="button"
                  disabled={paidPurchaseSaving}
                  style={[
                    styles.paidGatePrimaryAction,
                    paidPurchaseSaving && styles.paidGateActionDisabled,
                  ]}
                  onPress={() => void completePaidPurchase()}
                >
                  <Text style={styles.paidGatePrimaryText}>
                    {paidPurchaseSaving
                      ? "正在结算…"
                      : `支付 ${paidPrice} 币继续`}
                  </Text>
                </Pressable>
              ) : (
                <Pressable
                  accessibilityRole="button"
                  style={styles.paidGatePrimaryAction}
                  onPress={onRequirePaidLogin}
                >
                  <Text style={styles.paidGatePrimaryText}>验证登录账号</Text>
                </Pressable>
              )}
              <Pressable
                accessibilityRole="button"
                disabled={paidGateCheckingRef.current || paidPurchaseSaving}
                style={styles.paidGateSecondaryAction}
                onPress={() => void refreshPaidEntitlement(false)}
              >
                <Text style={styles.paidGateSecondaryText}>刷新余额</Text>
              </Pressable>
            </View>
          </View>
        </View>
      ) : null}
      {createElement("audio", { ref: audioRef, preload: "auto" })}
    </View>
  );
}

function AdminVideoEditor({
  visible,
  mode,
  initialVideo,
  onClose,
  onSaved,
}: {
  visible: boolean;
  mode: "create" | "edit" | "variants";
  initialVideo: AdminVideo | null;
  onClose: () => void;
  onSaved: (video: AdminVideo | null, created: boolean) => void;
}) {
  const [title, setTitle] = useState("");
  const [channel, setChannel] = useState("");
  const [category, setCategory] = useState("freshVideos");
  const [duration, setDuration] = useState("0:00");
  const [tags, setTags] = useState<string[]>([]);
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [subtitleUrl, setSubtitleUrl] = useState("");
  const [organizationStatus, setOrganizationStatus] = useState("");
  const [organizing, setOrganizing] = useState(false);
  const [variants, setVariants] = useState<AdminMediaVariant[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!visible) return;
    setTitle(initialVideo?.title || "");
    setChannel(initialVideo?.channel || "");
    setCategory(initialVideo?.category || "freshVideos");
    setDuration(initialVideo?.duration || "0:00");
    setTags(initialVideo?.manualTags || initialVideo?.tags || []);
    setThumbnailUrl(initialVideo?.thumbnailUrl || initialVideo?.thumbnail || "");
    setSubtitleUrl(initialVideo?.subtitleUrl || "");
    setOrganizationStatus("");
    setVariants(
      initialVideo?.mediaVariants?.length
        ? initialVideo.mediaVariants.map((variant) => ({ ...variant }))
        : [{
            id: `desktop-${Date.now()}`,
            kind: "desktop",
            label: "画质：电脑版 · 原始 · 高质量",
            source: "",
            format: "mp4",
            isDefault: true,
          }],
    );
    setError("");
  }, [initialVideo, visible]);

  const updateVariant = (
    index: number,
    field: "label" | "source",
    value: string,
  ) => setVariants((current) => current.map((variant, variantIndex) =>
    variantIndex === index ? { ...variant, [field]: value } : variant,
  ));

  const organize = async () => {
    const source = variants.find(
      (variant) => variant.kind !== "audio" && variant.source.trim(),
    )?.source.trim();
    if (!source) {
      setError("请先填写视频地址或服务器存储路径，再执行自动整理。");
      return;
    }
    setOrganizing(true);
    setError("");
    setOrganizationStatus("正在读取媒体信息并生成封面…");
    try {
      const result = await organizeAdminVideo({
        source,
        title: title.trim(),
        tags,
        category: category === "freshVideos" ? "" : category,
      });
      setTitle(result.title);
      setCategory(result.category);
      setDuration(result.duration);
      setTags(result.tags);
      if (result.thumbnailPath) {
        setThumbnailUrl(`${VIDEO_SERVER_BASE_URL}${result.thumbnailPath}`);
      }
      if (result.subtitlePath) {
        setSubtitleUrl(`${VIDEO_SERVER_BASE_URL}${result.subtitlePath}`);
      }
      setVariants((current) => current.map((variant, index) =>
        index === 0 && variant.kind !== "audio"
          ? {
              ...variant,
              kind: result.recommendedKind,
              resolution: result.width && result.height
                ? `${result.width}x${result.height}`
                : variant.resolution,
              codec: result.codec || variant.codec,
              label: `${result.recommendedKind === "mobile" ? "手机版" : "电脑版"} · ${result.width || "自动"}x${result.height || "检测"}`,
            }
          : variant,
      ));
      const subtitle = result.subtitleStatus === "embedded"
        ? `检测到 ${result.embeddedSubtitleTracks} 条内嵌字幕`
        : result.subtitleStatus === "api-ready"
          ? "未发现内嵌字幕，可调用已配置的转写 API"
          : "未发现内嵌字幕；语音字幕需要外接转写 API";
      setOrganizationStatus(
        `${result.orientation === "portrait" ? "竖屏" : result.orientation === "landscape" ? "横屏" : "方形/未知"} · ${result.duration} · ${result.category} · ${subtitle}`,
      );
    } catch (requestError) {
      setOrganizationStatus("");
      setError(requestError instanceof Error ? requestError.message : "自动整理失败");
    } finally {
      setOrganizing(false);
    }
  };

  const save = async () => {
    const cleanTitle = title.trim();
    const cleanChannel = channel.trim();
    const cleanVariants = variants
      .map((variant, index) => ({
        ...variant,
        id: variant.id || `${variant.kind}-${index + 1}`,
        label: variant.label.trim() || `视频版本 ${index + 1}`,
        source: variant.source.trim(),
      }))
      .filter((variant) => variant.source);
    if (!cleanTitle || !cleanChannel || !cleanVariants.length) {
      setError("请填写视频名称、频道，并至少保留一个有地址的视频版本。");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (mode === "create") {
        await createAdminVideo({
          title: cleanTitle,
          channel: cleanChannel,
          category,
          duration,
          manualTags: tags,
          thumbnailUrl,
          subtitleUrl,
          sourceUrl: cleanVariants[0].source,
          mediaVariants: cleanVariants,
        });
        onSaved(null, true);
      } else if (initialVideo) {
        const updated = await updateAdminVideo(initialVideo.id, {
          title: cleanTitle,
          channel: cleanChannel,
          category,
          duration,
          manualTags: tags,
          thumbnailUrl,
          subtitleUrl,
          mediaVariants: cleanVariants,
        });
        onSaved(updated, false);
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.adminEditorBackdrop}>
        <View style={styles.adminEditorCard}>
          <View style={styles.adminEditorHeader}>
            <View>
              <Text style={styles.adminEditorTitle}>
                {mode === "create" ? "新增视频" : mode === "variants" ? "管理视频版本" : "编辑视频与改名"}
              </Text>
              <Text style={styles.adminEditorHint}>仅管理员可见，保存后立即更新网站媒体库</Text>
            </View>
            <Pressable onPress={onClose} style={styles.adminEditorClose}>
              <Feather name="x" size={20} color="#bdeee0" />
            </Pressable>
          </View>
          <ScrollView style={styles.adminEditorScroll} contentContainerStyle={styles.adminEditorBody}>
            <View style={styles.adminVariantHeading}>
              <View>
                <Text style={styles.adminEditorLabel}>自动整理</Text>
                <Text style={styles.adminEditorHint}>读取方向、时长和编码，生成标题、标签、分类及封面</Text>
              </View>
              <Pressable disabled={organizing} style={styles.adminSmallButton} onPress={() => void organize()}>
                <Feather name="cpu" size={14} color="#07130f" />
                <Text style={styles.adminSmallButtonText}>{organizing ? "分析中…" : "立即自动整理"}</Text>
              </Pressable>
            </View>
            {organizationStatus ? <Text style={styles.adminEditorHint}>{organizationStatus}</Text> : null}
            <Text style={styles.adminEditorLabel}>视频名称</Text>
            <TextInput value={title} onChangeText={setTitle} style={styles.adminEditorInput} />
            <Text style={styles.adminEditorLabel}>频道名称</Text>
            <TextInput value={channel} onChangeText={setChannel} style={styles.adminEditorInput} />
            <Text style={styles.adminEditorLabel}>分类</Text>
            {createElement(
              "select",
              {
                value: category,
                onChange: (event: { target: { value: string } }) => setCategory(event.target.value),
                style: styles.adminEditorInput as never,
              },
              createElement("option", { value: "freshVideos" }, "最新"),
              createElement("option", { value: "games" }, "游戏"),
              createElement("option", { value: "music" }, "音乐"),
              createElement("option", { value: "shorts" }, "短片"),
              createElement("option", { value: "live" }, "直播"),
            )}
            <Text style={styles.adminEditorLabel}>标签</Text>
            <TextInput
              value={tags.join(", ")}
              onChangeText={(value) => setTags(value.split(/[,，]/).map((tag) => tag.trim()).filter(Boolean))}
              style={styles.adminEditorInput}
            />
            <Text style={styles.adminEditorLabel}>封面地址</Text>
            <TextInput value={thumbnailUrl} onChangeText={setThumbnailUrl} style={styles.adminEditorInput} autoCapitalize="none" />
            <View style={styles.adminVariantHeading}>
              <Text style={styles.adminEditorLabel}>视频版本</Text>
              <Pressable
                style={styles.adminSmallButton}
                onPress={() => setVariants((current) => [...current, {
                  id: `desktop-${Date.now()}`,
                  kind: "desktop",
                  label: "画质：电脑版 · 新版本",
                  source: "",
                  format: "mp4",
                }])}
              >
                <Feather name="plus" size={14} color="#07130f" />
                <Text style={styles.adminSmallButtonText}>增加版本</Text>
              </Pressable>
            </View>
            {variants.map((variant, index) => (
              <View key={`${variant.id}-${index}`} style={styles.adminVariantCard}>
                <View style={styles.adminVariantTopRow}>
                  <Text style={styles.adminVariantKind}>{variant.kind.toUpperCase()} · {variant.format || "媒体"}</Text>
                  <Pressable
                    style={styles.adminDangerLink}
                    onPress={() => setVariants((current) => current.filter((_, variantIndex) => variantIndex !== index))}
                  >
                    <Feather name="trash-2" size={14} color="#ff9c9c" />
                    <Text style={styles.adminDangerLinkText}>删除版本</Text>
                  </Pressable>
                </View>
                <Text style={styles.adminEditorLabel}>显示名称</Text>
                <TextInput
                  value={variant.label}
                  onChangeText={(value) => updateVariant(index, "label", value)}
                  style={styles.adminEditorInput}
                />
                <Text style={styles.adminEditorLabel}>媒体地址 / 存储路径</Text>
                <TextInput
                  value={variant.source}
                  onChangeText={(value) => updateVariant(index, "source", value)}
                  onBlur={() => {
                    if (mode === "create" && index === 0 && !organizationStatus && !organizing) {
                      void organize();
                    }
                  }}
                  style={styles.adminEditorInput}
                  autoCapitalize="none"
                />
              </View>
            ))}
            {error ? <Text style={styles.adminEditorError}>{error}</Text> : null}
          </ScrollView>
          <View style={styles.adminEditorFooter}>
            <Pressable onPress={onClose} style={styles.adminSecondaryButton}>
              <Text style={styles.adminSecondaryButtonText}>取消</Text>
            </Pressable>
            <Pressable disabled={saving} onPress={() => void save()} style={styles.adminPrimaryButton}>
              <Text style={styles.adminPrimaryButtonText}>{saving ? "保存中…" : "保存更改"}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function DesktopVideoActions({
  video,
  identity,
  isAdmin,
  onVideoChanged,
  onVideoDeleted,
  subscribed,
  subscriptionSaving,
  onToggleSubscription,
  mediaControlsHostRef,
  playerControlsHostRef,
  windowActionsHostRef,
  trackControlsHostRef,
  shareControlsHostRef,
  cacheDiagnosticsHostRef,
}: {
  video: ColumnVideo;
  identity: AccessIdentity;
  isAdmin: boolean;
  onVideoChanged: (video: AdminVideo | null, created: boolean) => void;
  onVideoDeleted: () => void;
  subscribed: boolean;
  subscriptionSaving: boolean;
  onToggleSubscription: () => void;
  mediaControlsHostRef?: (node: HTMLDivElement | null) => void;
  playerControlsHostRef?: (node: HTMLDivElement | null) => void;
  windowActionsHostRef?: (node: HTMLDivElement | null) => void;
  trackControlsHostRef?: (node: HTMLDivElement | null) => void;
  shareControlsHostRef?: (node: HTMLDivElement | null) => void;
  cacheDiagnosticsHostRef?: (node: HTMLDivElement | null) => void;
}) {
  const [favorited, setFavorited] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [comments, setComments] = useState<VideoComment[]>([]);
  const [commentText, setCommentText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [adminEditorMode, setAdminEditorMode] = useState<"create" | "edit" | "variants" | null>(null);
  const [adminVideo, setAdminVideo] = useState<AdminVideo | null>(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState("");
  const [editingCommentText, setEditingCommentText] = useState("");

  const refresh = () =>
    fetchVideoActionState(video.id, identity).then((state) => {
      setFavorited(state.favorited);
      setCommentCount(state.commentCount);
      setComments(state.comments);
    });

  useEffect(() => {
    let active = true;
    fetchVideoActionState(video.id, identity)
      .then((state) => {
        if (!active) return;
        setFavorited(state.favorited);
        setCommentCount(state.commentCount);
        setComments(state.comments);
      })
      .catch((requestError) => active && setError(requestError.message));
    return () => {
      active = false;
    };
  }, [identity?.uid, video.id]);

  const toggleFavorite = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await setVideoFavorite(video.id, video.title, identity, !favorited);
      setFavorited(!favorited);
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "收藏失败",
      );
    } finally {
      setSaving(false);
    }
  };

  const submitComment = async () => {
    const value = commentText.trim();
    if (!value || saving) return;
    setSaving(true);
    setError("");
    try {
      await addVideoComment(video.id, video.title, identity, value);
      setCommentText("");
      await refresh();
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "评论失败",
      );
    } finally {
      setSaving(false);
    }
  };

  const openAdminEditor = async (mode: "create" | "edit" | "variants") => {
    if (!isAdmin || adminLoading) return;
    if (mode === "create") {
      setAdminVideo(null);
      setAdminEditorMode(mode);
      return;
    }
    setAdminLoading(true);
    setError("");
    try {
      const adminVideos = await fetchAdminVideos();
      const current = adminVideos.find((item) => item.id === video.id);
      if (!current) throw new Error("管理员媒体库中找不到当前视频");
      setAdminVideo(current);
      setAdminEditorMode(mode);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "读取视频资料失败");
    } finally {
      setAdminLoading(false);
    }
  };

  const removeVideo = async (fullChain: boolean) => {
    if (!isAdmin || adminLoading) return;
    const confirmed = typeof window === "undefined" || window.confirm(
      fullChain
        ? `确定全链路删除《${video.title}》吗？\n\n将删除网站/VPS记录，并把本地媒体和 CDN 视频目录加入删除队列。公网视频将无法恢复。`
        : `确定仅从网站删除《${video.title}》吗？\n\n这会从网站媒体库移除视频，但保留 CDN 上的文件。`,
    );
    if (!confirmed) return;
    if (
      fullChain &&
      typeof window !== "undefined" &&
      !window.confirm("最后确认：CDN 删除不可从回收站恢复。仍要继续吗？")
    ) return;
    setAdminLoading(true);
    setError("");
    try {
      const result = await deleteAdminVideo(video.id, fullChain);
      if (fullChain && typeof window !== "undefined") {
        window.alert(
          result.cdnDeletionQueued === true
            ? "全链路删除已提交。网站记录已删除；本机同步任务会在下一轮清理本地媒体和 CDN 目录。"
            : "网站记录已删除，但 CDN 删除未进入队列，请检查同步服务。",
        );
      }
      onVideoDeleted();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "删除视频失败");
    } finally {
      setAdminLoading(false);
    }
  };

  const saveCommentEdit = async (commentId: string) => {
    const value = editingCommentText.trim();
    if (!value || saving) return;
    setSaving(true);
    setError("");
    try {
      await updateVideoComment(video.id, commentId, value);
      setEditingCommentId("");
      setEditingCommentText("");
      await refresh();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "修改评论失败");
    } finally {
      setSaving(false);
    }
  };

  const removeComment = async (comment: VideoComment) => {
    if (!comment.id || saving) return;
    const confirmed = typeof window === "undefined" || window.confirm("确定删除这条评论吗？");
    if (!confirmed) return;
    setSaving(true);
    setError("");
    try {
      await deleteVideoComment(video.id, comment.id);
      await refresh();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "删除评论失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.interactionPanel}>
      <View style={styles.interactionVideoTitleRow}>
        <View style={styles.interactionVideoTitleCopy}>
          <Text style={styles.interactionVideoTitle} numberOfLines={1}>
            {video.title}
          </Text>
          {video.channel ? (
            <Text style={styles.interactionVideoCategory} numberOfLines={1}>
              · {video.channel}
            </Text>
          ) : null}
        </View>
        {video.isPaid && Number(video.coinPrice) > 0 ? (
          <View style={styles.playerPaidBadge}>
            <Feather name="lock" size={11} color="#17120b" />
            <Text style={styles.playerPaidBadgeText} numberOfLines={1}>
              收费 · {video.coinPrice} 币
            </Text>
          </View>
        ) : null}
        {video.isAdvertisement ? (
          <View style={styles.playerAdBadge}>
            <Feather name="radio" size={11} color="#fff4dd" />
            <Text style={styles.playerAdBadgeText}>广告</Text>
          </View>
        ) : null}
        {video.vipOnly ? (
          <View style={styles.playerVipBadge}>
            <Feather name="shield" size={11} color="#04110d" />
            <Text style={styles.playerVipBadgeText}>VIP 专属</Text>
          </View>
        ) : null}
        {createElement("div", {
          ref: mediaControlsHostRef,
          style: styles.interactionMediaControlsHost as never,
          "aria-label": "画质和音质设置",
        })}
        {createElement("div", {
          ref: windowActionsHostRef,
          style: styles.interactionWindowActionsHost as never,
          "aria-label": "播放窗口控制",
        })}
        {createElement("div", {
          ref: trackControlsHostRef,
          style: styles.interactionTrackControlsHost as never,
          "aria-label": "视频切换控制",
        })}
      </View>
      <View style={styles.interactionHeader}>
        {createElement("div", {
          ref: shareControlsHostRef,
          style: styles.interactionShareControlsHost as never,
          "aria-label": "分享当前视频",
        })}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={favorited ? "取消收藏" : "收藏视频"}
          onPress={toggleFavorite}
          style={[
            styles.favoriteButton,
            favorited && styles.favoriteButtonActive,
          ]}
        >
          <Feather
            name={favorited ? "bookmark" : "bookmark"}
            size={18}
            color={favorited ? "#17120b" : "#f0ca82"}
          />
          <Text
            style={[
              styles.favoriteText,
              favorited && styles.favoriteTextActive,
            ]}
          >
            {favorited ? "已收藏" : "收藏"}
          </Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            subscribed ? `取消订阅 ${video.channel}` : `订阅 ${video.channel}`
          }
          disabled={!video.channelId || subscriptionSaving}
          onPress={onToggleSubscription}
          style={[
            styles.subscriptionButton,
            subscribed && styles.subscriptionButtonActive,
            (!video.channelId || subscriptionSaving) &&
              styles.subscriptionButtonDisabled,
          ]}
        >
          <Feather
            name={subscribed ? "check" : "plus"}
            size={17}
            color={subscribed ? "#d8f3dc" : "#c9e5e8"}
          />
          <Text
            style={[
              styles.subscriptionButtonText,
              subscribed && styles.subscriptionButtonTextActive,
            ]}
          >
            {subscriptionSaving
              ? "处理中..."
              : subscribed
              ? "已订阅频道"
              : "订阅频道"}
          </Text>
        </Pressable>
        <View style={styles.interactionMetadata}>
          <Text style={styles.commentCount}>评论 {commentCount}</Text>
          {video.tags?.length ? (
            <Text style={styles.videoTags} numberOfLines={1}>
              标签：{video.tags.join(" · ")}
            </Text>
          ) : null}
        </View>
        {createElement("div", {
          ref: playerControlsHostRef,
          style: styles.interactionPlayerControlsHost as never,
          "aria-label": "当前视频播放工具",
        })}
      </View>
      {isAdmin ? (
        <View style={styles.adminToolbar}>
          <View style={styles.adminToolbarBadge}>
            <Feather name="shield" size={14} color="#07130f" />
            <Text style={styles.adminToolbarBadgeText}>ADMIN 管理工具</Text>
          </View>
          <Pressable style={styles.adminToolbarButton} onPress={() => void openAdminEditor("create")}>
            <Feather name="plus-circle" size={14} color="#bdeee0" />
            <Text style={styles.adminToolbarButtonText}>新增视频</Text>
          </Pressable>
          <Pressable style={styles.adminToolbarButton} onPress={() => void openAdminEditor("edit")}>
            <Feather name="edit-3" size={14} color="#bdeee0" />
            <Text style={styles.adminToolbarButtonText}>编辑 / 改名</Text>
          </Pressable>
          <Pressable style={styles.adminToolbarButton} onPress={() => void openAdminEditor("variants")}>
            <Feather name="layers" size={14} color="#bdeee0" />
            <Text style={styles.adminToolbarButtonText}>管理版本</Text>
          </Pressable>
          <Pressable style={styles.adminToolbarDangerButton} onPress={() => void removeVideo(false)}>
            <Feather name="trash-2" size={14} color="#ffb0b0" />
            <Text style={styles.adminToolbarDangerText}>{adminLoading ? "处理中…" : "仅删网站"}</Text>
          </Pressable>
          <Pressable style={styles.adminToolbarCriticalButton} onPress={() => void removeVideo(true)}>
            <Feather name="alert-triangle" size={14} color="#fff" />
            <Text style={styles.adminToolbarCriticalText}>全链路删除</Text>
          </Pressable>
        </View>
      ) : null}
      {isAdmin
        ? createElement("div", {
            ref: cacheDiagnosticsHostRef,
            style: styles.interactionCacheDiagnosticsHost as never,
            "aria-label": "播放缓存本地命中明细",
          })
        : null}
      <View style={styles.commentComposer}>
        <TextInput
          value={commentText}
          onChangeText={setCommentText}
          onSubmitEditing={submitComment}
          placeholder="写下你的评论，这也会改善个性化推荐"
          placeholderTextColor="#777"
          maxLength={500}
          style={styles.commentInput}
        />
        <Pressable
          disabled={!commentText.trim() || saving}
          onPress={submitComment}
          style={styles.commentSubmit}
        >
          <Text style={styles.commentSubmitText}>
            {saving ? "提交中" : "发布评论"}
          </Text>
        </Pressable>
      </View>
      {error ? <Text style={styles.interactionError}>{error}</Text> : null}
      {comments.slice(0, 4).map((comment, index) => (
        <View key={comment.id || `${comment.timestamp}-${index}`} style={styles.commentRow}>
          <Text style={styles.commentAuthor}>
            {comment.userLabel || "匿名访客"}
          </Text>
          {editingCommentId === comment.id ? (
            <TextInput
              value={editingCommentText}
              onChangeText={setEditingCommentText}
              maxLength={500}
              style={styles.adminCommentInput}
            />
          ) : (
            <Text style={styles.commentBody}>{comment.commentText}</Text>
          )}
          {isAdmin && comment.id ? (
            <View style={styles.adminCommentActions}>
              {editingCommentId === comment.id ? (
                <>
                  <Pressable onPress={() => void saveCommentEdit(comment.id)} style={styles.adminCommentAction}>
                    <Text style={styles.adminCommentActionText}>保存</Text>
                  </Pressable>
                  <Pressable onPress={() => setEditingCommentId("")} style={styles.adminCommentAction}>
                    <Text style={styles.adminCommentActionText}>取消</Text>
                  </Pressable>
                </>
              ) : (
                <Pressable
                  onPress={() => {
                    setEditingCommentId(comment.id);
                    setEditingCommentText(comment.commentText);
                  }}
                  style={styles.adminCommentAction}
                >
                  <Feather name="edit-2" size={13} color="#8ee4ca" />
                  <Text style={styles.adminCommentActionText}>修改</Text>
                </Pressable>
              )}
              <Pressable onPress={() => void removeComment(comment)} style={styles.adminCommentAction}>
                <Feather name="trash-2" size={13} color="#ff9c9c" />
                <Text style={styles.adminCommentDangerText}>删除</Text>
              </Pressable>
            </View>
          ) : null}
        </View>
      ))}
      <AdminVideoEditor
        visible={adminEditorMode !== null}
        mode={adminEditorMode || "edit"}
        initialVideo={adminVideo}
        onClose={() => setAdminEditorMode(null)}
        onSaved={(updated, created) => {
          setAdminEditorMode(null);
          onVideoChanged(updated, created);
        }}
      />
    </View>
  );
}

export default function DesktopHome() {
  const { width, height } = useWindowDimensions();
  const dispatch = useDispatch<AppDispatch>();
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const loopMode = useSelector((state: RootState) => state.playback.loopMode);
  const [activeSidebar, setActiveSidebar] = useState("首页");
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [videos, setVideos] = useState<ColumnVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [catalogueTotal, setCatalogueTotal] = useState(0);
  const [catalogueHasMore, setCatalogueHasMore] = useState(true);
  const [catalogueAppending, setCatalogueAppending] = useState(false);
  const [catalogueError, setCatalogueError] = useState("");
  const [query, setQuery] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const [activeSearchTag, setActiveSearchTag] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const suggestionCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [searchHistory, setSearchHistory] = useState<{
    user: SearchKeywordHistoryItem[];
    public: SearchKeywordHistoryItem[];
  }>({ user: [], public: [] });
  const [searchHistoryRefresh, setSearchHistoryRefresh] = useState(0);
  const [sortMode, setSortMode] = useState<DesktopVideoSort>(() => {
    if (typeof window === "undefined") return "personalized";
    const saved = window.localStorage.getItem("neptuneDesktopVideoSort");
    return sortModes.some((item) => item.key === saved)
      ? (saved as DesktopVideoSort)
      : "personalized";
  });
  const [rankingRefresh, setRankingRefresh] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const appendLockedRef = useRef(false);
  const listScrollRef = useRef<ScrollView>(null);
  const pageOffsetsRef = useRef<Record<number, number>>({});
  const listGridTopRef = useRef(0);
  const pendingPageRef = useRef<number | null>(null);
  const [visiblePage, setVisiblePage] = useState(1);
  const [visitedPages, setVisitedPages] = useState<number[]>([1]);
  const [listAppearance, setListAppearance] = useState<{
    layout: "grid" | "compact" | "list";
    size: "small" | "medium" | "large" | "original";
  }>(() => {
    try {
      const saved = JSON.parse(
        window.localStorage.getItem("neptuneVideoListAppearance") || "{}",
      );
      return {
        layout: ["grid", "compact", "list"].includes(saved.layout)
          ? saved.layout
          : "grid",
        size: ["small", "medium", "large", "original"].includes(saved.size)
          ? saved.size
          : "original",
      };
    } catch {
      return { layout: "grid", size: "original" };
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "neptuneVideoListAppearance",
        JSON.stringify(listAppearance),
      );
    } catch {}
  }, [listAppearance]);
  const [selectedVideo, setSelectedVideo] = useState<ColumnVideo | null>(null);
  const [playerHeaderControlsHost, setPlayerHeaderControlsHost] =
    useState<HTMLDivElement | null>(null);
  const [playerActionControlsHost, setPlayerActionControlsHost] =
    useState<HTMLDivElement | null>(null);
  const [playerWindowActionsHost, setPlayerWindowActionsHost] =
    useState<HTMLDivElement | null>(null);
  const [playerTrackControlsHost, setPlayerTrackControlsHost] =
    useState<HTMLDivElement | null>(null);
  const [playerShareControlsHost, setPlayerShareControlsHost] =
    useState<HTMLDivElement | null>(null);
  const [playerCacheDiagnosticsHost, setPlayerCacheDiagnosticsHost] =
    useState<HTMLDivElement | null>(null);
  const [playerOpenMode, setPlayerOpenMode] = useState<"floating" | "window">(
    () => {
      try {
        return window.localStorage.getItem("neptuneDesktopPlayerOpenMode") ===
          "window"
          ? "window"
          : "floating";
      } catch {
        return "floating";
      }
    },
  );
  const standalonePlayer =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("playerWindow") === "1";
  const [videoOnlyFullscreen, setVideoOnlyFullscreen] =
    useState(standalonePlayer);
  const [playerWindowNotice, setPlayerWindowNotice] = useState("");
  const changePlayerOpenMode = (mode: "floating" | "window") => {
    setPlayerOpenMode(mode);
    try {
      window.localStorage.setItem("neptuneDesktopPlayerOpenMode", mode);
    } catch {
      /* The selection still works when storage is unavailable. */
    }
    setPlayerWindowNotice("");
    if (mode === "window" && selectedVideo && !standalonePlayer) {
      const url = new URL(window.location.href);
      url.searchParams.set("video", selectedVideo.id);
      url.searchParams.set("playerWindow", "1");
      const popup = window.open(url.toString(), "_blank");
      if (popup) {
        setSelectedVideo(null);
        popup.focus();
        return;
      }
      setPlayerWindowNotice(
        "新标签页被浏览器拦截，已继续在当前页面播放。允许弹出窗口后可重试。",
      );
    }
  };
  useEffect(() => {
    const syncPlayerOpenMode = (event: StorageEvent) => {
      if (event.key === "neptuneDesktopPlayerOpenMode") {
        setPlayerOpenMode(event.newValue === "window" ? "window" : "floating");
      }
    };
    window.addEventListener("storage", syncPlayerOpenMode);
    return () => window.removeEventListener("storage", syncPlayerOpenMode);
  }, []);
  const selectedVideoHistoryRef = useRef<string[]>([]);
  useEffect(() => {
    if (selectedVideo) {
      notifyWindowsPlayerVideoOpen(selectedVideo);
    } else {
      notifyWindowsPlayerVideoClose();
    }
  }, [selectedVideo]);
  const [floatingPlayerLayout, setFloatingPlayerLayout] =
    useState<FloatingPlayerLayout>(initialFloatingPlayerLayout);
  const floatingPlayerLayoutRef = useRef(floatingPlayerLayout);
  const floatingPlayerRestoreRef = useRef<FloatingPlayerLayout | null>(null);
  const floatingPlayerLayerRef = useRef<HTMLDivElement | null>(null);
  const floatingPlayerPointerCleanupRef = useRef<null | (() => void)>(null);
  const [floatingPlayerMaximized, setFloatingPlayerMaximized] = useState(false);
  useLayoutEffect(() => {
    // Controls are below the video, so clicking them may scroll the overlay.
    // A new window size must start at the top of the newly sized video.
    const layer = floatingPlayerLayerRef.current;
    if (layer) {
      layer.scrollTop = 0;
      layer.scrollLeft = 0;
    }
  }, [floatingPlayerMaximized, floatingPlayerLayout.width]);
  const [playerDragHeaderVisible, setPlayerDragHeaderVisible] = useState(false);
  const playerDragHeaderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [hoverPreviewVideoId, setHoverPreviewVideoId] = useState("");
  const hoverPreviewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pendingHoverPreviewIdRef = useRef("");
  const [cacheSettingsOpen, setCacheSettingsOpen] = useState(false);
  const [cachePolicyVersion, setCachePolicyVersion] = useState(0);
  useEffect(() => {
    const refresh = () => setCachePolicyVersion((version) => version + 1);
    window.addEventListener("playback-cache-policy-changed", refresh);
    return () => window.removeEventListener("playback-cache-policy-changed", refresh);
  }, []);
  const [accountOpen, setAccountOpen] = useState(false);
  const [paidAccount, setPaidAccount] = useState<PaidAccount | null>(null);
  const [smartPlaybackPolicy, setSmartPlaybackPolicy] =
    useState<SmartPlaybackPolicy>(DEFAULT_SMART_PLAYBACK_POLICY);
  const [smartPlaybackPolicySource, setSmartPlaybackPolicySource] = useState<
    "account" | "default"
  >("default");
  const [smartPlaybackDraft, setSmartPlaybackDraft] =
    useState<SmartPlaybackPolicy>(DEFAULT_SMART_PLAYBACK_POLICY);
  const [smartPlaybackSaving, setSmartPlaybackSaving] = useState(false);
  const [smartPlaybackNotice, setSmartPlaybackNotice] = useState("");
  const [accountMode, setAccountMode] = useState<"login" | "register">("login");
  const [accountNotice, setAccountNotice] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [loginEmail, setLoginEmail] = useState(
    USE_LOCAL_DEMO_AUTH ? DEMO_EMAIL : "",
  );
  const [loginPassword, setLoginPassword] = useState(
    USE_LOCAL_DEMO_AUTH ? DEMO_PASSWORD : "",
  );
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [androidDownloadOpen, setAndroidDownloadOpen] = useState(false);
  const [profileTab, setProfileTab] = useState<DesktopProfileTab>("history");
  const [channelSubscriptions, setChannelSubscriptions] = useState<
    ChannelSubscription[]
  >([]);
  const [viewerWatchHistory, setViewerWatchHistory] = useState<
    ViewerWatchHistoryItem[]
  >([]);
  const [viewerHistoryRetentionDays, setViewerHistoryRetentionDays] =
    useState(90);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [watchHistoryLoading, setWatchHistoryLoading] = useState(true);
  const [favoriteFolderVideos, setFavoriteFolderVideos] = useState<
    ColumnVideo[]
  >([]);
  const [favoriteFolderLoading, setFavoriteFolderLoading] = useState(false);
  const [favoriteFolderError, setFavoriteFolderError] = useState("");
  const [favoriteFolderRefresh, setFavoriteFolderRefresh] = useState(0);
  const [subscriptionError, setSubscriptionError] = useState("");
  const [watchHistoryError, setWatchHistoryError] = useState("");
  const [viewerLibraryRefresh, setViewerLibraryRefresh] = useState(0);
  const [subscriptionSavingId, setSubscriptionSavingId] = useState("");
  const [subscriptionPage, setSubscriptionPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [favoriteFolderPage, setFavoriteFolderPage] = useState(1);
  const sharedVideoIdRef = useRef(
    typeof window === "undefined"
      ? ""
      : new URLSearchParams(window.location.search).get("video") || "",
  );
  const sharedVideoHandledRef = useRef(false);

  useEffect(() => {
    if (sharedVideoHandledRef.current || !sharedVideoIdRef.current || loading) {
      return;
    }
    sharedVideoHandledRef.current = true;
    const sharedVideo = videos.find(
      (video) => video.id === sharedVideoIdRef.current,
    );
    if (sharedVideo && !sharedVideo.vipLocked) {
      setSelectedVideo(sharedVideo);
    }
  }, [loading, videos]);

  useEffect(() => {
    floatingPlayerLayoutRef.current = floatingPlayerLayout;
  }, [floatingPlayerLayout]);

  useEffect(() => {
    const keepPlayerInsideViewport = () => {
      setFloatingPlayerLayout((layout) => clampFloatingPlayerLayout(layout));
    };
    window.addEventListener("resize", keepPlayerInsideViewport);
    return () => window.removeEventListener("resize", keepPlayerInsideViewport);
  }, []);

  useEffect(() => () => floatingPlayerPointerCleanupRef.current?.(), []);

  useEffect(
    () => () => {
      if (playerDragHeaderTimerRef.current) {
        clearTimeout(playerDragHeaderTimerRef.current);
      }
    },
    [],
  );

  const revealPlayerDragHeader = () => {
    setPlayerDragHeaderVisible(true);
    if (playerDragHeaderTimerRef.current) {
      clearTimeout(playerDragHeaderTimerRef.current);
    }
    playerDragHeaderTimerRef.current = setTimeout(() => {
      setPlayerDragHeaderVisible(false);
      playerDragHeaderTimerRef.current = null;
    }, 1500);
  };

  const hidePlayerDragHeader = () => {
    if (playerDragHeaderTimerRef.current) {
      clearTimeout(playerDragHeaderTimerRef.current);
      playerDragHeaderTimerRef.current = null;
    }
    setPlayerDragHeaderVisible(false);
  };

  const beginFloatingPlayerPointerGesture = (
    event: FloatingPlayerPointerStartEvent,
    mode: "move" | "resize",
  ) => {
    event.preventDefault();
    floatingPlayerPointerCleanupRef.current?.();
    const startPointer = { x: event.clientX, y: event.clientY };
    const startLayout = floatingPlayerLayoutRef.current;
    setFloatingPlayerMaximized(false);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - startPointer.x;
      const dy = moveEvent.clientY - startPointer.y;
      setFloatingPlayerLayout(
        clampFloatingPlayerLayout(
          mode === "move"
            ? { ...startLayout, x: startLayout.x + dx, y: startLayout.y + dy }
            : {
                ...startLayout,
                width: startLayout.width + dx,
                height: startLayout.height + dy,
              },
        ),
      );
    };
    const finishPointerGesture = () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", finishPointerGesture);
      window.removeEventListener("pointercancel", finishPointerGesture);
      floatingPlayerPointerCleanupRef.current = null;
    };
    floatingPlayerPointerCleanupRef.current = finishPointerGesture;
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", finishPointerGesture);
    window.addEventListener("pointercancel", finishPointerGesture);
  };

  const scaleFloatingPlayer = (scale: number) => {
    const direction = scale >= 1 ? 1 : -1;
    const viewport = floatingPlayerViewport();
    const current = floatingPlayerMaximized
      ? direction < 0 && floatingPlayerRestoreRef.current
        ? floatingPlayerRestoreRef.current
        : { ...floatingPlayerLayoutRef.current, width: viewport.width, x: 0 }
      : floatingPlayerLayoutRef.current;
    const nextWidth = Math.max(Math.min(FLOATING_PLAYER_MIN_WIDTH, viewport.width), Math.min(viewport.width, current.width + direction * 96));
    if (nextWidth === current.width) return;
    setFloatingPlayerMaximized(false);
    if (direction < 0 && floatingPlayerRestoreRef.current) {
      floatingPlayerRestoreRef.current = null;
    }
    const nextHeight = current.height + direction * 54;
    setFloatingPlayerLayout(
      clampFloatingPlayerLayout({
        x: current.x - (nextWidth - current.width) / 2,
        y: current.y,
        width: nextWidth,
        height: nextHeight,
      }),
    );
  };

  const toggleFloatingPlayerMaximized = () => {
    if (floatingPlayerMaximized && floatingPlayerRestoreRef.current) {
      setFloatingPlayerLayout(
        clampFloatingPlayerLayout(floatingPlayerRestoreRef.current),
      );
      floatingPlayerRestoreRef.current = null;
      setFloatingPlayerMaximized(false);
      return;
    }
    const viewport = floatingPlayerViewport();
    floatingPlayerRestoreRef.current = floatingPlayerLayoutRef.current;
    setFloatingPlayerLayout({
      x: 0,
      y: 0,
      width: viewport.width,
      height: viewport.height,
    });
    setFloatingPlayerMaximized(true);
  };

  useEffect(() => {
    void dispatch(userAuthStateListener());
    void fetchPaidAccountSession()
      .then((account) => {
        setPaidAccount(account);
        if (USE_LOCAL_DEMO_AUTH) {
          if (account) {
            const user = accountUserFromPaidAccount(account);
            persistLocalAuthenticatedUser(user);
            dispatch(setUserState({ currentUser: user, loaded: true }));
          } else {
            // A null response is an explicit 401, so the saved session really
            // is no longer valid.
            persistLocalAuthenticatedUser(null);
            dispatch(setUserState({ currentUser: null, loaded: true }));
          }
        }
      })
      .catch(() => {
        // Keep the last known account while the API is temporarily unavailable
        // during a deployment. A transport error must never log the user out.
      });
  }, [dispatch]);

  useEffect(() => {
    void fetchSmartPlaybackPolicy()
      .then((result) => {
        setSmartPlaybackPolicy(result.policy);
        setSmartPlaybackDraft(result.policy);
        setSmartPlaybackPolicySource(result.source);
      })
      .catch(() => {
        setSmartPlaybackPolicy(DEFAULT_SMART_PLAYBACK_POLICY);
        setSmartPlaybackDraft(DEFAULT_SMART_PLAYBACK_POLICY);
        setSmartPlaybackPolicySource("default");
      });
  }, [paidAccount?.id]);

  const accountUserFromPaidAccount = (account: PaidAccount) => ({
    uid: account.id,
    email: account.email,
    displayName: account.label || account.email.split("@")[0] || "注册会员",
    photoURL: "",
    followingCount: 0,
    followersCount: 0,
    likesCount: 0,
  });

  const switchAccountMode = (mode: "login" | "register") => {
    setAccountMode(mode);
    setLoginError("");
    if (mode === "register") {
      if (loginEmail === DEMO_EMAIL) setLoginEmail("");
      if (loginPassword === DEMO_PASSWORD) setLoginPassword("");
    }
  };

  const acceptPaidAccountSession = (account: PaidAccount) => {
    setPaidAccount(account);
    if (USE_LOCAL_DEMO_AUTH) {
      const user = accountUserFromPaidAccount(account);
      persistLocalAuthenticatedUser(user);
      dispatch(setUserState({ currentUser: user, loaded: true }));
    }
    setAccountNotice(
      account.isAdmin
        ? "ADMIN 管理员身份验证成功"
        : account.isVip
        ? "VIP 身份验证成功"
        : "注册会员身份验证成功",
    );
    setRankingRefresh((value) => value + 1);
    setAccountOpen(false);
  };

  const submitDesktopLogin = async () => {
    if (!loginEmail.trim() || !loginPassword) {
      setLoginError("请输入登录账号和密码");
      return;
    }
    setLoginSubmitting(true);
    setLoginError("");
    try {
      const session = await createPaidAccountSession(
        loginEmail.trim(),
        loginPassword,
      );
      if (!session.account) throw new Error("账号验证失败");
      if (!USE_LOCAL_DEMO_AUTH) {
        await dispatch(
          login({ email: loginEmail.trim(), password: loginPassword }),
        ).unwrap();
      }
      acceptPaidAccountSession(session.account);
    } catch (error) {
      setLoginError(
        error instanceof Error ? error.message : "登录失败，请检查账号和密码",
      );
    } finally {
      setLoginSubmitting(false);
    }
  };

  const submitDesktopRegistration = async () => {
    if (!loginEmail.trim() || !loginPassword) {
      setLoginError("请输入注册邮箱和密码");
      return;
    }
    setLoginSubmitting(true);
    setLoginError("");
    try {
      const session = await registerPaidAccount(
        loginEmail.trim(),
        loginPassword,
        registerName.trim(),
      );
      if (!session.account) throw new Error("注册未完成");
      acceptPaidAccountSession(session.account);
    } catch (error) {
      setLoginError(
        error instanceof Error ? error.message : "注册失败，请重试",
      );
    } finally {
      setLoginSubmitting(false);
    }
  };

  const refreshPaidMembership = async () => {
    setLoginError("");
    try {
      const account = await fetchPaidAccountSession();
      if (!account) {
        setPaidAccount(null);
        if (USE_LOCAL_DEMO_AUTH) {
          persistLocalAuthenticatedUser(null);
          dispatch(setUserState({ currentUser: null, loaded: true }));
          setAccountMode("login");
        }
        setLoginError("账号验证已过期，请重新登录");
        return;
      }
      setPaidAccount(account);
      setAccountNotice(
        account.isAdmin
          ? "ADMIN 管理员身份已生效"
          : account.isVip
          ? "VIP 身份已生效"
          : "当前仍为注册会员",
      );
      setRankingRefresh((value) => value + 1);
    } catch (error) {
      setLoginError(
        error instanceof Error
          ? `服务器暂时不可用，已保留登录状态：${error.message}`
          : "服务器暂时不可用，已保留登录状态",
      );
    }
  };

  const logoutPaidAccount = async () => {
    setLoginError("");
    try {
      await clearPaidAccountSession();
    } catch {}
    setPaidAccount(null);
    persistLocalAuthenticatedUser(null);
    dispatch(setUserState({ currentUser: null, loaded: true }));
    setSelectedVideo(null);
    setAccountMode("login");
    setAccountNotice("账号已退出");
    setRankingRefresh((value) => value + 1);
  };

  const openAdminConsole = () => {
    if (typeof window !== "undefined") {
      window.location.assign(`${VIDEO_SERVER_BASE_URL}/admin/videos`);
    }
  };

  const activeSmartPlaybackPreset = smartPlaybackPresets.find((preset) =>
    sameSmartPlaybackPolicy(preset.policy, smartPlaybackDraft),
  );

  const applySmartPlaybackPreset = (preset: SmartPlaybackPreset) => {
    setSmartPlaybackDraft({ ...preset.policy });
    setSmartPlaybackNotice(`已套用“${preset.label}”，保存后对当前账号生效`);
  };

  const setSmartPlaybackDraftValue = (
    field: (typeof smartPlaybackFields)[number],
    value: number,
  ) => {
    const normalized = Math.max(0, Math.min(field.maximum, Math.round(value)));
    setSmartPlaybackDraft((current) => ({
      ...current,
      [field.key]: normalized,
    }));
    setSmartPlaybackNotice("参数已调整，点击保存后生效");
  };

  const savePersonalSmartPlaybackPolicy = async () => {
    if (!paidAccount) {
      setSmartPlaybackNotice("请先登录账号，再保存个人智能播放策略");
      setAccountMode("login");
      setAccountOpen(true);
      return;
    }
    setSmartPlaybackSaving(true);
    try {
      const result = await saveSmartPlaybackPolicy(smartPlaybackDraft);
      setSmartPlaybackPolicy(result.policy);
      setSmartPlaybackDraft(result.policy);
      setSmartPlaybackPolicySource("account");
      setSmartPlaybackNotice("个人智能播放策略已保存并立即生效");
    } catch (error) {
      setSmartPlaybackNotice(
        error instanceof Error ? error.message : "策略保存失败",
      );
    } finally {
      setSmartPlaybackSaving(false);
    }
  };

  const restorePersonalSmartPlaybackPolicy = async () => {
    if (!paidAccount) {
      setSmartPlaybackNotice("当前是访客模式，正在使用全站默认策略");
      return;
    }
    setSmartPlaybackSaving(true);
    try {
      const result = await resetSmartPlaybackPolicy();
      setSmartPlaybackPolicy(result.policy);
      setSmartPlaybackDraft(result.policy);
      setSmartPlaybackPolicySource("default");
      setSmartPlaybackNotice("已恢复全站默认智能播放策略");
    } catch (error) {
      setSmartPlaybackNotice(
        error instanceof Error ? error.message : "恢复默认失败",
      );
    } finally {
      setSmartPlaybackSaving(false);
    }
  };

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = "海皇娱乐 · Neptune Entertainment";
    }
    let mounted = true;
    setLoading(true);
    setCatalogueError("");
    if (typeof window !== "undefined") {
      window.localStorage.setItem("neptuneDesktopVideoSort", sortMode);
    }
    setCatalogueTotal(0);
    setCatalogueHasMore(true);
    fetchColumnVideoPage({
      surface: "desktop",
      sort: sortMode,
      visitorId: getAccessVisitorId(),
      userId: currentUser?.uid || "",
      limit: 60,
      offset: 0,
    })
      .then((page) => {
        if (!mounted) return;
        setVideos(page.videos);
        setCatalogueTotal(page.total);
        setCatalogueHasMore(page.videos.length < page.total);
      })
      .catch((error) => {
        if (!mounted) return;
        setVideos([]);
        setCatalogueError(
          error instanceof Error ? error.message : "视频目录加载失败",
        );
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [currentUser?.uid, rankingRefresh, sortMode]);

  useEffect(() => {
    let mounted = true;
    fetchSearchKeywordHistory(currentUser, 30)
      .then((history) => {
        if (!mounted) return;
        setSearchHistory({ user: history.user, public: history.public });
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [currentUser?.uid, searchHistoryRefresh]);

  useEffect(() => {
    let mounted = true;
    setSubscriptionsLoading(true);
    setWatchHistoryLoading(true);
    setSubscriptionError("");
    setWatchHistoryError("");
    fetchChannelSubscriptions(currentUser)
      .then((subscriptions) => {
        if (mounted) setChannelSubscriptions(subscriptions);
      })
      .catch((error) => {
        if (mounted) {
          setChannelSubscriptions([]);
          setSubscriptionError(
            error instanceof Error ? error.message : "订阅内容加载失败",
          );
        }
      })
      .finally(() => {
        if (mounted) setSubscriptionsLoading(false);
      });
    fetchViewerWatchHistory(currentUser, 200)
      .then((history) => {
        if (!mounted) return;
        setViewerWatchHistory(history.items);
        setViewerHistoryRetentionDays(history.retentionDays);
      })
      .catch((error) => {
        if (mounted) {
          setViewerWatchHistory([]);
          setWatchHistoryError(
            error instanceof Error ? error.message : "观看记录加载失败",
          );
        }
      })
      .finally(() => {
        if (mounted) setWatchHistoryLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [currentUser?.uid, viewerLibraryRefresh]);

  useEffect(() => {
    if (activeSidebar !== "首页" && activeSidebar !== "收藏夹") return;
    let mounted = true;
    setFavoriteFolderLoading(true);
    setFavoriteFolderError("");
    fetchAllColumnVideos({
      surface: "desktop",
      sort: "favorites",
      visitorId: getAccessVisitorId(),
      userId: currentUser?.uid || "",
      limit: 100,
    })
      .then((items) => {
        if (!mounted) return;
        setFavoriteFolderVideos(items);
      })
      .catch((error) => {
        if (!mounted) return;
        setFavoriteFolderVideos([]);
        setFavoriteFolderError(
          error instanceof Error ? error.message : "收藏夹加载失败",
        );
      })
      .finally(() => mounted && setFavoriteFolderLoading(false));
    return () => {
      mounted = false;
    };
  }, [activeSidebar, currentUser?.uid, favoriteFolderRefresh]);

  // Do not render demo cards while the real catalogue is loading. Rendering the
  // fallback first made the grid briefly show six items and then jump to the
  // actual API count.
  const sourceVideos = videos;
  const deliveryEligibleVideos = useMemo(
    () =>
      sourceVideos.filter((item) =>
        advertisementEligibleForDesktop(
          item,
          sortMode,
          committedQuery,
          activeSearchTag,
        ),
      ),
    [activeSearchTag, committedQuery, sortMode, sourceVideos],
  );
  const videoById = useMemo(
    () => new Map(sourceVideos.map((video) => [video.id, video])),
    [sourceVideos],
  );
  const watchHistoryWithVideos = useMemo(
    () =>
      viewerWatchHistory.flatMap((entry) => {
        const video = videoById.get(entry.videoId);
        return video ? [{ entry, video }] : [];
      }),
    [videoById, viewerWatchHistory],
  );
  const profileHistoryVideos = useMemo(
    () => watchHistoryWithVideos.map(({ video }) => video),
    [watchHistoryWithVideos],
  );
  const profileFavoriteVideos = useMemo(
    () => sourceVideos.filter((item) => item.ranking?.userSignals.favorited),
    [sourceVideos],
  );
  const profileCommentVideos = useMemo(
    () => sourceVideos.filter((item) => item.ranking?.userSignals.commented),
    [sourceVideos],
  );
  const profileVideos =
    profileTab === "favorites"
      ? profileFavoriteVideos
      : profileTab === "comments"
      ? profileCommentVideos
      : profileHistoryVideos;
  const profileWatchSeconds = viewerWatchHistory.reduce(
    (total, item) => total + item.totalWatchSeconds,
    0,
  );
  const profileClickCount = viewerWatchHistory.reduce(
    (total, item) => total + item.openCount,
    0,
  );
  const subscribedChannelIds = useMemo(
    () => new Set(channelSubscriptions.map((item) => item.channelId)),
    [channelSubscriptions],
  );
  const subscriptionVideos = useMemo(
    () =>
      sourceVideos.filter((video) =>
        Boolean(video.channelId && subscribedChannelIds.has(video.channelId)),
      ),
    [sourceVideos, subscribedChannelIds],
  );
  const discoverChannels = useMemo(() => {
    const grouped = new Map<
      string,
      {
        channelId: string;
        channel: string;
        thumbnail: string;
        videoCount: number;
      }
    >();
    for (const video of sourceVideos) {
      if (!video.channelId || subscribedChannelIds.has(video.channelId))
        continue;
      const current = grouped.get(video.channelId);
      if (current) {
        current.videoCount += 1;
      } else {
        grouped.set(video.channelId, {
          channelId: video.channelId,
          channel: video.channel,
          thumbnail: video.thumbnail,
          videoCount: 1,
        });
      }
    }
    return [...grouped.values()]
      .sort(
        (left, right) =>
          right.videoCount - left.videoCount ||
          left.channel.localeCompare(right.channel),
      )
      .slice(0, 12);
  }, [sourceVideos, subscribedChannelIds]);
  const collectionPageSize = 12;
  const subscriptionTotalPages = Math.max(
    1,
    Math.ceil(subscriptionVideos.length / collectionPageSize),
  );
  const historyTotalPages = Math.max(
    1,
    Math.ceil(watchHistoryWithVideos.length / collectionPageSize),
  );
  const favoriteFolderTotalPages = Math.max(
    1,
    Math.ceil(favoriteFolderVideos.length / collectionPageSize),
  );
  const paginatedSubscriptionVideos = subscriptionVideos.slice(
    (subscriptionPage - 1) * collectionPageSize,
    subscriptionPage * collectionPageSize,
  );
  const paginatedWatchHistory = watchHistoryWithVideos.slice(
    (historyPage - 1) * collectionPageSize,
    historyPage * collectionPageSize,
  );
  const paginatedFavoriteFolderVideos = favoriteFolderVideos.slice(
    (favoriteFolderPage - 1) * collectionPageSize,
    favoriteFolderPage * collectionPageSize,
  );
  const derivedUserSearchTags = useMemo(
    () => buildRelatedSearchTags(sourceVideos, committedQuery, "user", 12),
    [committedQuery, sourceVideos],
  );
  const derivedPublicSearchTags = useMemo(
    () => buildRelatedSearchTags(sourceVideos, committedQuery, "public", 12),
    [committedQuery, sourceVideos],
  );
  const userSearchTags = useMemo(
    () =>
      mergeSearchHistoryTags(
        derivedUserSearchTags,
        searchHistory.user,
        committedQuery,
        6,
      ),
    [committedQuery, derivedUserSearchTags, searchHistory.user],
  );
  const publicSearchTagCandidates = useMemo(
    () =>
      mergeSearchHistoryTags(
        derivedPublicSearchTags,
        searchHistory.public,
        committedQuery,
        12,
      ),
    [committedQuery, derivedPublicSearchTags, searchHistory.public],
  );
  const publicSearchTags = useMemo(() => {
    const userKeys = new Set(
      userSearchTags.map((tag) => searchTagDedupKey(tag.label)),
    );
    return publicSearchTagCandidates
      .filter((tag) => !userKeys.has(searchTagDedupKey(tag.label)))
      .slice(0, 6);
  }, [publicSearchTagCandidates, userSearchTags]);
  const inputSuggestions = useMemo(
    () => buildSearchSuggestions(sourceVideos, searchHistory, query),
    [query, searchHistory, sourceVideos],
  );
  const filteredVideos = useMemo(() => {
    return deliveryEligibleVideos.filter((item) => {
      const queryMatches = videoMatchesSearch(item, committedQuery);
      const searchTagMatches =
        !activeSearchTag || videoMatchesSearch(item, activeSearchTag);
      return queryMatches && searchTagMatches;
    });
  }, [activeSearchTag, committedQuery, deliveryEligibleVideos]);
  const compactSidebar = width < 1180 && !sidebarExpanded;
  const sidebarWidth = compactSidebar ? (width >= 720 ? 88 : 64) : 232;
  // Reserve the grid padding plus the desktop scrollbar width so the last
  // card fits instead of wrapping unexpectedly when the browser narrows.
  const contentWidth = Math.max(240, width - sidebarWidth - 64);
  const columnCount =
    contentWidth >= 1180
      ? 4
      : contentWidth >= 860
      ? 3
      : contentWidth >= 560
      ? 2
      : 1;
  const gap = 16;
  const cardWidth = Math.floor(
    (contentWidth - gap * (columnCount - 1)) / columnCount,
  );
  const listGap = listAppearance.layout === "compact" ? 8 : 16;
  const desiredCardWidth = {
    small: 220,
    medium: 300,
    large: 420,
    original: cardWidth,
  }[listAppearance.size];
  const listColumns =
    listAppearance.layout === "list"
      ? 1
      : listAppearance.size === "original"
      ? columnCount
      : Math.max(
          1,
          Math.floor(
            (contentWidth + listGap) /
              (desiredCardWidth *
                (listAppearance.layout === "compact" ? 0.8 : 1) +
                listGap),
          ),
        );
  const listCardWidth = Math.floor(
    (contentWidth - listGap * (listColumns - 1)) / listColumns,
  );
  const estimatedRowHeight =
    listAppearance.layout === "list"
      ? Math.max(
          170,
          (Math.min(desiredCardWidth, listCardWidth * 0.42) * 9) / 16,
        )
      : (listCardWidth * 9) / 16 +
        (listAppearance.layout === "compact" ? 6 : 170);
  const visibleRows = Math.max(
    1,
    Math.ceil(Math.max(240, height - 240) / (estimatedRowHeight + listGap)),
  );
  const pageSize = listColumns * visibleRows;
  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / pageSize));
  const paginatedVideos = useMemo(
    () => filteredVideos.slice(0, currentPage * pageSize),
    [currentPage, filteredVideos, pageSize],
  );

  useEffect(() => {
    if (activeSidebar !== "首页") return;
    const policy = getDesktopCachePolicy().policy;
    const requested = Math.max(
      0,
      Math.min(10, Number(policy.prefetchTracks) || 0),
    );
    if (!policy.enabled || requested === 0) return;

    const candidateLimit = requested * 5;
    const favorites = favoriteFolderVideos.slice(
      0,
      Math.min(20, candidateLimit),
    );
    const visible = paginatedVideos.slice(
      0,
      Math.min(20, candidateLimit),
    );
    const visibleIds = new Set(paginatedVideos.map((video) => video.id));
    const upcoming = filteredVideos
      .filter((video) => !visibleIds.has(video.id))
      .slice(0, candidateLimit);
    const seen = new Set<string>();
    const urls = [...favorites, ...visible, ...upcoming].flatMap((video) => {
      if (seen.has(video.id)) return [];
      seen.add(video.id);
      const url = hoverPreviewUrl(video);
      return url ? [url] : [];
    }).slice(0, candidateLimit);
    if (!urls.length) return;

    const timer = window.setTimeout(() => {
      void prefetchMediaUrls(urls);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [
    activeSidebar,
    cachePolicyVersion,
    favoriteFolderVideos,
    filteredVideos,
    paginatedVideos,
  ]);

  const hasMoreLoadedVideos = paginatedVideos.length < filteredVideos.length;
  const hasMoreVideos = hasMoreLoadedVideos || catalogueHasMore;
  const markPageVisited = (page: number) => {
    setVisiblePage(page);
    setVisitedPages((pages) =>
      pages.includes(page) ? pages : [...pages, page].sort((a, b) => a - b),
    );
  };
  const finishPageJump = () => {
    const page = pendingPageRef.current;
    if (page === null || pageOffsetsRef.current[page] === undefined) return;
    listScrollRef.current?.scrollTo({
      y: listGridTopRef.current + pageOffsetsRef.current[page],
      animated: false,
    });
    pendingPageRef.current = null;
    markPageVisited(page);
  };
  const jumpToPage = (page: number) => {
    pendingPageRef.current = page;
    setCurrentPage((loaded) => Math.max(loaded, page));
    requestAnimationFrame(finishPageJump);
  };
  const loadMoreVideos = async () => {
    if (
      loading ||
      catalogueAppending ||
      !hasMoreVideos ||
      appendLockedRef.current
    )
      return;
    appendLockedRef.current = true;
    if (hasMoreLoadedVideos) {
      setCurrentPage((page) => Math.min(totalPages, page + 1));
      return;
    }

    setCatalogueAppending(true);
    setCatalogueError("");
    try {
      const nextPage = await fetchColumnVideoPage({
        surface: "desktop",
        sort: sortMode,
        visitorId: getAccessVisitorId(),
        userId: currentUser?.uid || "",
        limit: 60,
        offset: videos.length,
      });
      setCatalogueTotal(nextPage.total);
      setVideos((current) => {
        const known = new Set(current.map((item) => item.id));
        const additions = nextPage.videos.filter((item) => !known.has(item.id));
        const merged = [...current, ...additions];
        setCatalogueHasMore(
          nextPage.videos.length > 0 && merged.length < nextPage.total,
        );
        return merged;
      });
      setCurrentPage((page) => page + 1);
    } catch (error) {
      setCatalogueError(
        error instanceof Error ? error.message : "下一批视频加载失败",
      );
    } finally {
      appendLockedRef.current = false;
      setCatalogueAppending(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setVisiblePage(1);
    setVisitedPages([1]);
    pageOffsetsRef.current = {};
    pendingPageRef.current = null;
    listScrollRef.current?.scrollTo({ y: 0, animated: false });
  }, [activeSearchTag, committedQuery, sortMode, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (subscriptionPage > subscriptionTotalPages) {
      setSubscriptionPage(subscriptionTotalPages);
    }
  }, [subscriptionPage, subscriptionTotalPages]);

  useEffect(() => {
    if (historyPage > historyTotalPages) setHistoryPage(historyTotalPages);
  }, [historyPage, historyTotalPages]);

  useEffect(() => {
    if (favoriteFolderPage > favoriteFolderTotalPages) {
      setFavoriteFolderPage(favoriteFolderTotalPages);
    }
  }, [favoriteFolderPage, favoriteFolderTotalPages]);

  useEffect(
    () => () => {
      if (suggestionCloseTimerRef.current) {
        clearTimeout(suggestionCloseTimerRef.current);
      }
      if (hoverPreviewTimerRef.current) {
        clearTimeout(hoverPreviewTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (hoverPreviewTimerRef.current) {
      clearTimeout(hoverPreviewTimerRef.current);
      hoverPreviewTimerRef.current = null;
    }
    pendingHoverPreviewIdRef.current = "";
    setHoverPreviewVideoId("");
  }, [
    activeSearchTag,
    activeSidebar,
    committedQuery,
    currentPage,
    historyPage,
    favoriteFolderPage,
    profileTab,
    sortMode,
    subscriptionPage,
  ]);

  const mobileFeedUrl = useMemo(() => {
    if (typeof window === "undefined") return "/";
    const basePath = window.location.pathname.startsWith("/tiktok/")
      ? "/tiktok/"
      : "/";
    return `${window.location.origin}${basePath}?view=mobile&embedded=desktop`;
  }, []);

  const toggleChannelSubscription = async (
    channelId: string,
    channel: string,
  ) => {
    if (!channelId || subscriptionSavingId) return;
    const nextSubscribed = !subscribedChannelIds.has(channelId);
    setSubscriptionSavingId(channelId);
    setSubscriptionError("");
    try {
      await setChannelSubscription(currentUser, channelId, nextSubscribed);
      setChannelSubscriptions((current) =>
        nextSubscribed
          ? [
              { channelId, channel, subscribedAt: new Date().toISOString() },
              ...current.filter((item) => item.channelId !== channelId),
            ]
          : current.filter((item) => item.channelId !== channelId),
      );
      setSubscriptionPage(1);
      setViewerLibraryRefresh((value) => value + 1);
    } catch (error) {
      setSubscriptionError(
        error instanceof Error ? error.message : "订阅操作失败",
      );
    } finally {
      setSubscriptionSavingId("");
    }
  };

  const submitSearch = () => {
    const nextQuery = query.trim();
    setSuggestionsOpen(false);
    setQuery(nextQuery);
    setCommittedQuery(nextQuery);
    setActiveSearchTag("");
    if (nextQuery) {
      void recordAccessEvent(
        {
          event: "search_query",
          page: "desktop-search",
          searchQuery: nextQuery,
        },
        currentUser,
      ).finally(() => setSearchHistoryRefresh((value) => value + 1));
    }
  };
  const clearSearch = () => {
    setSuggestionsOpen(false);
    setQuery("");
    setCommittedQuery("");
    setActiveSearchTag("");
  };
  const handleSearchInputChange = (value: string) => {
    setQuery(value);
    setSuggestionsOpen(Boolean(value.trim()));
  };
  const handleSearchFocus = () => {
    if (suggestionCloseTimerRef.current) {
      clearTimeout(suggestionCloseTimerRef.current);
      suggestionCloseTimerRef.current = null;
    }
    setSuggestionsOpen(Boolean(query.trim()));
  };
  const handleSearchBlur = () => {
    suggestionCloseTimerRef.current = setTimeout(() => {
      setSuggestionsOpen(false);
      suggestionCloseTimerRef.current = null;
    }, 160);
  };
  const chooseSearchSuggestion = (label: string) => {
    if (suggestionCloseTimerRef.current) {
      clearTimeout(suggestionCloseTimerRef.current);
      suggestionCloseTimerRef.current = null;
    }
    setQuery(label);
    setSuggestionsOpen(false);
  };
  const toggleSearchTag = (label: string, strategy: SearchTagStrategy) => {
    const activating =
      normalizeSearchText(activeSearchTag) !== normalizeSearchText(label);
    setActiveSearchTag(activating ? label : "");
    if (!activating) return;
    void recordAccessEvent(
      {
        event: "search_query",
        page: `desktop-keyword-tag-${strategy}`,
        searchQuery: label,
      },
      currentUser,
    ).finally(() => setSearchHistoryRefresh((value) => value + 1));
  };
  const stopAllHoverPreviews = () => {
    if (hoverPreviewTimerRef.current) {
      clearTimeout(hoverPreviewTimerRef.current);
      hoverPreviewTimerRef.current = null;
    }
    pendingHoverPreviewIdRef.current = "";
    setHoverPreviewVideoId("");
  };
  const scheduleHoverPreview = (videoId: string) => {
    if (hoverPreviewTimerRef.current) {
      clearTimeout(hoverPreviewTimerRef.current);
    }
    pendingHoverPreviewIdRef.current = videoId;
    hoverPreviewTimerRef.current = setTimeout(() => {
      if (pendingHoverPreviewIdRef.current === videoId) {
        setHoverPreviewVideoId(videoId);
      }
      hoverPreviewTimerRef.current = null;
    }, 500);
  };
  const stopHoverPreview = (videoId: string) => {
    if (pendingHoverPreviewIdRef.current === videoId) {
      pendingHoverPreviewIdRef.current = "";
      if (hoverPreviewTimerRef.current) {
        clearTimeout(hoverPreviewTimerRef.current);
        hoverPreviewTimerRef.current = null;
      }
    }
    setHoverPreviewVideoId((current) => (current === videoId ? "" : current));
  };
  const openSelectedVideo = (video: ColumnVideo) => {
    stopAllHoverPreviews();
    if (video.vipLocked) {
      setAccountNotice(
        "该位置是 VIP 专属视频占位。注册会员升级为 VIP 后，封面、标题和播放内容才会显示。",
      );
      switchAccountMode(currentUser ? "login" : "register");
      setAccountOpen(true);
      return;
    }
    setPlayerWindowNotice("");
    if (playerOpenMode === "window" && !standalonePlayer) {
      const url = new URL(window.location.href);
      url.searchParams.set("video", video.id);
      url.searchParams.set("playerWindow", "1");
      const popup = window.open(url.toString(), "_blank");
      if (popup) {
        setSelectedVideo(null);
        popup.focus();
        return;
      }
      setPlayerWindowNotice(
        "新窗口被浏览器拦截，已在当前页面播放。允许弹出窗口后可使用新窗口模式。",
      );
    }
    if (selectedVideo && selectedVideo.id !== video.id) {
      selectedVideoHistoryRef.current.push(selectedVideo.id);
    }
    setSelectedVideo(video);
  };
  const closeSelectedVideo = () => {
    if (standalonePlayer) window.close();
    selectedVideoHistoryRef.current = [];
    setSelectedVideo(null);
    setRankingRefresh((value) => value + 1);
  };
  const playbackPoolForVideo = (current: ColumnVideo) => {
    const filteredPoolContainsCurrent = filteredVideos.some(
      (video) => video.id === current.id,
    );
    return filteredPoolContainsCurrent && filteredVideos.length > 1
      ? filteredVideos
      : deliveryEligibleVideos;
  };
  const intelligentCachePlan = useMemo(() => {
    if (!selectedVideo || loopMode === "single") return [];
    const cachePolicy = getDesktopCachePolicy().policy;
    const requested = adaptivePrefetchCount(
      cachePolicy,
      Math.max(0, Math.min(10, Number(cachePolicy.prefetchTracks) || 0)),
    );
    if (!cachePolicy.enabled || requested === 0) return [];
    const filteredPoolContainsCurrent = filteredVideos.some(
      (video) => video.id === selectedVideo.id,
    );
    const pool = filteredPoolContainsCurrent && filteredVideos.length > 1
      ? filteredVideos
      : deliveryEligibleVideos;
    const ranked = rankPlaybackCandidates(
      pool,
      selectedVideo,
      smartPlaybackPolicy,
      selectedVideoHistoryRef.current,
    );
    const network = browserPlaybackNetwork();
    return buildIntelligentCachePlan(
      ranked,
      requested,
      network.bandwidthBps,
      network.saveData,
    );
  }, [
    cachePolicyVersion,
    deliveryEligibleVideos,
    filteredVideos,
    loopMode,
    selectedVideo,
    smartPlaybackPolicy,
  ]);
  useEffect(() => {
    if (!selectedVideo || loopMode === "single") return;
    const policy = getDesktopCachePolicy().policy;
    const requested = Math.max(0, Math.min(10, Number(policy.prefetchTracks) || 0));
    if (!policy.enabled || requested === 0) return;
    if (!intelligentCachePlan.length) return;
    const owner = playbackPreferenceOwner(currentUser?.uid, getAccessVisitorId());
    const preference = readPlaybackVariantPreference(owner);
    const urls: string[] = [];
    const segmentLimits: number[] = [];
    intelligentCachePlan.forEach((target) => {
      const current = target.video;
      const variants = (current.mediaVariants || []).filter(
        (variant) => variant.kind !== "audio",
      ).map(mergeLandscapeVariantIntoDesktop);
      const targetVariant = target.quality === "原画"
        ? undefined
        : variants.find((variant) =>
            `${variant.resolution} ${variant.quality} ${variant.label}`
              .toLowerCase()
              .includes(target.quality.toLowerCase()),
          );
      const variant = targetVariant || (
        preference.videoMode === "variant"
          ? matchPlaybackVariantPreference(variants, preference.video)
          : preferredPlaybackVariant(
              variants,
              current.desktopVideoUrl || current.videoUrl,
              "desktop",
            )
      );
      const url = variant?.url || current.desktopVideoUrl || current.videoUrl;
      if (url) {
        urls.push(url);
        segmentLimits.push(target.segmentCount);
      }
    });
    if (urls.length) {
      void prefetchMediaUrls(
        urls,
        segmentLimits,
      );
    }
  }, [
    cachePolicyVersion,
    currentUser?.uid,
    intelligentCachePlan,
    loopMode,
    selectedVideo?.id,
  ]);
  const advanceSelectedVideo = (mode: LoopMode) => {
    if (!selectedVideo) return;
    const nextVideo =
      getNextDesktopVideo(
        playbackPoolForVideo(selectedVideo),
        selectedVideo.id,
        mode,
        smartPlaybackPolicy,
        selectedVideoHistoryRef.current,
      ) || selectedVideo;
    if (nextVideo.id === selectedVideo.id) return;
    selectedVideoHistoryRef.current.push(selectedVideo.id);
    setSelectedVideo(nextVideo);
  };
  const playPreviousSelectedVideo = () => {
    if (!selectedVideo) return;
    let previousVideo: ColumnVideo | undefined;
    while (selectedVideoHistoryRef.current.length && !previousVideo) {
      const previousId = selectedVideoHistoryRef.current.pop();
      if (previousId) previousVideo = videoById.get(previousId);
    }
    previousVideo ||=
      getPreviousDesktopVideo(
        playbackPoolForVideo(selectedVideo),
        selectedVideo.id,
        loopMode === "single" ? "sequence" : loopMode,
        smartPlaybackPolicy,
      ) || undefined;
    if (!previousVideo || previousVideo.id === selectedVideo.id) return;
    setSelectedVideo(previousVideo);
  };

  return (
    <View style={styles.page}>
      {!standalonePlayer && (
        <>
          <View
            testID="desktop-sidebar"
            style={[styles.sidebar, { width: sidebarWidth }]}
          >
            <View style={styles.sidebarHeader}>
              {width < 1180 ? (
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={
                    compactSidebar ? "展开推荐侧栏" : "收起推荐侧栏"
                  }
                  accessibilityState={{ expanded: !compactSidebar }}
                  style={styles.sidebarToggle}
                  onPress={() => setSidebarExpanded((expanded) => !expanded)}
                >
                  <Feather
                    name={compactSidebar ? "menu" : "chevrons-left"}
                    size={22}
                    color="#c8c3ba"
                  />
                  {!compactSidebar && (
                    <Text style={styles.sidebarToggleText}>收起侧栏</Text>
                  )}
                </Pressable>
              ) : (
                <Brand onPress={() => setAndroidDownloadOpen(true)} />
              )}
            </View>
            <ScrollView
              style={styles.sidebarScroll}
              contentContainerStyle={styles.sidebarScrollContent}
              showsVerticalScrollIndicator
            >
              <View style={styles.sidebarSection}>
                {sidebar.map(([icon, label]) => (
                  <SidebarItem
                    key={label}
                    icon={icon}
                    label={compactSidebar ? "" : label}
                    accessibilityLabel={label}
                    active={activeSidebar === label}
                    onPress={() => {
                      if (label === "观看记录") setHistoryPage(1);
                      if (label === "订阅内容") setSubscriptionPage(1);
                      if (label === "收藏夹") setFavoriteFolderPage(1);
                      setActiveSidebar(label);
                    }}
                  />
                ))}
              </View>
              <View style={styles.divider} />
              {compactSidebar ? (
                <SidebarItem
                  icon="sliders"
                  label=""
                  accessibilityLabel="展开热门推荐和电脑版推荐"
                  onPress={() => setSidebarExpanded(true)}
                />
              ) : (
                <>
                  <View
                    testID="sidebar-popular-recommendations"
                    style={styles.sidebarRecommendationSection}
                  >
                    <Text style={styles.sectionTitle}>热门推荐</Text>
                    <View style={styles.sidebarFilterGrid}>
                      {committedQuery ? (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={`当前搜索关键词：${committedQuery}`}
                          accessibilityState={{ selected: !activeSearchTag }}
                          onPress={() => {
                            setActiveSidebar("首页");
                            setActiveSearchTag("");
                          }}
                          style={[
                            styles.sidebarFilterChip,
                            styles.sidebarSearchChip,
                            !activeSearchTag && styles.sortChipActive,
                          ]}
                        >
                          <Text
                            style={styles.sidebarFilterLabel}
                            numberOfLines={1}
                          >
                            搜索：{committedQuery}
                          </Text>
                        </Pressable>
                      ) : null}
                      {(
                        [
                          { strategy: "user", tags: userSearchTags },
                          { strategy: "public", tags: publicSearchTags },
                        ] as const
                      ).map(({ strategy, tags }) =>
                        tags.map((tag) => {
                          const active =
                            normalizeSearchText(activeSearchTag) ===
                            normalizeSearchText(tag.label);
                          return (
                            <Pressable
                              key={`${strategy}-${tag.key}`}
                              accessibilityRole="button"
                              accessibilityLabel={`${
                                strategy === "user" ? "用户" : "公共"
                              }策略历史关键词：${
                                tag.label
                              }，点击后计入搜索次数`}
                              accessibilityState={{ selected: active }}
                              onPress={() => {
                                setActiveSidebar("首页");
                                toggleSearchTag(tag.label, strategy);
                              }}
                              style={[
                                styles.sidebarFilterChip,
                                active && styles.sortChipActive,
                              ]}
                            >
                              <Text
                                numberOfLines={1}
                                style={[
                                  styles.sidebarFilterLabel,
                                  active && styles.sortLabelActive,
                                ]}
                              >
                                {tag.label}
                              </Text>
                            </Pressable>
                          );
                        }),
                      )}
                      {!userSearchTags.length && !publicSearchTags.length ? (
                        <Text style={styles.sidebarFilterEmpty}>
                          暂无推荐关键词
                        </Text>
                      ) : null}
                    </View>
                  </View>
                  <View style={styles.divider} />
                  <View
                    testID="sidebar-desktop-recommendations"
                    style={styles.sidebarRecommendationSection}
                  >
                    <Text style={styles.sectionTitle}>电脑版推荐</Text>
                    <View style={styles.sidebarFilterGrid}>
                      {sortModes.map((mode) => {
                        const active = mode.key === sortMode;
                        return (
                          <Pressable
                            key={mode.key}
                            accessibilityRole="button"
                            accessibilityLabel={`${mode.group}：${mode.label}`}
                            accessibilityState={{ selected: active }}
                            onPress={() => {
                              setActiveSidebar("首页");
                              setSortMode(mode.key);
                            }}
                            style={[
                              styles.sidebarFilterChip,
                              active && styles.sortChipActive,
                            ]}
                          >
                            <Text
                              numberOfLines={1}
                              style={[
                                styles.sidebarFilterLabel,
                                active && styles.sortLabelActive,
                              ]}
                            >
                              {mode.label}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                  <View style={styles.divider} />
                  {activeSidebar === "首页" && filteredVideos.length > 0 && (
                    <View style={{ padding: 12, gap: 10 }}>
                      <Text style={styles.paginationSummary}>
                        当前第 {visiblePage} / {totalPages} 页 · 共{" "}
                        {filteredVideos.length} 个视频
                      </Text>
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: 5,
                        }}
                      >
                        {[
                          [1, "首页"],
                          ...Array.from(
                            {
                              length:
                                Math.min(totalPages, visiblePage + 4) -
                                Math.max(1, visiblePage - 5) +
                                1,
                            },
                            (_, i) => {
                              const page = Math.max(1, visiblePage - 5) + i;
                              return [page, String(page)];
                            },
                          ),
                          [totalPages, "末页"],
                        ].map(([page, label], index) => (
                          <Pressable
                            key={`${label}-${index}`}
                            onPress={() => jumpToPage(Number(page))}
                            style={[
                              styles.pageNumber,
                              Number(page) === visiblePage &&
                                styles.pageNumberActive,
                            ]}
                          >
                            <Text
                              style={[
                                styles.pageNumberText,
                                Number(page) === visiblePage &&
                                  styles.pageNumberTextActive,
                              ]}
                            >
                              {label}
                            </Text>
                          </Pressable>
                        ))}
                      </View>
                      <Text style={styles.paginationSummary}>浏览过的页</Text>
                      {createElement(
                        "select",
                        {
                          "aria-label": "浏览过的页",
                          value: "",
                          onChange: (event: { target: { value: string } }) => {
                            const page = Number(event.target.value);
                            if (visitedPages.includes(page)) jumpToPage(page);
                          },
                          style: {
                            width: "100%",
                            background: "#0b211a",
                            color: "#bff7e6",
                            border: "1px solid #34745f",
                            borderRadius: 8,
                            padding: "8px 12px",
                            cursor: "pointer",
                          },
                        },
                        createElement(
                          "option",
                          { value: "", disabled: true },
                          "点击选择浏览过的页",
                        ),
                        ...visitedPages.map((page) =>
                          createElement(
                            "option",
                            { key: page, value: String(page) },
                            `第 ${page} 页${
                              page === visiblePage ? "（当前页）" : ""
                            }`,
                          ),
                        ),
                      )}
                    </View>
                  )}
                  <Text style={styles.footerCopy}>
                    关于 · 隐私 · 条款{`\n`}© 2026 海皇娱乐{`\n`}字体：MiSans ·
                    Inter
                  </Text>
                  <View
                    style={{
                      flexDirection: "column",
                      flexWrap: "wrap",
                      alignItems: "stretch",
                      gap: 12,
                      paddingHorizontal: 12,
                      paddingVertical: 14,
                    }}
                  >
                    {createElement(
                      "label",
                      {
                        style: {
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          color: "#bff7e6",
                          fontSize: 14,
                        },
                      },
                      "排列方式",
                      createElement(
                        "select",
                        {
                          "aria-label": "视频列表排列方式",
                          value: listAppearance.layout,
                          onChange: (event: { target: { value: string } }) =>
                            setListAppearance((current) => ({
                              ...current,
                              layout: event.target
                                .value as typeof current.layout,
                            })),
                          style: {
                            background: "#0b211a",
                            color: "#bff7e6",
                            border: "1px solid #34745f",
                            borderRadius: 8,
                            padding: "8px 12px",
                          },
                        },
                        ...(
                          [
                            ["grid", "标准网格"],
                            ["compact", "紧凑网格"],
                            ["list", "横向列表"],
                          ] as const
                        ).map(([value, label]) =>
                          createElement("option", { key: value, value }, label),
                        ),
                      ),
                    )}
                    {createElement(
                      "label",
                      {
                        style: {
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          color: "#bff7e6",
                          fontSize: 14,
                        },
                      },
                      "单元大小",
                      createElement(
                        "select",
                        {
                          "aria-label": "视频列表单元大小",
                          value: listAppearance.size,
                          onChange: (event: { target: { value: string } }) =>
                            setListAppearance((current) => ({
                              ...current,
                              size: event.target.value as typeof current.size,
                            })),
                          style: {
                            background: "#0b211a",
                            color: "#bff7e6",
                            border: "1px solid #34745f",
                            borderRadius: 8,
                            padding: "8px 12px",
                          },
                        },
                        ...(
                          [
                            ["small", "小"],
                            ["medium", "中"],
                            ["large", "大"],
                            ["original", "原始大尺寸"],
                          ] as const
                        ).map(([value, label]) =>
                          createElement("option", { key: value, value }, label),
                        ),
                      ),
                    )}
                  </View>
                </>
              )}
            </ScrollView>
          </View>

          <View style={styles.main}>
            {activeSidebar === "短视频" ? (
              <View style={styles.mobileFeedEmbed}>
                {createElement("iframe", {
                  src: mobileFeedUrl,
                  title: "海皇娱乐手机版短视频",
                  allow:
                    "autoplay; fullscreen; picture-in-picture; encrypted-media",
                  allowFullScreen: true,
                  style: {
                    width: "100%",
                    height: "100%",
                    border: 0,
                    display: "block",
                    backgroundColor: "#050505",
                  },
                })}
              </View>
            ) : activeSidebar === "订阅内容" ? (
              <ScrollView
                style={styles.desktopLibraryScroll}
                contentContainerStyle={styles.desktopLibraryContent}
                showsVerticalScrollIndicator
              >
                <View style={styles.desktopLibraryHero}>
                  <View style={styles.desktopLibraryHeroCopy}>
                    <View style={styles.desktopLibraryHeroIcon}>
                      <Feather name="youtube" size={28} color="#17120b" />
                    </View>
                    <View style={styles.desktopLibraryHeroText}>
                      <Text style={styles.desktopLibraryTitle}>订阅内容</Text>
                      <Text style={styles.desktopLibrarySubtitle}>
                        订阅喜欢的频道，新内容会集中显示在这里。
                      </Text>
                    </View>
                  </View>
                  <View style={styles.desktopLibraryHeroActions}>
                    <View style={styles.desktopLibrarySummaryBadge}>
                      <Text style={styles.desktopLibrarySummaryValue}>
                        {channelSubscriptions.length}
                      </Text>
                      <Text style={styles.desktopLibrarySummaryLabel}>
                        个频道
                      </Text>
                    </View>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="刷新订阅内容"
                      style={styles.desktopLibraryRefreshButton}
                      onPress={() =>
                        setViewerLibraryRefresh((value) => value + 1)
                      }
                    >
                      <Feather name="refresh-cw" size={16} color="#e6c88f" />
                      <Text style={styles.desktopLibraryRefreshText}>刷新</Text>
                    </Pressable>
                  </View>
                </View>

                {subscriptionError || catalogueError ? (
                  <View style={styles.desktopLibraryError}>
                    <Feather name="alert-circle" size={17} color="#ff9c9c" />
                    <Text style={styles.desktopLibraryErrorText}>
                      {subscriptionError || catalogueError}
                    </Text>
                  </View>
                ) : null}

                <View style={styles.desktopLibrarySectionHeader}>
                  <View>
                    <Text style={styles.desktopLibrarySectionTitle}>
                      已订阅频道
                    </Text>
                    <Text style={styles.desktopLibrarySectionSubtitle}>
                      点击频道右侧按钮可以取消订阅。
                    </Text>
                  </View>
                </View>
                {channelSubscriptions.length > 0 ? (
                  <View style={styles.subscribedChannelList}>
                    {channelSubscriptions.map((item) => (
                      <View
                        key={item.channelId}
                        style={styles.subscribedChannelChip}
                      >
                        <View style={styles.subscribedChannelAvatar}>
                          <Text style={styles.subscribedChannelAvatarText}>
                            {item.channel.trim().slice(0, 1).toUpperCase() ||
                              "本"}
                          </Text>
                        </View>
                        <Text
                          style={styles.subscribedChannelName}
                          numberOfLines={1}
                        >
                          {item.channel}
                        </Text>
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel={`取消订阅 ${item.channel}`}
                          disabled={Boolean(subscriptionSavingId)}
                          style={[
                            styles.subscribedChannelRemove,
                            Boolean(subscriptionSavingId) &&
                              styles.subscriptionButtonDisabled,
                          ]}
                          onPress={() =>
                            void toggleChannelSubscription(
                              item.channelId,
                              item.channel,
                            )
                          }
                        >
                          <Feather name="x" size={15} color="#b9afa1" />
                        </Pressable>
                      </View>
                    ))}
                  </View>
                ) : !subscriptionsLoading &&
                  !subscriptionError &&
                  !catalogueError ? (
                  <Text style={styles.desktopLibraryInlineEmpty}>
                    还没有订阅频道，可从下方推荐频道开始。
                  </Text>
                ) : null}

                <View style={styles.desktopLibrarySectionHeader}>
                  <View>
                    <Text style={styles.desktopLibrarySectionTitle}>
                      订阅视频
                    </Text>
                    <Text style={styles.desktopLibrarySectionSubtitle}>
                      共 {subscriptionVideos.length} 个视频 · 第{" "}
                      {subscriptionPage}/{subscriptionTotalPages} 页
                    </Text>
                  </View>
                </View>

                {subscriptionsLoading || loading ? (
                  <Text style={styles.loadingText}>正在加载订阅内容...</Text>
                ) : subscriptionError ||
                  catalogueError ? null : paginatedSubscriptionVideos.length >
                  0 ? (
                  <>
                    <View style={[styles.grid, styles.desktopLibraryVideoGrid]}>
                      {paginatedSubscriptionVideos.map((item) => (
                        <VideoCard
                          key={`subscription-${item.id}`}
                          item={item}
                          width={cardWidth}
                          contextLabel={`已订阅 · ${item.channel}`}
                          previewActive={hoverPreviewVideoId === item.id}
                          onHoverIn={() => scheduleHoverPreview(item.id)}
                          onHoverOut={() => stopHoverPreview(item.id)}
                          onPress={() => openSelectedVideo(item)}
                        />
                      ))}
                    </View>
                    <View style={styles.desktopLibraryPagination}>
                      <Pressable
                        disabled={subscriptionPage === 1}
                        style={[
                          styles.pageButton,
                          subscriptionPage === 1 && styles.pageButtonDisabled,
                        ]}
                        onPress={() =>
                          setSubscriptionPage((page) => Math.max(1, page - 1))
                        }
                      >
                        <Text style={styles.pageButtonText}>上一页</Text>
                      </Pressable>
                      <Text style={styles.desktopLibraryPageSummary}>
                        第 {subscriptionPage}/{subscriptionTotalPages} 页
                      </Text>
                      <Pressable
                        disabled={subscriptionPage === subscriptionTotalPages}
                        style={[
                          styles.pageButton,
                          subscriptionPage === subscriptionTotalPages &&
                            styles.pageButtonDisabled,
                        ]}
                        onPress={() =>
                          setSubscriptionPage((page) =>
                            Math.min(subscriptionTotalPages, page + 1),
                          )
                        }
                      >
                        <Text style={styles.pageButtonText}>下一页</Text>
                      </Pressable>
                    </View>
                  </>
                ) : (
                  <View style={styles.desktopLibraryEmpty}>
                    <Feather name="youtube" size={45} color="#756a5c" />
                    <Text style={styles.desktopLibraryEmptyTitle}>
                      暂无订阅视频
                    </Text>
                    <Text style={styles.desktopLibraryEmptyText}>
                      订阅下方频道，或打开任意视频后点击“订阅频道”。
                    </Text>
                    <Pressable
                      style={styles.desktopProfileEmptyButton}
                      onPress={() => setActiveSidebar("首页")}
                    >
                      <Text style={styles.desktopProfileEmptyButtonText}>
                        去首页发现视频
                      </Text>
                    </Pressable>
                  </View>
                )}

                {discoverChannels.length > 0 ? (
                  <>
                    <View style={styles.desktopLibrarySectionHeader}>
                      <View>
                        <Text style={styles.desktopLibrarySectionTitle}>
                          发现频道
                        </Text>
                        <Text style={styles.desktopLibrarySectionSubtitle}>
                          从当前视频库中选择想订阅的频道。
                        </Text>
                      </View>
                    </View>
                    <View style={styles.channelDiscoveryGrid}>
                      {discoverChannels.map((channel) => (
                        <View
                          key={channel.channelId}
                          style={styles.channelDiscoveryCard}
                        >
                          <Image
                            source={{ uri: channel.thumbnail }}
                            style={styles.channelDiscoveryImage}
                            resizeMode="cover"
                          />
                          <View style={styles.channelDiscoveryCopy}>
                            <Text
                              style={styles.channelDiscoveryName}
                              numberOfLines={1}
                            >
                              {channel.channel}
                            </Text>
                            <Text style={styles.channelDiscoveryMeta}>
                              {channel.videoCount} 个视频
                            </Text>
                          </View>
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel={`订阅 ${channel.channel}`}
                            disabled={Boolean(subscriptionSavingId)}
                            style={[
                              styles.channelDiscoverySubscribe,
                              Boolean(subscriptionSavingId) &&
                                styles.subscriptionButtonDisabled,
                            ]}
                            onPress={() =>
                              void toggleChannelSubscription(
                                channel.channelId,
                                channel.channel,
                              )
                            }
                          >
                            <Feather name="plus" size={15} color="#17120b" />
                            <Text style={styles.channelDiscoverySubscribeText}>
                              {subscriptionSavingId === channel.channelId
                                ? "处理中"
                                : "订阅"}
                            </Text>
                          </Pressable>
                        </View>
                      ))}
                    </View>
                  </>
                ) : null}
              </ScrollView>
            ) : activeSidebar === "收藏夹" ? (
              <ScrollView
                style={styles.desktopLibraryScroll}
                contentContainerStyle={styles.desktopLibraryContent}
                showsVerticalScrollIndicator
              >
                <View style={styles.desktopLibraryHero}>
                  <View style={styles.desktopLibraryHeroCopy}>
                    <View style={styles.desktopLibraryHeroIcon}>
                      <Feather name="bookmark" size={27} color="#17120b" />
                    </View>
                    <View style={styles.desktopLibraryHeroText}>
                      <Text style={styles.desktopLibraryTitle}>收藏夹</Text>
                      <Text style={styles.desktopLibrarySubtitle}>
                        收藏的视频集中保存在这里，并跟随当前账号或访客身份。
                      </Text>
                    </View>
                  </View>
                  <View style={styles.desktopLibraryHeroActions}>
                    <View style={styles.desktopLibrarySummaryBadge}>
                      <Text style={styles.desktopLibrarySummaryValue}>
                        {favoriteFolderVideos.length}
                      </Text>
                      <Text style={styles.desktopLibrarySummaryLabel}>
                        个视频
                      </Text>
                    </View>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="刷新收藏夹"
                      style={styles.desktopLibraryRefreshButton}
                      onPress={() =>
                        setFavoriteFolderRefresh((value) => value + 1)
                      }
                    >
                      <Feather name="refresh-cw" size={16} color="#e6c88f" />
                      <Text style={styles.desktopLibraryRefreshText}>刷新</Text>
                    </Pressable>
                  </View>
                </View>

                {favoriteFolderError ? (
                  <View style={styles.desktopLibraryError}>
                    <Feather name="alert-circle" size={17} color="#ff9c9c" />
                    <Text style={styles.desktopLibraryErrorText}>
                      {favoriteFolderError}
                    </Text>
                  </View>
                ) : null}

                <View style={styles.desktopLibrarySectionHeader}>
                  <View>
                    <Text style={styles.desktopLibrarySectionTitle}>
                      已收藏视频
                    </Text>
                    <Text style={styles.desktopLibrarySectionSubtitle}>
                      共 {favoriteFolderVideos.length} 个视频 · 第{" "}
                      {favoriteFolderPage}/{favoriteFolderTotalPages} 页
                    </Text>
                  </View>
                </View>

                {favoriteFolderLoading ? (
                  <Text style={styles.loadingText}>正在加载收藏夹...</Text>
                ) : favoriteFolderError ? null : paginatedFavoriteFolderVideos.length >
                  0 ? (
                  <>
                    <View style={[styles.grid, styles.desktopLibraryVideoGrid]}>
                      {paginatedFavoriteFolderVideos.map((item) => (
                        <VideoCard
                          key={`favorite-folder-${item.id}`}
                          item={item}
                          width={cardWidth}
                          contextLabel="已收藏"
                          previewActive={hoverPreviewVideoId === item.id}
                          onHoverIn={() => scheduleHoverPreview(item.id)}
                          onHoverOut={() => stopHoverPreview(item.id)}
                          onPress={() => openSelectedVideo(item)}
                        />
                      ))}
                    </View>
                    <View style={styles.desktopLibraryPagination}>
                      <Pressable
                        disabled={favoriteFolderPage === 1}
                        style={[
                          styles.pageButton,
                          favoriteFolderPage === 1 && styles.pageButtonDisabled,
                        ]}
                        onPress={() =>
                          setFavoriteFolderPage((page) => Math.max(1, page - 1))
                        }
                      >
                        <Text style={styles.pageButtonText}>上一页</Text>
                      </Pressable>
                      <Text style={styles.desktopLibraryPageSummary}>
                        第 {favoriteFolderPage}/{favoriteFolderTotalPages} 页
                      </Text>
                      <Pressable
                        disabled={
                          favoriteFolderPage === favoriteFolderTotalPages
                        }
                        style={[
                          styles.pageButton,
                          favoriteFolderPage === favoriteFolderTotalPages &&
                            styles.pageButtonDisabled,
                        ]}
                        onPress={() =>
                          setFavoriteFolderPage((page) =>
                            Math.min(favoriteFolderTotalPages, page + 1),
                          )
                        }
                      >
                        <Text style={styles.pageButtonText}>下一页</Text>
                      </Pressable>
                    </View>
                  </>
                ) : (
                  <View style={styles.desktopLibraryEmpty}>
                    <Feather name="bookmark" size={45} color="#756a5c" />
                    <Text style={styles.desktopLibraryEmptyTitle}>
                      收藏夹还是空的
                    </Text>
                    <Text style={styles.desktopLibraryEmptyText}>
                      打开任意视频并点击“收藏”，它就会出现在这里。
                    </Text>
                    <Pressable
                      style={styles.desktopProfileEmptyButton}
                      onPress={() => setActiveSidebar("首页")}
                    >
                      <Text style={styles.desktopProfileEmptyButtonText}>
                        去首页收藏视频
                      </Text>
                    </Pressable>
                  </View>
                )}
              </ScrollView>
            ) : activeSidebar === "观看记录" ? (
              <ScrollView
                style={styles.desktopLibraryScroll}
                contentContainerStyle={styles.desktopLibraryContent}
                showsVerticalScrollIndicator
              >
                <View style={styles.desktopLibraryHero}>
                  <View style={styles.desktopLibraryHeroCopy}>
                    <View
                      style={[
                        styles.desktopLibraryHeroIcon,
                        styles.desktopHistoryHeroIcon,
                      ]}
                    >
                      <Feather name="clock" size={27} color="#061719" />
                    </View>
                    <View style={styles.desktopLibraryHeroText}>
                      <Text style={styles.desktopLibraryTitle}>观看记录</Text>
                      <Text style={styles.desktopLibrarySubtitle}>
                        按最后观看时间排列，并保留上次播放位置。
                      </Text>
                    </View>
                  </View>
                  <View style={styles.desktopLibraryHeroActions}>
                    <View style={styles.desktopLibrarySummaryBadge}>
                      <Text style={styles.desktopLibrarySummaryValue}>
                        {watchHistoryWithVideos.length}
                      </Text>
                      <Text style={styles.desktopLibrarySummaryLabel}>
                        个视频
                      </Text>
                    </View>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="刷新观看记录"
                      style={styles.desktopLibraryRefreshButton}
                      onPress={() =>
                        setViewerLibraryRefresh((value) => value + 1)
                      }
                    >
                      <Feather name="refresh-cw" size={16} color="#e6c88f" />
                      <Text style={styles.desktopLibraryRefreshText}>刷新</Text>
                    </Pressable>
                  </View>
                </View>

                {watchHistoryError || catalogueError ? (
                  <View style={styles.desktopLibraryError}>
                    <Feather name="alert-circle" size={17} color="#ff9c9c" />
                    <Text style={styles.desktopLibraryErrorText}>
                      {watchHistoryError || catalogueError}
                    </Text>
                  </View>
                ) : null}

                <View style={styles.desktopLibrarySectionHeader}>
                  <View>
                    <Text style={styles.desktopLibrarySectionTitle}>
                      最近观看
                    </Text>
                    <Text style={styles.desktopLibrarySectionSubtitle}>
                      保留 {viewerHistoryRetentionDays} 天 · 第 {historyPage}/
                      {historyTotalPages} 页
                    </Text>
                  </View>
                </View>

                {watchHistoryLoading || loading ? (
                  <Text style={styles.loadingText}>正在加载观看记录...</Text>
                ) : watchHistoryError ||
                  catalogueError ? null : paginatedWatchHistory.length > 0 ? (
                  <>
                    <View style={[styles.grid, styles.desktopLibraryVideoGrid]}>
                      {paginatedWatchHistory.map(({ entry, video }) => (
                        <VideoCard
                          key={`history-${video.id}`}
                          item={video}
                          width={cardWidth}
                          contextLabel={`${formatHistoryTimestamp(
                            entry.lastWatchedAt,
                          )} · ${
                            entry.completed
                              ? "已播完"
                              : `看到 ${formatPlaybackPosition(
                                  entry.resumePositionSeconds,
                                )}`
                          }`}
                          previewActive={hoverPreviewVideoId === video.id}
                          onHoverIn={() => scheduleHoverPreview(video.id)}
                          onHoverOut={() => stopHoverPreview(video.id)}
                          onPress={() => openSelectedVideo(video)}
                        />
                      ))}
                    </View>
                    <View style={styles.desktopLibraryPagination}>
                      <Pressable
                        disabled={historyPage === 1}
                        style={[
                          styles.pageButton,
                          historyPage === 1 && styles.pageButtonDisabled,
                        ]}
                        onPress={() =>
                          setHistoryPage((page) => Math.max(1, page - 1))
                        }
                      >
                        <Text style={styles.pageButtonText}>上一页</Text>
                      </Pressable>
                      <Text style={styles.desktopLibraryPageSummary}>
                        第 {historyPage}/{historyTotalPages} 页
                      </Text>
                      <Pressable
                        disabled={historyPage === historyTotalPages}
                        style={[
                          styles.pageButton,
                          historyPage === historyTotalPages &&
                            styles.pageButtonDisabled,
                        ]}
                        onPress={() =>
                          setHistoryPage((page) =>
                            Math.min(historyTotalPages, page + 1),
                          )
                        }
                      >
                        <Text style={styles.pageButtonText}>下一页</Text>
                      </Pressable>
                    </View>
                  </>
                ) : (
                  <View style={styles.desktopLibraryEmpty}>
                    <Feather name="clock" size={45} color="#756a5c" />
                    <Text style={styles.desktopLibraryEmptyTitle}>
                      还没有观看记录
                    </Text>
                    <Text style={styles.desktopLibraryEmptyText}>
                      播放过的视频会自动出现在这里；再次打开时可选择从上次位置继续播放。
                    </Text>
                    <Pressable
                      style={styles.desktopProfileEmptyButton}
                      onPress={() => setActiveSidebar("首页")}
                    >
                      <Text style={styles.desktopProfileEmptyButtonText}>
                        去首页看看
                      </Text>
                    </Pressable>
                  </View>
                )}
              </ScrollView>
            ) : activeSidebar === "个人中心" ? (
              <ScrollView
                style={styles.desktopProfileScroll}
                contentContainerStyle={styles.desktopProfileContent}
                showsVerticalScrollIndicator
              >
                <View style={styles.desktopProfileHero}>
                  <View style={styles.desktopProfileIdentity}>
                    <View style={styles.desktopProfileAvatar}>
                      <Text style={styles.desktopProfileAvatarText}>
                        {(currentUser?.displayName || currentUser?.email || "D")
                          .slice(0, 1)
                          .toUpperCase()}
                      </Text>
                    </View>
                    <View style={styles.desktopProfileIdentityCopy}>
                      <View style={styles.desktopProfileTitleRow}>
                        <Text style={styles.desktopProfileName}>
                          {currentUser?.displayName || "演示用户"}
                        </Text>
                        <View style={styles.desktopProfileStatusBadge}>
                          <Text style={styles.desktopProfileStatusText}>
                            {currentUser ? "已登录" : "访客模式"}
                          </Text>
                        </View>
                      </View>
                      <Text style={styles.desktopProfileEmail}>
                        {currentUser?.email || "当前浏览器访客空间"}
                      </Text>
                      <Text style={styles.desktopProfileDescription}>
                        收藏、观看和评论偏好会用于改善当前账号的个性化推荐。
                      </Text>
                    </View>
                  </View>
                  <View style={styles.desktopProfileActions}>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="返回电脑版首页"
                      style={styles.desktopProfileSecondaryButton}
                      onPress={() => setActiveSidebar("首页")}
                    >
                      <Feather name="home" size={17} color="#e6c88f" />
                      <Text style={styles.desktopProfileSecondaryButtonText}>
                        返回首页
                      </Text>
                    </Pressable>
                    {currentUser ? (
                      <Pressable
                        accessibilityRole="link"
                        accessibilityLabel="进入媒体管理后台"
                        style={styles.desktopProfileSecondaryButton}
                        onPress={openAdminConsole}
                      >
                        <Feather name="shield" size={17} color="#e6c88f" />
                        <Text style={styles.desktopProfileSecondaryButtonText}>
                          管理后台
                        </Text>
                      </Pressable>
                    ) : null}
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="打开播放缓存设置"
                      style={styles.desktopProfilePrimaryButton}
                      onPress={() => setCacheSettingsOpen(true)}
                    >
                      <Feather name="settings" size={17} color="#17120b" />
                      <Text style={styles.desktopProfilePrimaryButtonText}>
                        缓存设置
                      </Text>
                    </Pressable>
                  </View>
                </View>

                <View style={styles.smartPlaybackSettings}>
                  <View style={styles.smartPlaybackSettingsHeader}>
                    <View style={styles.smartPlaybackSettingsHeading}>
                      <View style={styles.smartPlaybackSettingsIcon}>
                        <Feather name="zap" size={19} color="#04110d" />
                      </View>
                      <View style={styles.smartPlaybackSettingsCopy}>
                        <Text style={styles.smartPlaybackSettingsTitle}>
                          我的智能播放
                        </Text>
                        <Text style={styles.smartPlaybackSettingsSubtitle}>
                          选择快捷策略或微调分值，仅影响当前登录账号
                        </Text>
                      </View>
                    </View>
                    <View
                      style={[
                        styles.smartPlaybackSourceBadge,
                        smartPlaybackPolicySource === "account" &&
                          styles.smartPlaybackSourceBadgeCustom,
                      ]}
                    >
                      <Text style={styles.smartPlaybackSourceBadgeText}>
                        {smartPlaybackPolicySource === "account"
                          ? "个人专属"
                          : "全站默认"}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.smartPlaybackPresetHeader}>
                    <View>
                      <Text style={styles.smartPlaybackSectionTitle}>
                        10 套快捷策略
                      </Text>
                      <Text style={styles.smartPlaybackSectionHint}>
                        当前：
                        {activeSmartPlaybackPreset?.label || "自定义分值"}
                      </Text>
                    </View>
                    {!paidAccount ? (
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel="登录后保存智能播放策略"
                        style={styles.smartPlaybackLoginButton}
                        onPress={() => {
                          setAccountMode("login");
                          setAccountOpen(true);
                        }}
                      >
                        <Text style={styles.smartPlaybackLoginButtonText}>
                          登录后保存
                        </Text>
                      </Pressable>
                    ) : null}
                  </View>

                  <View style={styles.smartPlaybackPresetGrid}>
                    {smartPlaybackPresets.map((preset, index) => {
                      const active =
                        activeSmartPlaybackPreset?.key === preset.key;
                      return (
                        <Pressable
                          key={preset.key}
                          accessibilityRole="button"
                          accessibilityLabel={`快捷策略 ${index + 1}：${
                            preset.label
                          }`}
                          style={[
                            styles.smartPlaybackPresetCard,
                            active && styles.smartPlaybackPresetCardActive,
                          ]}
                          onPress={() => applySmartPlaybackPreset(preset)}
                        >
                          <Text
                            style={[
                              styles.smartPlaybackPresetIndex,
                              active && styles.smartPlaybackPresetTextActive,
                            ]}
                          >
                            {String(index + 1).padStart(2, "0")}
                          </Text>
                          <Text
                            style={[
                              styles.smartPlaybackPresetLabel,
                              active && styles.smartPlaybackPresetTextActive,
                            ]}
                          >
                            {preset.label}
                          </Text>
                          <Text
                            style={[
                              styles.smartPlaybackPresetDescription,
                              active &&
                                styles.smartPlaybackPresetDescriptionActive,
                            ]}
                          >
                            {preset.description}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>

                  <View style={styles.smartPlaybackFieldHeader}>
                    <Text style={styles.smartPlaybackSectionTitle}>
                      分值微调
                    </Text>
                    <Text style={styles.smartPlaybackSectionHint}>
                      分值越高，选下一条视频时影响越强
                    </Text>
                  </View>
                  <View style={styles.smartPlaybackFieldGrid}>
                    {smartPlaybackFields.map((field) => (
                      <View
                        key={field.key}
                        style={styles.smartPlaybackFieldCard}
                      >
                        <View style={styles.smartPlaybackFieldCopy}>
                          <Text style={styles.smartPlaybackFieldLabel}>
                            {field.label}
                          </Text>
                          <Text style={styles.smartPlaybackFieldDescription}>
                            {field.description}
                          </Text>
                        </View>
                        <View style={styles.smartPlaybackStepper}>
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel={`${field.label}减分`}
                            style={styles.smartPlaybackStepButton}
                            onPress={() =>
                              setSmartPlaybackDraftValue(
                                field,
                                smartPlaybackDraft[field.key] - field.step,
                              )
                            }
                          >
                            <Feather name="minus" size={15} color="#d8c59d" />
                          </Pressable>
                          <TextInput
                            accessibilityLabel={`${field.label}分值`}
                            style={styles.smartPlaybackValueInput}
                            keyboardType="numeric"
                            value={String(smartPlaybackDraft[field.key])}
                            selectTextOnFocus
                            onChangeText={(value) =>
                              setSmartPlaybackDraftValue(
                                field,
                                Number(value) || 0,
                              )
                            }
                          />
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel={`${field.label}加分`}
                            style={styles.smartPlaybackStepButton}
                            onPress={() =>
                              setSmartPlaybackDraftValue(
                                field,
                                smartPlaybackDraft[field.key] + field.step,
                              )
                            }
                          >
                            <Feather name="plus" size={15} color="#d8c59d" />
                          </Pressable>
                        </View>
                      </View>
                    ))}
                  </View>

                  <View style={styles.smartPlaybackSettingsFooter}>
                    <Text
                      accessibilityLiveRegion="polite"
                      style={styles.smartPlaybackNotice}
                    >
                      {smartPlaybackNotice ||
                        (sameSmartPlaybackPolicy(
                          smartPlaybackDraft,
                          smartPlaybackPolicy,
                        )
                          ? "当前分值已生效"
                          : "有尚未保存的调整")}
                    </Text>
                    <View style={styles.smartPlaybackSettingsActions}>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel="恢复全站默认智能播放策略"
                        disabled={smartPlaybackSaving}
                        style={styles.smartPlaybackResetButton}
                        onPress={() =>
                          void restorePersonalSmartPlaybackPolicy()
                        }
                      >
                        <Text style={styles.smartPlaybackResetButtonText}>
                          恢复默认
                        </Text>
                      </Pressable>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel="保存我的智能播放策略"
                        disabled={smartPlaybackSaving}
                        style={[
                          styles.smartPlaybackSaveButton,
                          smartPlaybackSaving &&
                            styles.smartPlaybackSaveButtonDisabled,
                        ]}
                        onPress={() => void savePersonalSmartPlaybackPolicy()}
                      >
                        <Feather name="save" size={16} color="#04110d" />
                        <Text style={styles.smartPlaybackSaveButtonText}>
                          {smartPlaybackSaving ? "保存中…" : "保存我的策略"}
                        </Text>
                      </Pressable>
                    </View>
                  </View>
                </View>

                <View style={styles.desktopProfileStatGrid}>
                  {[
                    {
                      key: "watched",
                      icon: "play-circle",
                      label: "看过的视频",
                      value: String(profileHistoryVideos.length),
                      hint: `${profileClickCount} 次打开`,
                    },
                    {
                      key: "watch-time",
                      icon: "clock",
                      label: "累计播放",
                      value: formatProfileWatchTime(profileWatchSeconds),
                      hint: `最近 ${viewerHistoryRetentionDays} 天`,
                    },
                    {
                      key: "favorites",
                      icon: "bookmark",
                      label: "我的收藏",
                      value: String(profileFavoriteVideos.length),
                      hint: "按当前账号或访客识别",
                    },
                    {
                      key: "comments",
                      icon: "message-circle",
                      label: "评论互动",
                      value: String(profileCommentVideos.length),
                      hint: "评论过的视频数量",
                    },
                  ].map((stat) => (
                    <View key={stat.key} style={styles.desktopProfileStatCard}>
                      <View style={styles.desktopProfileStatIcon}>
                        <Feather
                          name={stat.icon as never}
                          size={19}
                          color="#d7a955"
                        />
                      </View>
                      <Text style={styles.desktopProfileStatLabel}>
                        {stat.label}
                      </Text>
                      <Text style={styles.desktopProfileStatValue}>
                        {stat.value}
                      </Text>
                      <Text style={styles.desktopProfileStatHint}>
                        {stat.hint}
                      </Text>
                    </View>
                  ))}
                </View>

                {searchHistory.user.length > 0 && (
                  <View style={styles.desktopProfileKeywords}>
                    <Text style={styles.desktopProfileKeywordsTitle}>
                      我的常用关键词
                    </Text>
                    <View style={styles.desktopProfileKeywordList}>
                      {searchHistory.user.slice(0, 8).map((item) => (
                        <Pressable
                          key={item.keyword}
                          accessibilityRole="button"
                          accessibilityLabel={`搜索关键词 ${item.keyword}`}
                          style={styles.desktopProfileKeyword}
                          onPress={() => {
                            setQuery(item.keyword);
                            setCommittedQuery(item.keyword);
                            setActiveSearchTag("");
                            setActiveSidebar("首页");
                          }}
                        >
                          <Text style={styles.desktopProfileKeywordText}>
                            {item.keyword}
                          </Text>
                          <Text style={styles.desktopProfileKeywordCount}>
                            {item.count}
                          </Text>
                        </Pressable>
                      ))}
                    </View>
                  </View>
                )}

                <View style={styles.desktopProfileLibraryHeader}>
                  <View>
                    <Text style={styles.desktopProfileLibraryTitle}>
                      我的视频内容
                    </Text>
                    <Text style={styles.desktopProfileLibrarySubtitle}>
                      观看记录按最后观看时间排列；收藏与评论状态来自当前用户策略。
                    </Text>
                  </View>
                  <View style={styles.desktopProfileTabs}>
                    {desktopProfileTabs.map((tab) => {
                      const active = profileTab === tab.key;
                      const count =
                        tab.key === "favorites"
                          ? profileFavoriteVideos.length
                          : tab.key === "comments"
                          ? profileCommentVideos.length
                          : profileHistoryVideos.length;
                      return (
                        <Pressable
                          key={tab.key}
                          accessibilityRole="button"
                          accessibilityLabel={`${tab.label}，${count} 个视频`}
                          onPress={() => setProfileTab(tab.key)}
                          style={[
                            styles.desktopProfileTab,
                            active && styles.desktopProfileTabActive,
                          ]}
                        >
                          <Feather
                            name={tab.icon}
                            size={16}
                            color={active ? "#17120b" : "#bca77f"}
                          />
                          <Text
                            style={[
                              styles.desktopProfileTabText,
                              active && styles.desktopProfileTabTextActive,
                            ]}
                          >
                            {tab.label}
                          </Text>
                          <Text
                            style={[
                              styles.desktopProfileTabCount,
                              active && styles.desktopProfileTabCountActive,
                            ]}
                          >
                            {count}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>

                {loading ? (
                  <Text style={styles.loadingText}>正在加载个人内容...</Text>
                ) : profileVideos.length > 0 ? (
                  <View style={[styles.grid, styles.desktopProfileVideoGrid]}>
                    {profileVideos.map((item) => (
                      <VideoCard
                        key={`profile-${profileTab}-${item.id}`}
                        item={item}
                        width={cardWidth}
                        previewActive={hoverPreviewVideoId === item.id}
                        onHoverIn={() => scheduleHoverPreview(item.id)}
                        onHoverOut={() => stopHoverPreview(item.id)}
                        onPress={() => openSelectedVideo(item)}
                      />
                    ))}
                  </View>
                ) : (
                  <View style={styles.desktopProfileEmpty}>
                    <Feather
                      name={
                        profileTab === "favorites"
                          ? "bookmark"
                          : profileTab === "comments"
                          ? "message-circle"
                          : "clock"
                      }
                      size={44}
                      color="#756a5c"
                    />
                    <Text style={styles.desktopProfileEmptyTitle}>
                      {profileTab === "favorites"
                        ? "还没有收藏视频"
                        : profileTab === "comments"
                        ? "还没有评论互动"
                        : "还没有观看记录"}
                    </Text>
                    <Text style={styles.desktopProfileEmptyText}>
                      打开视频播放、收藏或评论后，这里会自动更新。
                    </Text>
                    <Pressable
                      style={styles.desktopProfileEmptyButton}
                      onPress={() => setActiveSidebar("首页")}
                    >
                      <Text style={styles.desktopProfileEmptyButtonText}>
                        去首页看看
                      </Text>
                    </Pressable>
                  </View>
                )}
              </ScrollView>
            ) : (
              <>
                <View style={styles.header}>
                  {compactSidebar && width >= 900 && (
                    <Brand onPress={() => setAndroidDownloadOpen(true)} />
                  )}
                  <View style={styles.searchArea}>
                    <View style={styles.searchShell}>
                      <TextInput
                        value={query}
                        onChangeText={handleSearchInputChange}
                        onFocus={handleSearchFocus}
                        onBlur={handleSearchBlur}
                        onSubmitEditing={submitSearch}
                        placeholder="搜索海量精彩内容"
                        placeholderTextColor="#777"
                        style={styles.searchInput}
                      />
                      {query.length > 0 && (
                        <Pressable
                          accessibilityRole="button"
                          accessibilityLabel="清空搜索"
                          style={styles.clearButton}
                          onPress={clearSearch}
                        >
                          <Feather name="x" size={20} color="#555" />
                        </Pressable>
                      )}
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel="确定搜索"
                        style={styles.searchButton}
                        onPress={submitSearch}
                      >
                        <Feather name="search" size={17} color="#d7a955" />
                        <Text style={styles.searchButtonText}>确定</Text>
                      </Pressable>
                    </View>
                    {suggestionsOpen && inputSuggestions.length > 0 ? (
                      <View style={styles.searchSuggestions}>
                        <View style={styles.searchSuggestionHeader}>
                          <Text style={styles.searchSuggestionHeading}>
                            关键词提示
                          </Text>
                          <Text style={styles.searchSuggestionHint}>
                            选择后仍需点击确定
                          </Text>
                        </View>
                        {inputSuggestions.map((suggestion) => (
                          <Pressable
                            key={suggestion.key}
                            accessibilityRole="button"
                            accessibilityLabel={`关键词提示：${suggestion.label}，${suggestion.source}，选择后仍需确定`}
                            onPress={() =>
                              chooseSearchSuggestion(suggestion.label)
                            }
                            style={({ pressed }) => [
                              styles.searchSuggestionItem,
                              pressed && styles.searchSuggestionItemPressed,
                            ]}
                          >
                            <View style={styles.searchSuggestionIcon}>
                              <Feather
                                name="search"
                                size={15}
                                color="#d7a955"
                              />
                            </View>
                            <View style={styles.searchSuggestionCopy}>
                              <Text
                                numberOfLines={1}
                                style={styles.searchSuggestionText}
                              >
                                {suggestion.label}
                              </Text>
                              <Text style={styles.searchSuggestionSource}>
                                {suggestion.source}
                              </Text>
                            </View>
                            <Feather
                              name="corner-down-left"
                              size={15}
                              color="#756a5c"
                            />
                          </Pressable>
                        ))}
                      </View>
                    ) : null}
                  </View>
                  {width >= 980 && (
                    <Pressable style={styles.roundButton}>
                      <Feather name="mic" size={20} color="#d7a955" />
                    </Pressable>
                  )}
                  {width >= 1080 && <View style={styles.headerSpacer} />}
                  {width >= 1080 && (
                    <Pressable style={styles.createButton}>
                      <Feather name="plus" size={20} color="#17120b" />
                      <Text style={styles.createText}>发布</Text>
                    </Pressable>
                  )}
                  {width >= 1080 && (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="下载安卓客户端"
                      style={styles.androidDownloadButton}
                      onPress={() => setAndroidDownloadOpen(true)}
                    >
                      <Feather name="smartphone" size={18} color="#f5efe5" />
                      <Text style={styles.androidDownloadButtonText}>
                        安卓下载
                      </Text>
                    </Pressable>
                  )}
                  {width >= 980 && (
                    <Pressable style={styles.roundButton}>
                      <Feather name="bell" size={20} color="#d7a955" />
                    </Pressable>
                  )}
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="打开账号"
                    style={styles.userAvatar}
                    onPress={() => setAccountOpen(true)}
                  >
                    <Text style={styles.userAvatarText}>
                      {(currentUser?.displayName || currentUser?.email || "D")
                        .slice(0, 1)
                        .toUpperCase()}
                    </Text>
                  </Pressable>
                </View>

                <ScrollView
                  ref={listScrollRef}
                  style={styles.content}
                  showsVerticalScrollIndicator
                  scrollEventThrottle={100}
                  onContentSizeChange={() => {
                    appendLockedRef.current = false;
                    requestAnimationFrame(finishPageJump);
                  }}
                  onScroll={({ nativeEvent }) => {
                    const { contentOffset, contentSize, layoutMeasurement } =
                      nativeEvent;
                    if (pendingPageRef.current === null) {
                      const offset =
                        contentOffset.y - listGridTopRef.current + 8;
                      const page = Object.entries(pageOffsetsRef.current)
                        .filter(([, y]) => y <= offset)
                        .reduce(
                          (latest, [key]) => Math.max(latest, Number(key)),
                          1,
                        );
                      markPageVisited(page);
                    }
                    if (
                      contentOffset.y + layoutMeasurement.height >=
                      contentSize.height - 160
                    )
                      loadMoreVideos();
                  }}
                >
                  <Text
                    style={[
                      styles.paginationSummary,
                      { paddingHorizontal: 24, paddingVertical: 12 },
                    ]}
                  >
                    共 {catalogueTotal || filteredVideos.length} 个视频 · 已加载{" "}
                    {videos.length} 个 · 已显示 {paginatedVideos.length} 个
                  </Text>

                  <View
                    style={[styles.grid, { gap: listGap }]}
                    onLayout={(event) => {
                      listGridTopRef.current = event.nativeEvent.layout.y;
                    }}
                  >
                    {paginatedVideos.map((item, index) => (
                      <VideoCard
                        onLayout={
                          index % pageSize === 0
                            ? (event) => {
                                pageOffsetsRef.current[
                                  Math.floor(index / pageSize) + 1
                                ] = event.nativeEvent.layout.y;
                                if (pendingPageRef.current !== null)
                                  requestAnimationFrame(finishPageJump);
                              }
                            : undefined
                        }
                        key={item.id}
                        item={item}
                        width={listCardWidth}
                        horizontal={listAppearance.layout === "list"}
                        coverOnly={listAppearance.layout === "compact"}
                        thumbnailWidth={desiredCardWidth}
                        previewActive={hoverPreviewVideoId === item.id}
                        onHoverIn={() => scheduleHoverPreview(item.id)}
                        onHoverOut={() => stopHoverPreview(item.id)}
                        onPress={() => openSelectedVideo(item)}
                      />
                    ))}
                  </View>

                  {!loading && filteredVideos.length > 0 && (
                    <View style={styles.pagination}>
                      {hasMoreVideos ? (
                        <Pressable
                          style={styles.pageButton}
                          onPress={loadMoreVideos}
                        >
                          <Text style={styles.pageButtonText}>
                            {catalogueAppending
                              ? "正在加载下一批…"
                              : "继续下拉加载更多 · 点击也可加载"}
                          </Text>
                        </Pressable>
                      ) : (
                        <Text style={styles.paginationSummary}>
                          已显示全部 {catalogueTotal || filteredVideos.length} 个视频
                        </Text>
                      )}
                    </View>
                  )}

                  {!loading && catalogueError ? (
                    <View style={styles.emptyState}>
                      <Feather name="alert-circle" size={42} color="#ff9c9c" />
                      <Text style={styles.emptyTitle}>视频目录加载失败</Text>
                      <Text style={styles.emptyText}>{catalogueError}</Text>
                    </View>
                  ) : null}
                  {!loading &&
                    !catalogueError &&
                    filteredVideos.length === 0 && (
                      <View style={styles.emptyState}>
                        <Feather name="search" size={42} color="#aaa" />
                        <Text style={styles.emptyTitle}>没有找到相关视频</Text>
                        <Text style={styles.emptyText}>
                          换一个关键词或选择其他分类试试
                        </Text>
                      </View>
                    )}
                  {loading && (
                    <Text style={styles.loadingText}>正在加载视频...</Text>
                  )}
                </ScrollView>
              </>
            )}
          </View>
        </>
      )}
      {standalonePlayer && !selectedVideo && (
        <View
          style={{
            position: "fixed" as never,
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "black",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!loading && (
            <Text style={{ color: "#aaa", padding: 24 }}>
              {catalogueError
                ? "视频加载失败，请刷新重试"
                : "该视频暂时无法播放"}
            </Text>
          )}
        </View>
      )}
      {selectedVideo ? (
        <View
          ref={(node) => {
            floatingPlayerLayerRef.current = node as unknown as HTMLDivElement;
          }}
          pointerEvents="box-none"
          style={[
            styles.floatingPlayerLayer,
            !standalonePlayer && styles.floatingPlayerLayerMaximized,
          ]}
        >
          <View
            style={[
              styles.playerCard,
              {
                left:
                  standalonePlayer || floatingPlayerMaximized
                    ? 0
                    : floatingPlayerLayout.x,
                top:
                  standalonePlayer || floatingPlayerMaximized
                    ? 0
                    : floatingPlayerLayout.y,
                width:
                  standalonePlayer || floatingPlayerMaximized
                    ? "100%"
                    : floatingPlayerLayout.width,
                height: standalonePlayer ? "100%" : "auto",
              },
            ]}
          >
            {createElement(
              "div",
              {
                style: (videoOnlyFullscreen
                  ? styles.playerHeaderHoverZoneHidden
                  : styles.playerHeaderHoverZone) as never,
                onPointerEnter: revealPlayerDragHeader,
                onPointerMove: revealPlayerDragHeader,
                onPointerLeave: hidePlayerDragHeader,
              },
              <View
                style={[
                  styles.playerHeader,
                  playerDragHeaderVisible
                    ? styles.playerHeaderVisible
                    : styles.playerHeaderHidden,
                ]}
              >
              {createElement(
                "div",
                {
                  style: styles.playerHeaderCopy as never,
                  onPointerDown: (event: FloatingPlayerPointerStartEvent) =>
                    beginFloatingPlayerPointerGesture(event, "move"),
                },
                <Text style={styles.playerChannel}>
                  拖动顶部栏移动
                </Text>,
              )}
              {playerWindowActionsHost
                ? createPortal(
                    <View style={styles.playerWindowActions}>
                {!standalonePlayer && (
                  <>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="缩小播放窗口"
                      style={styles.playerWindowButton}
                      onPress={() => scaleFloatingPlayer(0.86)}
                    >
                      <Feather name="minus" size={18} color="white" />
                    </Pressable>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="放大播放窗口"
                      style={styles.playerWindowButton}
                      onPress={() => scaleFloatingPlayer(1.16)}
                    >
                      <Feather name="plus" size={18} color="white" />
                    </Pressable>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={
                        floatingPlayerMaximized
                          ? "还原播放窗口"
                          : "最大化播放窗口"
                      }
                      style={styles.playerWindowButton}
                      onPress={toggleFloatingPlayerMaximized}
                    >
                      <Feather
                        name={
                          floatingPlayerMaximized ? "minimize-2" : "maximize-2"
                        }
                        size={17}
                        color="white"
                      />
                    </Pressable>
                  </>
                )}
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="关闭播放窗口"
                  style={styles.playerClose}
                  onPress={closeSelectedVideo}
                >
                  <Feather name="x" size={22} color="white" />
                </Pressable>
                    </View>,
                    playerWindowActionsHost,
                  )
                : null}
              </View>,
            )}
            <ScrollView
              style={[
                styles.floatingPlayerBody,
                !standalonePlayer &&
                  styles.floatingPlayerBodyMaximized,
              ]}
              contentContainerStyle={styles.floatingPlayerBodyContent}
              showsVerticalScrollIndicator
            >
              {playerWindowNotice ? (
                <Text style={{ color: "#f5c76c", padding: 10 }}>
                  {playerWindowNotice}
                </Text>
              ) : null}
              <DesktopHlsPlayer
                key={`player-${selectedVideo.id}`}
                headerControlsHost={playerHeaderControlsHost}
                actionControlsHost={playerActionControlsHost}
                shareControlsHost={playerShareControlsHost}
                cacheDiagnosticsHost={playerCacheDiagnosticsHost}
                showCacheDiagnostics={paidAccount?.isAdmin === true}
                intelligentCachePlan={intelligentCachePlan}
                windowControlsHost={playerTrackControlsHost}
                playerOpenMode={playerOpenMode}
                onPlayerOpenModeChange={changePlayerOpenMode}
                maximizeVideoArea={!standalonePlayer}
                fitBrowserViewport={floatingPlayerMaximized}
                startVideoFullscreen={standalonePlayer}
                onVideoFullscreenChange={setVideoOnlyFullscreen}
                windowControls={
                  <>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`上一首，当前播放顺序：${desktopLoopModeLabels[loopMode]}`}
                      disabled={sourceVideos.length < 2}
                      style={[
                        styles.playerWindowButton,
                        styles.playerTrackButton,
                        sourceVideos.length < 2 &&
                          styles.playerWindowButtonDisabled,
                      ]}
                      onPress={playPreviousSelectedVideo}
                    >
                      <Feather name="skip-back" size={17} color="#bff7e6" />
                    </Pressable>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`切换下一首，按${desktopLoopModeLabels[loopMode]}选择`}
                      disabled={sourceVideos.length < 2}
                      style={[
                        styles.playerWindowButton,
                        styles.playerTrackButton,
                        {
                          width: "auto",
                          paddingHorizontal: 12,
                          flexDirection: "row",
                          gap: 6,
                        },
                        sourceVideos.length < 2 &&
                          styles.playerWindowButtonDisabled,
                      ]}
                      onPress={() =>
                        advanceSelectedVideo(
                          loopMode === "single" ? "sequence" : loopMode,
                        )
                      }
                    >
                      <Feather name="skip-forward" size={17} color="#bff7e6" />
                      <Text style={{ color: "#bff7e6", fontWeight: "600" }}>
                        切换
                      </Text>
                    </Pressable>
                  </>
                }
                video={selectedVideo}
                identity={currentUser}
                initialResumePosition={
                  viewerWatchHistory.find(
                    (entry) => entry.videoId === selectedVideo.id,
                  )?.resumePositionSeconds || 0
                }
                onHistoryCommitted={() => {
                  setHistoryPage(1);
                  setViewerLibraryRefresh((value) => value + 1);
                }}
                onPlaybackFinish={advanceSelectedVideo}
                onRequirePaidLogin={() => setAccountOpen(true)}
              />
              {!videoOnlyFullscreen && (
                <DesktopVideoActions
                  key={`actions-${selectedVideo.id}`}
                  video={selectedVideo}
                  identity={currentUser}
                  isAdmin={paidAccount?.isAdmin === true}
                  onVideoChanged={(updated, created) => {
                    if (updated && !created) {
                      setSelectedVideo((current) => current && current.id === updated.id
                        ? {
                            ...current,
                            title: updated.title,
                            channel: updated.channel,
                            mediaVariants: updated.mediaVariants.flatMap((variant) => {
                              const url = variant.resolvedUrl || variant.source;
                              if (!url || !["mobile", "desktop", "audio"].includes(variant.kind)) return [];
                              return [{
                                id: variant.id,
                                kind: variant.kind as "mobile" | "desktop" | "audio",
                                label: variant.label,
                                format: variant.format || "",
                                quality: variant.quality || "",
                                resolution: variant.resolution || "",
                                bitrate: variant.bitrate || "",
                                codec: variant.codec || "",
                                isDefault: variant.isDefault === true,
                                url,
                              }];
                            }),
                          }
                        : current,
                      );
                    }
                    setRankingRefresh((value) => value + 1);
                  }}
                  onVideoDeleted={() => {
                    setSelectedVideo(null);
                    setRankingRefresh((value) => value + 1);
                  }}
                  subscribed={Boolean(
                    selectedVideo.channelId &&
                      subscribedChannelIds.has(selectedVideo.channelId),
                  )}
                  subscriptionSaving={Boolean(subscriptionSavingId)}
                  onToggleSubscription={() => {
                    if (selectedVideo.channelId) {
                      void toggleChannelSubscription(
                        selectedVideo.channelId,
                        selectedVideo.channel,
                      );
                    }
                  }}
                  mediaControlsHostRef={setPlayerHeaderControlsHost}
                  playerControlsHostRef={setPlayerActionControlsHost}
                  windowActionsHostRef={setPlayerWindowActionsHost}
                  trackControlsHostRef={setPlayerTrackControlsHost}
                  shareControlsHostRef={setPlayerShareControlsHost}
                  cacheDiagnosticsHostRef={setPlayerCacheDiagnosticsHost}
                />
              )}
            </ScrollView>
            {!standalonePlayer &&
              createElement(
                "div",
                {
                  role: "slider",
                  "aria-label": "拖动调整播放窗口大小",
                  style: styles.floatingPlayerResizeHandle as never,
                  onPointerDown: (event: FloatingPlayerPointerStartEvent) =>
                    beginFloatingPlayerPointerGesture(event, "resize"),
                },
                <Feather name="move" size={14} color="#28c99a" />,
              )}
          </View>
        </View>
      ) : null}

      <Modal
        transparent
        visible={cacheSettingsOpen}
        animationType="fade"
        onRequestClose={() => setCacheSettingsOpen(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable
            style={styles.modalCloseArea}
            onPress={() => setCacheSettingsOpen(false)}
          />
          <View style={styles.cacheSettingsCard}>
            <View style={styles.cacheSettingsHeader}>
              <View>
                <Text style={styles.cacheSettingsTitle}>
                  个人中心 · 缓存设置
                </Text>
                <Text style={styles.cacheSettingsSubtitle}>
                  配置随账号保存，视频缓存在当前浏览器设备
                </Text>
              </View>
              <Pressable
                style={styles.cacheSettingsClose}
                onPress={() => setCacheSettingsOpen(false)}
              >
                <Feather name="x" size={23} color="#222" />
              </Pressable>
            </View>
            <ScrollView>
              <HlsCacheSettings />
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Modal
        transparent
        visible={accountOpen}
        animationType="fade"
        onRequestClose={() => setAccountOpen(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable
            style={styles.modalCloseArea}
            onPress={() => setAccountOpen(false)}
          />
          <View style={styles.accountCard}>
            <View style={styles.accountAvatar}>
              <Text style={styles.accountAvatarText}>
                {(currentUser?.displayName || currentUser?.email || "D")
                  .slice(0, 1)
                  .toUpperCase()}
              </Text>
            </View>
            <Text style={styles.accountName}>
              {currentUser?.displayName || "演示用户"}
            </Text>
            <Text style={styles.accountEmail}>
              {currentUser?.email || "当前未登录"}
            </Text>
            <Text style={styles.accountStatus}>
              {paidAccount?.isAdmin
                ? "ADMIN 管理员 · 已启用后台权限"
                : paidAccount?.isVip
                ? "VIP 会员 · 已解锁专属视频"
                : currentUser
                ? "注册会员 · 尚未升级 VIP"
                : "请登录或注册会员账号"}
            </Text>
            {accountNotice ? (
              <Text style={styles.accountNotice}>{accountNotice}</Text>
            ) : null}
            {currentUser ? (
              <>
                <Pressable
                  style={styles.accountVipRefreshAction}
                  onPress={() => void refreshPaidMembership()}
                >
                  <Feather name="refresh-cw" size={15} color="#bdeee0" />
                  <Text style={styles.accountVipRefreshText}>
                    刷新会员 / VIP 状态
                  </Text>
                </Pressable>
                <Pressable
                  style={styles.accountAction}
                  onPress={() => {
                    setAccountOpen(false);
                    setActiveSidebar("个人中心");
                  }}
                >
                  <Text style={styles.accountActionText}>进入个人中心</Text>
                </Pressable>
                {paidAccount?.isAdmin ? (
                  <>
                    <Pressable
                      style={styles.accountAdminAction}
                      onPress={openAdminConsole}
                    >
                      <Feather name="shield" size={16} color="#e6c88f" />
                      <Text style={styles.accountAdminActionText}>
                        进入管理后台
                      </Text>
                    </Pressable>
                    <Text style={styles.accountAdminHint}>
                      管理员身份已验证，无需输入 Token
                    </Text>
                  </>
                ) : null}
                <Pressable
                  style={styles.accountLogoutAction}
                  onPress={() => void logoutPaidAccount()}
                >
                  <Text style={styles.accountLogoutText}>退出当前账号</Text>
                </Pressable>
              </>
            ) : (
              <>
                <View style={styles.accountModeTabs}>
                  <Pressable
                    style={[
                      styles.accountModeTab,
                      accountMode === "login" && styles.accountModeTabActive,
                    ]}
                    onPress={() => switchAccountMode("login")}
                  >
                    <Text
                      style={[
                        styles.accountModeTabText,
                        accountMode === "login" &&
                          styles.accountModeTabTextActive,
                      ]}
                    >
                      登录
                    </Text>
                  </Pressable>
                  <Pressable
                    style={[
                      styles.accountModeTab,
                      accountMode === "register" && styles.accountModeTabActive,
                    ]}
                    onPress={() => switchAccountMode("register")}
                  >
                    <Text
                      style={[
                        styles.accountModeTabText,
                        accountMode === "register" &&
                          styles.accountModeTabTextActive,
                      ]}
                    >
                      注册会员
                    </Text>
                  </Pressable>
                </View>
                {accountMode === "register" ? (
                  <TextInput
                    value={registerName}
                    onChangeText={setRegisterName}
                    placeholder="会员名称（可选）"
                    placeholderTextColor="#746d63"
                    style={styles.accountInput}
                  />
                ) : null}
                <TextInput
                  value={loginEmail}
                  onChangeText={setLoginEmail}
                  autoCapitalize="none"
                  keyboardType="email-address"
                  placeholder={
                    accountMode === "register" ? "注册邮箱" : "登录账号"
                  }
                  placeholderTextColor="#746d63"
                  style={styles.accountInput}
                />
                <TextInput
                  value={loginPassword}
                  onChangeText={setLoginPassword}
                  onSubmitEditing={() =>
                    void (accountMode === "register"
                      ? submitDesktopRegistration()
                      : submitDesktopLogin())
                  }
                  secureTextEntry
                  placeholder="密码"
                  placeholderTextColor="#746d63"
                  style={styles.accountInput}
                />
                {loginError ? (
                  <Text style={styles.accountError}>{loginError}</Text>
                ) : null}
                <Pressable
                  disabled={loginSubmitting}
                  style={[
                    styles.accountAction,
                    loginSubmitting && styles.accountActionDisabled,
                  ]}
                  onPress={() =>
                    void (accountMode === "register"
                      ? submitDesktopRegistration()
                      : submitDesktopLogin())
                  }
                >
                  <Text style={styles.accountActionText}>
                    {loginSubmitting
                      ? accountMode === "register"
                        ? "正在注册…"
                        : "正在登录…"
                      : accountMode === "register"
                      ? "注册会员"
                      : "登录账号"}
                  </Text>
                </Pressable>
              </>
            )}
            <Pressable
              style={styles.accountClose}
              onPress={() => setAccountOpen(false)}
            >
              <Text style={styles.accountCloseText}>关闭</Text>
            </Pressable>
          </View>
        </View>
      </Modal>

      <Modal
        transparent
        visible={androidDownloadOpen}
        animationType="fade"
        onRequestClose={() => setAndroidDownloadOpen(false)}
      >
        <View style={styles.modalBackdrop}>
          <Pressable
            style={styles.modalCloseArea}
            onPress={() => setAndroidDownloadOpen(false)}
          />
          <View style={styles.androidDownloadCard}>
            <View style={styles.androidDownloadIcon}>
              <Feather name="smartphone" size={27} color="#17120b" />
            </View>
            <Text style={styles.androidDownloadTitle}>海皇娱乐客户端下载</Text>
            <Text style={styles.androidDownloadSubtitle}>
              手机扫码，或选择对应的安装包
            </Text>
            <Image
              source={require("../../../assets/download-center-qr.png")}
              style={styles.androidDownloadQr}
              resizeMode="contain"
            />
            <Text style={styles.androidDownloadVersion}>
              v1.2.0 · Android APK / iPhone IPA
            </Text>
            <View style={{ width: "100%", flexDirection: "row", gap: 10 }}>
              {[
                {
                  label: "下载 APK",
                  href: "/tiktok/downloads/haihuang-entertainment-android-v1.2.0.apk",
                  file: "haihuang-entertainment-android-v1.2.0.apk",
                  primary: true,
                },
                {
                  label: "下载 IPA",
                  href: "/tiktok/downloads/?platform=ios",
                  file: "ios-download",
                  primary: false,
                },
              ].map((download) =>
                createElement(
                  "a",
                  {
                    key: download.file,
                    href: download.href,
                    download: download.file,
                    style: {
                      flex: 1,
                      height: 44,
                      borderRadius: 22,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: download.primary ? "none" : "1px solid #80643b",
                      backgroundColor: download.primary ? "#d7a955" : "#27231e",
                      color: download.primary ? "#17120b" : "#f5efe5",
                      fontSize: 15,
                      fontWeight: 800,
                      textDecoration: "none",
                    },
                  },
                  download.label,
                ),
              )}
            </View>
            <Pressable
              style={styles.androidDownloadClose}
              onPress={() => setAndroidDownloadOpen(false)}
            >
              <Text style={styles.androidDownloadCloseText}>关闭</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </View>
  );
}
